/**
 * JSON-API клиент SPA ↔ Go-бэкенд (см. backend-foundation/api-contract.md).
 *
 * Конвенции контракта:
 *  - cookie-сессия: все запросы с `credentials:'include'`;
 *  - CSRF double-submit: мутации (POST/PUT/PATCH/DELETE) несут заголовок `X-CSRF-Token`,
 *    значение которого равно cookie `csrf` (её ставит `GET /api/auth/csrf` и вход);
 *  - единый конверт ошибок `{error:{code,message,details}}` → `ApiError`;
 *  - списки — `{items,total,limit,offset}`.
 */

/**
 * Базовый URL JSON-API из переменной окружения (раздельно dev/prod).
 * По умолчанию same-origin '/api' — один и тот же код SPA работает и в dev (через dev-proxy Vite),
 * и в prod (бэкенд раздаёт статику и отвечает на /api). Серверный рендеринг не используется.
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

/** Сборка абсолютного пути к ресурсу API. */
export function apiUrl(path: string): string {
  const base = API_BASE_URL.replace(/\/$/, '')
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${base}${suffix}`
}

// --- Типы домена (форма ответов бэкенда Блока 1) ---

/**
 * Роль учётной записи. Командные (team-access): `admin` — администратор (суперадминистратор),
 * `head` — руководитель, `employee` — сотрудник; клиентские — `company`, `individual`.
 */
export type Role = 'admin' | 'head' | 'employee' | 'company' | 'individual'

/** Командная роль — её выбирают при создании сотрудника и меняют в «Аккаунтах». */
export type TeamRole = 'admin' | 'head' | 'employee'

export interface Org {
  id: string
  name: string
}

/** Объект пользователя (api-contract §1). У company есть org; у individual/команды — нет. */
export interface User {
  id: string
  role: Role
  fullName: string
  email: string
  position: string
  /** Телефон сотрудника Центрики (team-access); у клиентских записей пуст. */
  phone?: string
  org?: Org | null
  /** Признак отключённого аккаунта (Блок 2: userDTO дополнен полем `disabled`). */
  disabled?: boolean
  /**
   * Сколько организаций закреплено за входом клиента-компании (multi-organization-login); у
   * физлица и команды поля нет. `org` при этом — активная организация, при невыбранной пуст.
   */
  organizationsCount?: number
  /**
   * Представитель организации у входа компании в активной организации (login-representative;
   * ТЗ п.9.4): ФИО и должность — справа вверху шапки. Нет — название организации и «Представитель
   * организации».
   */
  representative?: { fullName: string; position: string }
}

/** Организация входа в окне выбора и переключателе (GET /api/auth/organizations). */
export interface Organization {
  id: string
  name: string
  nameShort: string
  inn: string
  /** Непрочитанные записи «колокольчика» входа по этой организации. */
  unread: number
  /** Активная организация сессии. */
  active: boolean
  lastSelectedAt?: string
}

/** Вход с доступом к организации (карточка клиента у команды, multi-organization-login). */
export interface ClientLogin {
  id: string
  email: string
  fullName: string
  disabled: boolean
  /** Основной вход организации — тот, что правит «Учётные данные». */
  primary: boolean
  linkedAt: string
  /** К скольким ещё организациям у входа есть доступ: смена его почты/пароля коснётся и их. */
  otherOrganizations: number
  /** Представитель организации у входа (login-representative): контактное лицо; нет — не указан. */
  representative?: { contactId: string; fullName: string; position: string }
}

export interface NavConfig {
  /** Идентификаторы разделов навигации, рассчитанные сервером по роли. */
  sections: string[]
  /**
   * Только у клиента (centrika-docs!98, training-order-filling): сколько заказов ждут действий
   * клиента — «На заполнении» и «Требуются правки», по ВСЕМ его заказам, не по странице списка.
   * Число показывается у пункта «Мои заказы». У команды поля нет.
   */
  ordersAwaitingClient?: number
  /**
   * Только у клиента (client-questions; ТЗ п.9.7): вопросы организации с непрочитанным ответом
   * Центрики — точка на конвертике в шапке.
   */
  questionsUnread?: number
  /** Только у команды: вопросы клиентов, ждущие ответа, видимые вошедшему (у «Вопросов клиентов»). */
  questionsAwaitingTeam?: number
}

export interface BellConfig {
  unread: number
}

/**
 * Онбординг профиля компании (§3.1, D5, p2-company-profile): решение о показе модалки
 * заполнения профиля при первом входе. Присутствует только у клиента-компании.
 */
export interface OnboardingStatus {
  /** Вычисляемое свойство — набор обязательных реквизитов заполнен (см. BE isCompanyProfileComplete). */
  profileComplete: boolean
  /** Признак «пропустить» — модалка не показывается навязчиво повторно. */
  skipped: boolean
}

/** Контекст шапки и навигации (GET /api/me/context). */
export interface MeContext {
  user: User
  org?: Org | null
  nav: NavConfig
  bell: BellConfig
  onboarding?: OnboardingStatus
}

/**
 * Менеджер клиента в карточке клиента у команды (team-access). Клиенту менеджер не показывается.
 * `disabled` — его учётная запись отключена.
 */
export interface ClientManager {
  id: string
  fullName: string
  phone: string
  email: string
  disabled: boolean
}

/**
 * Ответственный по абонентскому обслуживанию по обучению (training-subscription-responsible):
 * учётная запись, которая становится ответственным в заказах обучения клиентов на обслуживании.
 * `responsible` — null, если не задана; `assignedOrders` — только в ответе на сохранение: сколько
 * уже отправленных заказов получили ответственного.
 */
export interface TrainingSubscriptionResponsible {
  responsible: ClientManager | null
  assignedOrders?: number
}

/** Строка справочника сотрудников Центрики (GET /api/team/staff) — действующие записи команды. */
export interface StaffMember {
  id: string
  fullName: string
  role: TeamRole
  phone: string
  email: string
}

/** Событие ленты «колокольчика» (GET /api/notifications). */
export interface Notification {
  id: string
  kind: string
  title: string
  body: string
  read: boolean
  createdAt: string
  relatedOrderId?: string
  /** Клиент уведомления: команда открывает карточку клиента (опрос по экологии). */
  relatedClientId?: string
  /** Вопрос клиента (client-questions): запись открывает вопрос. */
  relatedQuestionId?: string
}

// --- Блок 3: заказы / документы / журнал (api-contract §3c) ---

/** Услуга заказа (CHECK БД; профриск в ед.ч. — `profrisk`). Ярлыки — см. lib/orders. */
export type Service = 'sout' | 'training' | 'profrisk' | 'ecology'

/**
 * Статус данных заказа (ось 1; CHECK БД). Канон — `changes_requested`
 * (фронт-заглушка Блока 1 ошибочно писала `rework` — сведено к `changes_requested`).
 */
export type OrderDataStatus =
  | 'draft'
  /** Только обучение: заявка отправлена менеджеру, клиент довносит данные (training-order-filling). */
  | 'filling'
  | 'review'
  | 'changes_requested'
  | 'in_progress'
  | 'done'
  /** Отменён клиентом или командой (order-cancel-restore); «Восстановить» вернёт статус до отмены. */
  | 'cancelled'

/**
 * Сведения об отмене заказа (order-cancel-restore; ТЗ п.1.5, п.9.3, В21) — есть только у отменённого.
 * `byName` — кто отменил, только у команды (клиенту имена сотрудников Центрики не отдаются).
 */
export interface OrderCancellation {
  /** Когда отменён (ISO). */
  at: string
  /** Отменил клиент (иначе — Центрика): клиент восстанавливает только свою отмену. */
  byClient: boolean
  /** Причина; пустая строка — не указана. */
  reason: string
  /** Статус до отмены — в него вернёт «Восстановить» (нет у карточки списка). */
  fromStatus?: OrderDataStatus
  byName?: string
}

/** Краткая ссылка на текущий этап «Ход работ» (в карточке/строке очереди). */
export interface StageRef {
  key: string
  label: string
  position: number
}

/**
 * Документ реестра (§3c). `count` — для будущей группировки обучения (в Блоке 3 всегда 1);
 * `typeName` — каноническая форма из `document_types.name`. `downloadUrl` = `/api/files/{id}/download`.
 */
export interface DocumentDTO {
  id: string
  title: string
  typeCode: string
  typeName: string
  date: string
  orderId?: string
  ownerName?: string
  ownerKind?: 'company' | 'individual'
  downloadUrl: string
  count: number
  /** Привязка к строке рабочего места СОУТ (§7.5), если документ загружен для неё. */
  soutLineId?: string
}

/** Ответ загрузки файла (POST /api/team/files). */
export interface FileDTO {
  id: string
  originalName: string
  mimeType: string
  sizeBytes: number
  createdAt: string
}

/** Этап «Ход работ» (§3c). `isDone` = position < позиции текущего. */
export interface StageDTO {
  key: string
  label: string
  position: number
  isCurrent: boolean
  isDone: boolean
  targetDate?: string
  /**
   * Момент завершения этапа — только у `isDone`: ставится при уходе с этапа, снимается откатом.
   * У этапов, завершённых до появления поля, отсутствует.
   */
  completedAt?: string
  documents: DocumentDTO[]
}

/** Единица размера заказа (D10): рабочие места / слушатели / должности. */
export type CompositionUnit = 'workplaces' | 'trainees' | 'positions' | ''

/** Вычисляемая сводка «Состав заказа» (D10): счётчик размера + подразделения. */
export interface OrderComposition {
  unit: CompositionUnit
  count: number
  departments: string[]
}

/** Карточка заказа в списке «Мои заказы» (§3c). */
export interface OrderCard {
  id: string
  service: Service
  /**
   * Номер заказа «индекс услуги-порядковый номер» («СОУТ-12») — тот же, что в `OrderDetail`
   * (centrika-docs!47). Карточка списка показывает его над наименованием услуги
   * (centrika-docs!50); собирает сервер.
   */
  number: string
  /** @deprecated D10: клиент не отображает; остаётся в DTO для командной стороны. Размер — `itemCount`. */
  volumeSummary: string
  dataStatus: OrderDataStatus
  currentStage: StageRef
  createdAt: string
  /**
   * Дата ПОСЛЕДНЕЙ отправки заказа клиентом на проверку — вторая часть строки дат карточки
   * «Мои заказы» («Создан … · Отправлен …», centrika-docs!95). Отсутствует, если заказ ни разу
   * не отправляли: строка содержит только дату создания.
   */
  submittedAt?: string
  /** Вычисляемый размер заказа (D10): рабочих мест/слушателей/должностей. */
  itemCount: number
  /**
   * Согласия ПД заказа ОБУЧЕНИЯ (centrika-docs, MR !36): сколько уникальных сотрудников
   * состава уже с загруженным согласием и сколько их всего. У прочих услуг и у заказа без
   * состава — `0/0`.
   */
  consentsUploaded: number
  consentsTotal: number
  /**
   * «Заполнено N из M» заказа обучения (training-listener-completeness): слушатели со всеми данными
   * и все слушатели и места без названного слушателя.
   */
  listenersComplete?: number
  listenersTotal?: number
  /**
   * Заказ по абонентскому обслуживанию по обучению (training-subscription) — отметка для команды:
   * приходит только в «Истории заказов» карточки клиента, в «Моих заказах» клиента поля нет.
   */
  trainingSubscription?: boolean
  /** Отмена и восстановление (order-cancel-restore): сведения, можно ли отменить и восстановить. */
  cancellation?: OrderCancellation
  /** Нет поля — действие недоступно. */
  canCancel?: boolean
  canRestore?: boolean
  /** Изменения состава заявки на согласовании (training-change-approval); нет поля — их нет. */
  pendingChange?: TrainingPendingChange
}

/**
 * Изменения состава заявки на согласовании в списках (training-change-approval): `awaiting_client`
 * — предложила Центрика, ждут клиента; `awaiting_team` — предложил клиент, ждут команду.
 */
export type TrainingPendingChange = 'awaiting_client' | 'awaiting_team'

/**
 * Предзаполнение заявки на обучение по срокам (training-order-entry-points): сколько сотрудников,
 * программ и пар подставится (сводка) или подставлено (`wizard` — мастер после заполнения);
 * `unavailable` — программы не из каталога без замены, `alreadyOrdered` — пары из поданных заказов.
 */
export interface TrainingPrefill {
  persons: number
  programs: number
  pairs: number
  unavailable: string[]
  alreadyOrdered: number
  wizard?: TrainingWizard
}

/** Строка протокола изменений состава заявки — текст на момент предложения. */
export interface TrainingChangeEntry {
  kind:
    | 'item_added'
    | 'item_removed'
    | 'places_changed'
    | 'listener_added'
    | 'listener_removed'
    | 'listener_replaced'
  text: string
}

/**
 * Изменения состава заявки на согласовании (training-change-approval; ТЗ п.1.4, В20). `canDecide` —
 * «Согласовать» и «Отклонить» (предложила другая сторона), `canWithdraw` — «Отозвать» (своё).
 * `byName` — кто предложил из команды, только команде.
 */
export interface TrainingChange {
  byClient: boolean
  byName?: string
  at: string
  protocol: TrainingChangeEntry[]
  canDecide: boolean
  canWithdraw: boolean
}

/** Исход предложения изменений состава. */
export type TrainingChangeOutcome = 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'dropped'

/**
 * Строка истории изменений состава (ответ заказчицы 04.11 к п.1.4: «Где протокол изменений?»):
 * кто и когда предложил, протокол, решение и причина отказа. Имена — только команде.
 */
export interface TrainingChangeLogEntry {
  proposedAt: string
  byClient: boolean
  proposedBy?: string
  protocol: TrainingChangeEntry[]
  outcome: TrainingChangeOutcome
  decidedAt?: string
  decidedBy?: string
  reason?: string
}

/**
 * Позиция желаемого состава в предложении изменений: ровно одно из `programId` и `packageId`;
 * `listenersCount` не меньше числа `personIds`. Позиции, которой нет в списке, — убрать.
 */
export type TrainingChangeItemInput =
  | { programId: string; personIds: string[]; listenersCount: number }
  | { packageId: string; personIds: string[]; listenersCount: number }

/** Полная карточка заказа (клиентский скоуп). Права (`can*`) вычисляет бэкенд. */
export interface OrderDetail {
  id: string
  service: Service
  /** @deprecated D10: клиент не отображает; остаётся в DTO для командной стороны. Размер — `composition`. */
  volumeSummary: string
  /**
   * Номер заказа «индекс услуги-порядковый номер» («СОУТ-12», centrika-docs!47): назначается
   * при создании, не меняется и не переиспользуется. Собирает сервер — порядковая часть
   * сквозная по всем клиентам и услугам и из полей DTO не выводится.
   */
  number: string
  dataStatus: OrderDataStatus
  returnComment: string
  createdAt: string
  submittedAt?: string
  completedAt?: string
  stages: StageDTO[]
  documents: DocumentDTO[]
  snapshotCommon: Record<string, unknown>
  composition: OrderComposition
  /**
   * «Реквизиты на дату создания заказа» (F2-07, замечание менеджера): read-only снимок
   * реквизитов организации, зафиксированный НА МОМЕНТ создания заказа
   * (`orders.snapshot_requisites`) — правки профиля компании ПОСЛЕ создания заказа его
   * не меняют. `undefined` — у клиента-физлица (реквизитов организации нет вовсе);
   * когда объект присутствует — показывать ВЕСЬ стабильный набор полей (`CompanyProfileView`),
   * пустые значения — как «—», не скрывать.
   */
  snapshotRequisites?: CompanyProfile
  canEdit: boolean
  canSubmit: boolean
  canDelete: boolean
  /** Отмена и восстановление (order-cancel-restore): см. `OrderCard`. */
  cancellation?: OrderCancellation
  /** Нет поля — действие недоступно. */
  canCancel?: boolean
  canRestore?: boolean
  /** Изменения состава заказа обучения на согласовании (training-change-approval). */
  trainingChange?: TrainingChange
  /** Можно предложить изменения состава: заказ обучения «На заполнении», «На проверке», «В работе» без ждущих изменений. */
  canProposeChange?: boolean
  /** История изменений состава заказа обучения, новые сверху (ответ заказчицы 04.11 к п.1.4). */
  trainingChangeHistory?: TrainingChangeLogEntry[]
}

/** Строка очереди заказов команды (GET /api/team/orders, §3c). */
export interface TeamOrderRow {
  id: string
  service: Service
  /** Номер заказа для колонки «№ заказа» очереди (centrika-docs!50). */
  number: string
  volumeSummary: string
  /**
   * Вычисляемый размер заказа (D10): рабочих мест/слушателей/должностей. Колонка «Объём» очереди
   * для заказа СОУТ показывает число рабочих мест числительным (centrika-docs!63); у остальных
   * услуг — текстовый `volumeSummary`.
   */
  itemCount: number
  dataStatus: OrderDataStatus
  ownerClientId: string
  ownerName: string
  /**
   * ИНН организации-владельца — вторая строка колонки «Клиент» очереди (centrika-docs!51).
   * Отсутствует у клиента-физлица и у компании без заполненного ИНН.
   */
  ownerInn?: string
  ownerKind: 'company' | 'individual'
  responsibleName?: string
  currentStage: StageRef
  createdAt: string
  /**
   * Дата ПОСЛЕДНЕЙ отправки заказа клиентом на проверку — колонка «Дата отправки на
   * проверку» очереди (centrika-docs!77). Отсутствует, если заказ ни разу не отправляли:
   * в таблице показывается прочерк «—».
   */
  submittedAt?: string
  /**
   * «Мяч» на стороне команды: «На проверке», заявка на обучение без договора и счёта, изменения
   * клиента на согласовании.
   */
  awaitingTeam: boolean
  /** Изменения состава заявки на согласовании (training-change-approval). */
  pendingChange?: TrainingPendingChange
  /** «Заполнено N из M» заказа обучения (training-listener-completeness). */
  listenersComplete?: number
  listenersTotal?: number
  /**
   * Заказ по абонентскому обслуживанию по обучению (training-subscription): клиент был на
   * обслуживании в день отправки заказа на проверку. Бывает только у заказа обучения организации.
   */
  trainingSubscription: boolean
}

/** Карточка заказа со стороны команды: поля OrderDetail + владелец и командные права. */
export interface TeamOrderDetail extends Omit<OrderDetail, 'canEdit' | 'canSubmit' | 'canDelete'> {
  /** ФИО ответственного от Центрики. Только у команды: клиенту сервер его не отдаёт. */
  responsibleName?: string
  /**
   * Вычисляемый размер заказа (D10). Для заказа СОУТ карточка «Состав заказа» и read-only поле
   * «Объём заказа» правки показывают число рабочих мест числительным (centrika-docs!63); у
   * обучения/профрисков объём — текстовый `volumeSummary`.
   */
  itemCount: number
  /**
   * Регистрационный номер заказа СОУТ (centrika-docs!73) и профрисков
   * (profrisk-order-registration-number): присваивает лаборатория, вводит/правит команда на
   * карточке заказа. Пустая строка — рег.номера нет. У обучения сервер возвращает пустую строку
   * (карточка не рисуется, `lib/orders` `hasRegistrationNumber`). Необязательно в типе — старые
   * фикстуры/ответы без поля трактуются как пустой рег.номер.
   */
  registrationNumber?: string
  ownerClientId: string
  ownerName: string
  ownerKind: 'company' | 'individual'
  responsibleAccountId?: string
  /** Контакты ответственного из справочника сотрудников (team-access); пусто — не заданы. */
  responsiblePhone?: string
  responsibleEmail?: string
  canReturn: boolean
  canAccept: boolean
  canComplete: boolean
  /**
   * Отмена и восстановление (order-cancel-restore): команда отменяет поданный и не завершённый
   * заказ и восстанавливает любой отменённый; `cancellation.byName` — кто отменил.
   */
  cancellation?: OrderCancellation
  /** Нет поля — действие недоступно. */
  canCancel?: boolean
  canRestore?: boolean
  /**
   * Результаты услуги заполнены настолько, что заказ можно завершить (centrika-docs!41):
   * у обучения — дата окончания обучения и итоговый документ у каждой программы со
   * слушателями; у прочих услуг всегда `true`. Кнопка «Завершить» рисуется по
   * `canComplete` (статус), а недоступной её делает этот признак.
   */
  resultsReady: boolean
  /** Заказ по абонентскому обслуживанию по обучению — см. `TeamOrderRow.trainingSubscription`. */
  trainingSubscription: boolean
}

/**
 * Структурированные детали записи журнала (jsonb `NOT NULL DEFAULT '{}'` на бэкенде — приходит
 * как объект, часто пустой `{}`). Форма зависит от `action` (см. `auditDetailsText` в lib/orders);
 * это НЕ строка — рендерить только через форматтер, никогда через `${details}`/`String(details)`.
 */
export type AuditDetails = Record<string, unknown>

/** Запись журнала действий (§3c). Русские ярлыки `action` — на фронте (см. lib/orders). */
export interface AuditDTO {
  id: string
  action: string
  actorName: string
  objectType: string
  objectId?: string
  ownerName?: string
  createdAt: string
  details?: AuditDetails
}

/** Краткая карточка клиента (ответ POST /api/team/clients и строка GET /api/team/clients). */
export interface ClientSummary {
  id: string
  name: string
  /** ИНН — приходит в листинге Блока 2 (необязателен для обратной совместимости). */
  inn?: string
  /** ФИО менеджера клиента (team-access); пусто или отсутствует — менеджер не назначен. */
  managerName?: string
  /** ФИО менеджера по сопровождению (client-support-manager); пусто — не назначен. */
  supportManagerName?: string
}

/**
 * Карточка клиента (ответ GET /api/team/clients/{cid}, контракт §3b):
 * `{ id, kind, name, createdAt, profile? }`. Это НЕ ClientSummary — несёт тип клиента,
 * наименование (заголовок карточки) и реквизиты (`profile` — только у компании).
 */
export interface ClientCard {
  id: string
  kind: 'company' | 'individual'
  name: string
  createdAt: string
  profile?: CompanyProfile
  /** Текущий email общего входа компании (есть только у company). */
  loginEmail?: string
  /** Абонентское обслуживание по обучению (есть только у company). */
  trainingSubscription?: TrainingSubscription
  /** Менеджер клиента (team-access); отсутствует — не назначен. */
  manager?: ClientManager
  /** Менеджер по сопровождению (client-support-manager; ТЗ п.9.1); отсутствует — не назначен. */
  supportManager?: ClientManager
}

/**
 * Срок договора абонентского обслуживания по обучению (training-subscription, доработка ТЗ
 * заказчика 05.10.2026). `from`/`to` — ISO `YYYY-MM-DD`, пустые строки — срок не задан. `active`
 * считает сервер: сегодняшняя дата входит в срок (границы включительно) — фронт сам даты с
 * «сегодня» не сравнивает.
 */
export interface TrainingSubscription {
  from: string
  to: string
  active: boolean
}

/** Стандартная обёртка списка с пагинацией. */
export interface ListResponse<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

// --- Блок 4: СОУТ (service-sout, api-contract §3d). JSON-схемы — дословно по пиннингу §3d. ---

/** Член комиссии (snapshot_common.commission). */
export interface CommissionMember {
  /**
   * Ссылка на карточку сотрудника (p3-sout-commission D2): выбор подписанта строго из
   * сотрудников организации. Даёт запрет дублей, список кандидатов ответственного, адрес
   * записи телефона/email. Отсутствует у снимков/черновиков до этого change (читается как
   * ""/undefined — back-compat, D6: строка требует перевыбора, логика — поздние волны).
   */
  personId?: string
  fullName: string
  position: string
  /**
   * Ссылка на справочник должностей (p2-position-dictionary, Волна 3): выбор строгий из
   * combobox. В снапшот подписантов уходит ЗАМОРОЖЕННЫЙ текст `position` (label на момент
   * подачи) + `positionId` рядом. Отсутствует у снимков до этого change (читается как "").
   */
  positionId?: string
  isChair: boolean
}

/** Ответственный за СОУТ (snapshot_common.responsible). */
export interface Responsible {
  /**
   * Ссылка на карточку выбранного члена комиссии (p3-sout-commission D2): кандидаты
   * ответственного ⊆ комиссии. Адрес записи телефона/email обратно в карточку. Отсутствует
   * у снимков до этого change (back-compat, D6).
   */
  personId?: string
  fullName: string
  position: string
  /** Ссылка на справочник должностей (см. `CommissionMember.positionId`). */
  positionId?: string
  phone: string
  email: string
}

/** Служебный транспорт по месту (conditions.transport[]). Массы и год — числа. */
export interface SoutTransport {
  brand: string
  year: number
  massLaden: number
  massUnladen: number
  /** Гос. номер ТС (IC-2, план фазы 2 §решение 3) — необязательное поле, additive. */
  plate: string
}

/** Несчастный случай по месту (conditions.accidents[]). */
export interface SoutAccident {
  description: string
  date: string
  /**
   * Тяжесть (IC-2, план фазы 2 §решение 3) — additive, дополняет description/date.
   * Значения — `ACCIDENT_SEVERITIES` из `lib/sout-dictionaries.ts`, дефолт «Легкий».
   */
  severity: string
}

/** Снимок данных человека на месте (sout_lines.snapshot_person). */
export interface SoutPerson {
  fullName: string
  snils: string
  gender: 'm' | 'f' | ''
  birthDate: string
  isMinor: boolean
  isDisabled: boolean
}

/**
 * Компенсации за вредные условия труда (design D3, §7.1) — шесть независимых отметок
 * дословно по прототипу `sout.html`, вместо свободного текста. Ни одна не отмечена по
 * умолчанию.
 */
export interface SoutCompensations {
  extraPay: boolean
  extraVacation: boolean
  shortWeek: boolean
  milk: boolean
  earlyPension: boolean
  medical: boolean
}

/** Тип рабочего места (§7.1, sout.html): «Действующее» (дефолт) | «Вакансия». */
export type SoutRmType = 'D' | 'V'

/** Условия рабочего места (sout_lines.snapshot_conditions). */
export interface SoutConditions {
  departmentName: string
  actualAddress: string
  position: string
  /** Тип рабочего места (design D3) — «D» действующее (дефолт) | «V» вакансия. */
  rmType: SoutRmType
  shiftHours: number
  weekHours: number
  schedule: string
  /**
   * Свободный текст графика при `schedule === 'Иной'` (IC-2, additive, §решение 8).
   * У снимков до этого change отсутствует — читается как "".
   */
  scheduleOther: string
  hadPreviousSout: boolean
  rmNumber: string
  previousClass: string
  /**
   * Метаданные алгоритма предзаполнения «Предыдущая спецоценка» (p3-sout-workplaces Волна 3,
   * D5.1, §8) — СЕРВЕР-КОНТРОЛИРУЕМЫЕ, клиент их не редактирует напрямую (PATCH их не
   * доверяет присланным значениям). Дублируются в `SoutLine.previousSout` вычисляемым блоком —
   * удобным контрактом для формы (Волна 6). Опциональны (omitempty на бэке; отсутствуют у
   * снимков до Волны 3 — back-compat).
   */
  previousSoutSource?: 'historical' | 'order' | ''
  previousSoutHistoricalResultId?: string
  compensations: SoutCompensations
  equipment: string
  materials: string
  transport: SoutTransport[]
  accidents: SoutAccident[]
  /**
   * Явный признак источника строки (p3-sout-workplaces Волна 5, D2.1, решение B):
   * `'structure'` — создана «Из структуры компании» (сотрудник или вакансия), `'manual'` —
   * любой другой путь (ручное создание, копия, adhoc-пара). `''` (или отсутствует у
   * снимков до этого change) — источник неизвестен, бейдж НЕ показывается.
   */
  lineSource?: SoutLineSource
}

/**
 * Значения `SoutConditions.lineSource`/`SoutLine.lineSource` (Волна 5, D2.1, решение B); `import` —
 * строка загружена из «Анкеты и формы СОУТ» (order-import-legacy-forms).
 */
export type SoutLineSource = 'structure' | 'manual' | 'import' | ''

/**
 * Вычисляемый блок «Предыдущая спецоценка» строки (p3-sout-workplaces Волна 3, D5.1, §8) —
 * `soutLineDTO.previousSout`. `class`/`rmNumber` дублируют `conditions.previousClass`/
 * `conditions.rmNumber` (те же значения, back-compat со старыми снимками, §15); `source` —
 * откуда подставлены значения; `editable=false` СТРОГО ТОЛЬКО при `source==='order'`
 * (данные предыдущего заказа — read-only по ТЗ §8); `historicalResultId` — id исторической
 * записи `sout_results`, куда пишутся правки при `source==='historical'`.
 */
export interface SoutPreviousSout {
  class: string
  rmNumber: string
  source: 'historical' | 'order' | 'none'
  editable: boolean
  historicalResultId?: string
}

/** Строка СОУТ (рабочее место заказа) → DTO `SoutLine` (§3d). */
export interface SoutLine {
  id: string
  workplaceId: string | null
  personId: string | null
  carryoverFromOrderId: string | null
  person: SoutPerson
  conditions: SoutConditions
  valid: boolean
  /** Незаполненные обязательные поля (для красной рамки/счётчика). */
  missing: string[]
  /** Проекция `conditions.lineSource` на верхний уровень (удобно для бейджа в списке). */
  lineSource?: SoutLineSource
  /**
   * Вычисляемый блок «Предыдущая спецоценка» (p3-sout-workplaces Волна 3/6, D5.1, §8) —
   * опционален В ТИПЕ (back-compat со старыми фикстурами/моками тестов, где поле опущено);
   * реальный бэкенд отдаёт его ВСЕГДА (без omitempty). Форма (`workplace-form.tsx`) использует
   * `derivePreviousSout` как безопасный фолбэк при отсутствии.
   */
  previousSout?: SoutPreviousSout
}

/** Анкета СОУТ (GET /api/orders/{id}/sout). */
export interface SoutAnketa {
  organization: CompanyProfile
  commission: CommissionMember[]
  responsible: Responsible
  pdConsent: boolean
  /** Дата согласия на обработку ПД (снимок; сервер уже отдаёт, ранее не было в типе). */
  pdConsentAt?: string
  lines: SoutLine[]
  canEdit: boolean
}

/** Статус места на дашборде «Спецоценка» (§3d, сервер вычисляет). */
export type SoutDashboardStatus = 'overdue' | 'soon' | 'valid' | 'none'

/** Строка дашборда «Спецоценка» (§3d). */
export interface SoutDashboardRow {
  workplaceId: string
  departmentName: string
  position: string
  hazardClass: string
  conductedOn?: string
  validUntil?: string
  perpetual: boolean
  status: SoutDashboardStatus
  /**
   * Сотрудники, связанные с рабочим местом (§4, p3-structure-sout-training, D7) — ВСЕ persons
   * места, включая сменщиков (делят один workplaceId), порядок стабильный (по fullName,
   * сортировка на сервере). Пустой массив — рабочее место вакантно.
   */
  employees: EmployeeSummary[]
}

/** Дашборд «Спецоценка»: светофор-счётчики + порог + строки мест (§3d). */
export interface SoutDashboard {
  counters: { overdue: number; soon: number; valid: number; none: number }
  threshold: number
  rows: SoutDashboardRow[]
}

/** Результат СОУТ по месту (sout_results) → DTO `SoutResult` (§3d). */
export interface SoutResult {
  id: string
  workplaceId: string
  lineId?: string
  hazardClass: string
  /** Исходный класс из анкеты клиента (фиксируется, не затирается правкой менеджера). */
  originalClass: string
  conductedOn?: string
  validUntil?: string
  perpetual: boolean
  sourceOrderId: string
  /** Необязательный комментарий команды к результату (§7.5). */
  comment?: string
  /**
   * Признак «Декларация» (centrika-docs!76) — состояние чекбокса при повторном открытии
   * результата. Сервер выводит его из сохранённых (класс «1»/«2» + бессрочный срок),
   * отдельной колонки в БД нет (см. domain.SoutResultDeclaration).
   */
  declaration?: boolean
  /**
   * «N раб.места» (centrika-docs!90) — номер рабочего места, сохранённый в результате
   * ЭТОГО заказа. Пустая строка — номер ещё не сохранён. Текст: ведущие нули значимы.
   */
  rmNumber?: string
}

/**
 * Историческая (вне заказа) запись СОУТ по рабочему месту (p2-manual-historical, §11.2).
 * Роль-агностично: вносят и правят и клиент, и команда. `validUntil`/`perpetual` —
 * вычисляются сервером (не вводятся вручную, design D3) — читаются как read-only.
 */
export interface SoutHistoricalResult {
  id: string
  workplaceId: string
  hazardClass: string
  conductedOn?: string
  validUntil?: string
  perpetual: boolean
  /**
   * Признак «Декларация» (centrika-docs!29) — состояние чекбокса при повторном открытии
   * записи. Сервер выводит его из сохранённых значений (класс «1»/«2» + бессрочный срок),
   * отдельной колонки в БД нет.
   */
  declaration?: boolean
  comment?: string
  documentFileId?: string
  documentUrl?: string
  updatedAt?: string
  /**
   * Номер рабочего места по прошлой СОУТ (p3-sout-workplaces Волна 1, D5, §8 ТЗ) —
   * необязателен; хранится в исторической записи (миграция 00024). Правило «уже
   * заполненный не перезаписывать» и алгоритм предзаполнения из связанного заказа —
   * бизнес-логика следующей волны, здесь только контракт поля.
   */
  rmNumber?: string
}

/** Создание исторической записи СОУТ (POST /api/sout/historical-results). */
export interface CreateSoutHistoricalResultInput {
  workplaceId: string
  hazardClass: string
  conductedOn: string
  comment?: string
  documentFileId?: string
  rmNumber?: string
  /**
   * Чекбокс «Декларация» (centrika-docs!29): допустим только для классов «1»/«2» (иначе
   * 422 по ключу `declaration`). Срок действия по нему считает сервер — `validUntil`/
   * `perpetual` клиент по-прежнему не передаёт.
   */
  declaration?: boolean
}

/** Правка исторической записи СОУТ (PATCH /api/sout/historical-results/{id}). */
export type UpdateSoutHistoricalResultInput = Partial<CreateSoutHistoricalResultInput>

// --- Блок 4: входные тела (формы — дословно по §3d) ---

/** Автосохранение анкеты (PATCH /api/orders/{id}/sout). */
export interface SoutAnketaPatch {
  commission?: CommissionMember[]
  responsible?: Responsible
  pdConsent?: boolean
}

/**
 * Добавление рабочего места (POST /api/orders/{id}/sout/lines).
 * `copyFromLineId` → копия с обнулённым СНИЛС; `carryover && workplaceId` → перенос условий из
 * последнего поданного/готового заказа СОУТ по этому месту (копия).
 */
export interface AddSoutLineInput {
  workplaceId?: string
  departmentName?: string
  /** Ad-hoc должность — дословный лейбл из справочника (PositionSelect, Волна 3). */
  position?: string
  copyFromLineId?: string
  carryover?: boolean
}

/**
 * Ручное создание нового рабочего места (+опционально нового сотрудника) и строки заказа за
 * один шаг (POST /api/orders/{id}/sout/lines/manual, p3-sout-workplaces §14, D7, Волна 7).
 * Страница «Добавить вручную» (`pages/order-sout-workplace-new.tsx`) собирает `conditions`/
 * `person` целиком из локального черновика (`WorkplaceForm` в режиме `manual`) и шлёт ОДНИМ
 * запросом; `position` — дословный лейбл из справочника (`PositionSelect`, `positionId`
 * опущен намеренно — сервер резолвит строгим текстовым совпадением, как и `AddSoutLineInput`).
 * 422 с дублем (СНИЛС сотрудника / пара подразделение+должность) несёт `details` с полями
 * `departmentName`/`person.snils`/`person.fullName`/`person.birthDate`/`person.gender`.
 */
export interface AddManualSoutLineInput {
  departmentName: string
  position?: string
  positionId?: string
  conditions?: SoutConditions
  person?: SoutPerson
  /**
   * Клиент подтвердил создание ЕЩЁ ОДНОГО рабочего места с теми же подразделением и должностью
   * (centrika-docs!101 — «10 дворников в отделе»). Без флага сервер отклоняет совпадение 422 по
   * полю `departmentName`, поэтому форма сначала запрашивает `workplaceMatches` и показывает
   * диалог подтверждения. На дубль СНИЛС флаг не влияет.
   */
  confirmDuplicate?: boolean
}

/**
 * Совпадающее рабочее место структуры для диалога подтверждения (centrika-docs!101):
 * подразделение, должность и назначенные АКТИВНЫЕ сотрудники. Пустой `persons` — на месте нет
 * сотрудника, диалог показывает «Сотрудник не назначен».
 */
export interface SoutWorkplaceMatch {
  id: string
  departmentName: string
  position: string
  persons: { fullName: string; snils: string }[]
}

/** Автосохранение строки (PATCH /api/orders/{id}/sout/lines/{lineId}). */
export interface UpdateSoutLineInput {
  person?: Partial<SoutPerson>
  conditions?: Partial<SoutConditions>
  workplaceId?: string | null
  personId?: string | null
}

/**
 * Массовое добавление строк из структуры компании (POST /api/orders/{id}/sout/lines/
 * from-persons, §2/§2.2, D2). `personIds` — сотрудники (в т.ч. сменщики — своей строкой на
 * personId); нужен хотя бы один. p3-critical-fixes (№3в): поле `workplaceIds` удалено —
 * вакантные рабочие места больше не попадают в заказ СОУТ этим путём.
 */
export interface SoutBulkAddFromStructureInput {
  personIds: string[]
}

/**
 * Сотрудник, которого НЕ удалось добавить массовым добавлением, и причина (§2.2 критерий
 * «показать, кого и почему не добавили»).
 */
export interface SoutBulkRejection {
  personId?: string
  reason: string
}

/** Ответ массового добавления из структуры компании. */
export interface SoutBulkAddResult {
  added: SoutLine[]
  rejected: SoutBulkRejection[]
}

/** Предпросмотр переноса условий из прошлого заказа (GET …/sout/carryover?workplaceId=). */
export interface SoutCarryoverPreview {
  found: boolean
  fromOrderId?: string
  conditions?: SoutConditions
}

/** Один результат для upsert (PUT /api/team/orders/{id}/sout/results). */
export interface SoutResultInput {
  lineId?: string
  workplaceId?: string
  hazardClass: string
  /**
   * «Дата завершения» строки (centrika-docs!75) — общая дата завершения заказа, которую фронт
   * подставляет в каждую строку. Срок действия по ней считает сервер.
   */
  conductedOn: string
  /**
   * Чекбокс «Декларация» (centrika-docs!76): допустим только для классов «1»/«2». Бессрочность
   * результата даёт ТОЛЬКО отмеченная декларация; сам класс её больше не даёт. `validUntil`/
   * `perpetual` клиент не передаёт — их считает сервер.
   */
  declaration?: boolean
  /** Необязательный комментарий команды к результату (§7.5). */
  comment?: string
  /**
   * «N раб.места» (centrika-docs!90) — необязательный текстовый номер рабочего места по
   * результатам этого заказа. Передаётся ВСЕГДА (в т.ч. пустой строкой): очищенное поле
   * очищает сохранённый номер.
   */
  rmNumber?: string
}

/** Тело upsert результатов СОУТ (§3d). */
export interface PutSoutResultsInput {
  items: SoutResultInput[]
}

/**
 * Правило, по которому строка сводной ведомости сопоставлена с местом заказа
 * (sout-results-statement-import): номер рабочего места, подразделение + должность, только должность;
 * `none` — место заказа не найдено.
 */
export type SoutStatementMatch = 'rm_number' | 'department_position' | 'position' | 'none'

/** Строка рабочего места сводной ведомости и её сопоставление с местом заказа. */
export interface SoutStatementRow {
  /** Порядковый номер места в ведомости (1..N). */
  rowIndex: number
  departmentName: string
  rmNumber: string
  position: string
  /** Графа «Итоговый класс (подкласс) условий труда» как в документе («-» — не заполнено). */
  finalClass: string
  /** Графа «… с учетом эффективного применения СИЗ» как в документе. */
  classWithPpe: string
  /** Класс для подстановки: с учётом СИЗ, если указан, иначе итоговый. Пусто, если не распознан. */
  hazardClass: string
  classRecognized: boolean
  /** Сопоставленное место заказа (`SoutLine.id`); null — не найдено. */
  lineId: string | null
  match: SoutStatementMatch
  /** Пара составлена по порядку среди одинаковых мест — нужна проверка глазами. */
  ambiguous: boolean
}

/**
 * Разобранная сводная ведомость (POST /api/team/orders/{id}/sout/results/statement). Сервер ничего
 * не сохраняет: значения подставляются в форму результатов и сохраняются её кнопкой.
 */
export interface SoutStatement {
  organizationName: string
  /**
   * «Дата составления», ГГГГ-ММ-ДД; пусто — не найдена. Подставляется в общую «Дату завершения»
   * заказа (sout-results-declaration-and-statement-date).
   */
  compiledOn: string
  /** «Рабочие места (ед.)» из сводной таблицы ведомости; 0 — не найдено. */
  declaredWorkplaces: number
  rows: SoutStatementRow[]
  /** Места заказа без строки ведомости, в порядке анкеты. */
  unmatchedLineIds: string[]
}

/** Рабочее место декларации соответствия условий труда. */
export interface SoutDeclarationWorkplace {
  /** Порядковый номер места в декларации (1..N). */
  index: number
  /** Индивидуальный номер рабочего места («20439.001»). */
  rmNumber: string
  position: string
  /** Численность занятых работников; 0 — не указана. */
  headcount: number
}

/**
 * Разобранная декларация (POST /api/team/orders/{id}/sout/results/declaration,
 * sout-results-declaration-and-statement-date). Сервер ничего не сохраняет: места сопоставляются с
 * таблицей результатов на форме, признак «Декларация» сохраняется её кнопкой.
 */
export interface SoutDeclaration {
  organizationName: string
  inn: string
  /** ИНН декларации совпадает с организацией заказа; null — сравнить не с чем. */
  innMatches: boolean | null
  /** «Декларация подана на основании» (заключение эксперта, протоколы). */
  basis: string
  workplaces: SoutDeclarationWorkplace[]
}

// --- Блок 5: обучение (service-training, api-contract §3e). Формы — дословно по §3e. ---

/**
 * Направление программы/записи обучения (CHECK БД; §3e). Русские ярлыки — см. lib/training.
 * `ot` — Охрана труда; `fire` — Пожарная безопасность; `ecology` — Экология;
 * `work_skills` — Рабочие специальности; `industrial_safety` — Промбезопасность; `other` — прочее/ролевые.
 */
export type TrainingDirection =
  'ot' | 'fire' | 'ecology' | 'work_skills' | 'industrial_safety' | 'other'

/**
 * Статус записи/строки обучения (CHECK БД; §3e). Сервер вычисляет авторитетно (lib/training — зеркало).
 * Отображаемая метка `valid` = «В силе». Цвет: expired→red, expiring→amber, valid→green, in_process→gray.
 */
export type TrainingStatus = 'valid' | 'in_process' | 'expiring' | 'expired'

/**
 * Направление программы обучения (справочник, centrika-docs!55). `systemCode` — канон
 * спец-поведения у системных направлений (пусто у пользовательских); `system` — признак
 * системного (нельзя удалить). Наименование `name` редактируемо.
 */
export interface TrainingDirectionItem {
  id: string
  name: string
  systemCode: string
  system: boolean
  /** Дерево разделов каталога (training-catalog-v2): родитель (пусто — корень) и порядок в уровне. */
  parentId: string
  sort: number
}

/** База (требования к образованию) программы каталога — колонка «База (образование)». */
export type EducationBase = 'any' | 'vo_spo' | 'school'

/** ОТ-индекс программы охраны труда. */
/**
 * Индекс программы (§3e): «А», «Б», «ПП», «СИЗ», «В1»… — свободный, буква и до пяти букв или цифр
 * (ответ заказчицы 04.11 к п.5.2: индексы программ В — В1, В2, В3 задаются в каталоге).
 */
export type OtIndex = string

/**
 * Режим периодичности программы (centrika-docs!54): `months` — задана периодичность
 * (`recurrenceMonths`), `employer` — «периодичность определяет работодатель» (не бессрочная, без
 * даты, без напоминаний), `none` — «без срока» (бессрочно).
 */
export type RecurrenceMode = 'months' | 'employer' | 'none'

/**
 * Программа справочника обучения (§3e). `recurrenceMonths:null` — не в режиме `months`. Направление —
 * ссылка на справочник (`directionId`); `directionName` — актуальное имя, `directionSystemCode` —
 * канон спец-поведения (пусто у пользовательского направления). centrika-docs!55, !54.
 */
export interface Program {
  id: string
  name: string
  code: string
  /** Необязательное примечание (centrika-docs!53); переносы строк сохраняются. */
  note: string
  directionId: string
  directionName: string
  directionSystemCode: string
  recurrenceMonths: number | null
  recurrenceMode: RecurrenceMode
  /** Атрибуты каталога заказчицы (training-catalog-v2; ТЗ 26.10.2026, п.4.4.1). */
  shortName: string
  hours: number | null
  documentType: string
  /** «Подлежат обучению» — по пункту на строку. */
  audience: string[]
  rank: string
  profStandard: string
  /** Подпись периодичности как в каталоге — у периодичности без числа лет, иначе пусто. */
  periodText: string
  educationBase: EducationBase
  otIndex: OtIndex | null
  sort: number
  /** Архивная программа читается везде, но в выбор не попадает. */
  archived: boolean
  replacedById?: string
  /** Разложена на программы каталога (training-program-split): из архива не возвращается. */
  split?: boolean
  /** Цена (копейки) и раздел УЦ — только в ответе команде. */
  priceKopecks?: number
  ucSection?: string
  /**
   * Подтверждение (training-packages-approval; ТЗ 26.10.2026, п.4.4.3): клиенту приходят только
   * действующие. Автор, дата добавления и причина отклонения — только команде.
   */
  status: CatalogStatus
  createdById?: string
  createdByName?: string
  createdAt?: string
  reviewNote?: string
}

/** Статус программы и пакета каталога: действует / на подтверждении / отклонено. */
export type CatalogStatus = 'active' | 'pending' | 'rejected'

/** Программа в составе пакета. */
export interface ProgramPackageItem {
  programId: string
  code: string
  name: string
  shortName: string
  hours: number | null
  documentType: string
  status: CatalogStatus
  archived: boolean
}

/**
 * Пакет программ (training-packages-approval; ТЗ 26.10.2026, п.4.4.2): выбирается целиком, с одним
 * списком слушателей на все программы. `available` — можно выбрать в заказ (действует, не в
 * архиве, все программы действуют). Цена, автор и причина отклонения — только команде.
 */
export interface ProgramPackage {
  id: string
  name: string
  sort: number
  status: CatalogStatus
  archived: boolean
  available: boolean
  items: ProgramPackageItem[]
  priceKopecks?: number
  createdById?: string
  createdByName?: string
  createdAt?: string
  reviewNote?: string
}

/** Название, программы (в порядке пакета) и цена пакета. */
export interface ProgramPackageInput {
  name: string
  programIds: string[]
  priceKopecks: number | null
}

/** Решение администратора по добавленному в каталог: подтвердить или отклонить с причиной. */
export type CatalogReviewInput = { decision: 'approve' } | { decision: 'reject'; reason: string }

/** Снимок слушателя в строке/записи (§3e). */
export interface TrainingPerson {
  fullName: string
  snils: string
  departmentName: string
  position: string
}

/**
 * Строка ведомости заказа обучения «человек + программа» (§3e). `dueOn`/`status` считает сервер.
 * ОТ — одна строка с `otIndices` (А/Б/СИЗ/ПП/В) и единым сроком. `documentUrl` — индивидуальный документ.
 */
export interface TrainingLine {
  id: string
  personId: string
  person: TrainingPerson
  programId: string
  programName: string
  directionName: string
  directionSystemCode: string
  grade: string
  otIndices: string[]
  pdConsent: boolean
  pdConsentAt?: string
  issuedOn?: string
  dueOn?: string
  status: TrainingStatus
  documentUrl?: string
}

/**
 * Программа каталога + текущий выбор сотрудников под ней (шаг 2 мастера обучения, §6.3,
 * p2-service-training D1: состав ведёт клиент).
 */
export interface TrainingProgramComposition extends Program {
  selectedPersonIds: string[]
  /**
   * Число мест программы в заказе (training-listeners-count): заявленное число слушателей, но не
   * меньше выбранных сотрудников; 0 — программа не выбрана.
   */
  listenersCount: number
}

/**
 * Пакет программ на шаге 2 мастера обучения (training-order-packages; ответ заказчицы 27.10, В9):
 * выбирается целиком, с одним списком слушателей на все программы; цены клиенту нет. `programs` —
 * у пакета в заказе программы его строк состава, у остальных — текущий состав. `available:false` —
 * пакет выбрать уже нельзя (архив, программа не действует): его слушателей можно только убрать.
 */
export interface TrainingPackageComposition {
  id: string
  name: string
  available: boolean
  programs: Program[]
  selectedPersonIds: string[]
  /** Число мест пакета в заказе (training-listeners-count); 0 — пакет не выбран. */
  listenersCount: number
  /** Цена пакета из каталога — только команде (командный редактор изменений состава). */
  priceKopecks?: number
}

/** Ошибка строки файла каталога (лист программ или разделов). */
export interface CatalogImportIssue {
  sheet: 'programs' | 'sections'
  row: number
  column: string
  code: string
  message: string
}

/** Изменённая (или новая) программа загрузки каталога: поля — подписи колонок, было → стало. */
export interface CatalogProgramChange {
  id: string
  code: string
  name: string
  restored: boolean
  fields: { field: string; old: string; new: string }[]
}

/**
 * Предпросмотр (`applied:false`) или итог (`applied:true`) загрузки файла каталога
 * (POST /api/admin/training/catalog/import). Предпросмотр — та же загрузка с откатом.
 */
export interface CatalogImportResult {
  applied: boolean
  issues: CatalogImportIssue[]
  /** Пакеты из файла: состав и цену файл не задаёт — только есть ли такой пакет в кабинете. */
  packages: { name: string; exists: boolean }[]
  sectionsCreated: string[]
  sectionsRemoved: string[]
  created: CatalogProgramChange[]
  changed: CatalogProgramChange[]
  unchanged: number
  missing: { id: string; code: string; name: string }[]
  archiveMissing: boolean
  recordsRecomputed: number
}

/** Итог разложения прежней программы на программы каталога (training-program-split). */
export interface ProgramSplitResult {
  /** Записи прежней программы, ставшие записями программ состава. */
  recordsSplit: number
  /** Созданные записи программ состава. */
  recordsCreated: number
  /** Программы состава — в порядке выбора. */
  components: Program[]
}

/** Архивная программа без замены, на которую ещё ссылаются записи или незавершённые заказы. */
export interface ProgramReplacementCandidate {
  id: string
  code: string
  name: string
  directionName: string
  recordsCount: number
  openLinesCount: number
  /** Неархивные пакеты, в составе которых программа. */
  packagesCount: number
}

/**
 * Сотрудник в модалке выбора под КОНКРЕТНУЮ программу (GET .../training/employees, §6.3,
 * D7): `status` — относительно ЭТОЙ программы (не агрегат по всем направлениям).
 */
export interface TrainingProgramEmployee {
  personId: string
  fullName: string
  departmentName: string
  position: string
  status: TrainingStatus
  dueOn?: string
  selected: boolean
  /**
   * Почему сотрудника нельзя выбрать здесь (training-order-packages): программа у него в заказе
   * уже есть — в пакете или отдельно. Нет — выбрать можно.
   */
  blocked?: string
}

/** Строка шага 3 «Подтверждение и персональные данные» (§6.4). */
export interface TrainingSelectedEmployee {
  personId: string
  fullName: string
  departmentName: string
  position: string
  snils: string
  birthDate?: string
  gender: string
  /**
   * Актуальный e-mail из карточки сотрудника (centrika-docs!87) — экран «Заказ отправлен»
   * показывает и правит его; это ЖИВОЕ значение карточки, а не замороженный снимок состава.
   */
  email?: string
  /** Образование сотрудника (centrika-docs!59) — свободная строка; шаг 3 приводит к списку значений. */
  education?: string
  programs: string[]
  hasConsent: boolean
  consentFileName?: string
  consentUrl?: string
  /** Документ об образовании сотрудника (centrika-docs!59) — один на человека. */
  educationDocument?: EducationDocumentStatus
  /**
   * Полнота данных слушателя (training-listener-completeness, серверное правило): нужен ли
   * документ о высшем или среднем профессиональном образовании (у слушателя есть программа
   * «ВО/СПО»), светофор и незаполненные поля. Мастер пересчитывает их тем же правилом после правки.
   */
  needsEducationDocument?: boolean
  status?: ListenerStatus
  missing?: ListenerField[]
}

/** Светофор слушателя: всё заполнено / не всё / ничего (training-listener-completeness). */
export type ListenerStatus = 'complete' | 'partial' | 'empty'

/** Поля полноты данных слушателя (ключи серверного правила). */
export type ListenerField =
  | 'snils'
  | 'birthDate'
  | 'gender'
  | 'education'
  | 'email'
  | 'consent'
  | 'educationDocument'

/**
 * 3-шаговый мастер обучения (GET /api/orders/{id}/training, p2-service-training —
 * инверсия D1/A3): состав (программы+сотрудники) формирует КЛИЕНТ, не команда.
 */
export interface TrainingWizard {
  organization?: CompanyProfile
  programs: TrainingProgramComposition[]
  /** Пакеты программ: доступные для выбора и уже выбранные в заказе (training-order-packages). */
  packages: TrainingPackageComposition[]
  selectedEmployees: TrainingSelectedEmployee[]
  canEdit: boolean
  /**
   * Клиент на абонентском обслуживании по обучению (training-order-filling): заявку менеджеру не
   * отправляет — черновик сразу уходит «На проверку». У отправленного заказа — его отметка.
   */
  subscription: boolean
  /**
   * Только у физлица (individual-training-order): «он сам» — единственный слушатель; мастер
   * добавляет его в каждую выбранную программу.
   */
  selfPersonId?: string
}

/**
 * Реконсиляция состава ОДНОЙ программы или ОДНОГО пакета (PATCH /api/orders/{id}/training,
 * автосейв шага 2): желаемый список слушателей; пустой — убрать из заказа.
 */
/**
 * `listenersCount` (training-listeners-count) — заявленное число слушателей, не меньше выбранных
 * сотрудников; 0 вместе с пустым `personIds` убирает программу или пакет из заказа. Без него мест
 * столько, сколько выбрано сотрудников (пустой список убирает позицию).
 */
export type TrainingCompositionPatch =
  | { programId: string; personIds: string[]; listenersCount?: number }
  | { packageId: string; personIds: string[]; listenersCount?: number }

/**
 * Состояние согласия на обработку персональных данных сотрудника. Согласие привязано к карточке
 * сотрудника — один PDF на человека, во всех его заказах обучения; ответ upload/delete и поле
 * карточки сотрудника.
 */
export interface TrainingConsentStatus {
  personId: string
  hasConsent: boolean
  consentFileName?: string
  consentUrl?: string
}

/**
 * Состояние документа об образовании сотрудника (centrika-docs!59). Привязан к карточке
 * сотрудника — один PDF на человека независимо от числа заказов; ответ upload/delete и
 * поле в карточке/составе/шаге 3.
 */
export interface EducationDocumentStatus {
  hasDocument: boolean
  fileName?: string
  url?: string
}

/**
 * Строка слушателя в группе программы состава заказа обучения (centrika-docs!41).
 * Данные — «фотография» состава (снимок у поданного заказа), согласие — один файл на
 * человека независимо от числа его программ.
 */
export interface TrainingTrainee {
  lineId: string
  personId: string
  fullName: string
  snils: string
  position: string
  departmentName: string
  gender: string
  hasConsent: boolean
  consentFileName?: string
  consentUrl?: string
  /** Документ об образовании сотрудника (centrika-docs!59) — один на человека. */
  educationDocument?: EducationDocumentStatus
  /** Полнота данных слушателя (training-listener-completeness): complete|partial|empty. */
  status?: ListenerStatus
}

/**
 * Группа программы в составе заказа обучения: свои реквизиты (дата окончания обучения и
 * итоговый документ — протокол) + слушатели именно этой программы.
 */
export interface TrainingProgramGroup {
  programId: string
  programName: string
  directionName: string
  directionSystemCode: string
  endDate?: string
  documentName?: string
  documentUrl?: string
  /** Пакеты, в составе которых программа выбрана в заказе (training-order-packages). */
  packages?: string[]
  trainees: TrainingTrainee[]
  /**
   * Мест, на которые слушатель ещё не назван (training-listeners-count; только черновик и «На
   * заполнении»).
   */
  unnamedCount?: number
  /**
   * У программы база «ВО/СПО» (снимок, иначе каталог): у слушателей группы есть документ об
   * образовании; иначе поля нет (training-listener-completeness, ответ заказчицы В12).
   */
  needsEducationDocument?: boolean
  /**
   * Программа для строки «плашка документа · краткое название · часы» (ТЗ п.6.3.3): у поданного
   * заказа — снимок на дату подачи. Нет — показывается `programName`.
   */
  program?: Program
}

/**
 * Состав заказа обучения группами по программам — один и тот же вид у команды
 * (`GET /api/team/orders/{id}/training`) и у клиента
 * (`GET /api/orders/{id}/training/composition`). `canEditResults` — заказ «В работе»:
 * реквизиты программ правит команда, клиенту они в любом статусе только на чтение.
 */
export interface TrainingComposition {
  groups: TrainingProgramGroup[]
  canEditResults: boolean
  /**
   * Позиции заявки (training-change-approval): программа, выбранная отдельно, или пакет, места и
   * слушатели. `locked` — по программе позиции уже выпущен итоговый документ, её слушателей не изменить.
   */
  items?: TrainingCompositionItem[]
}

export interface TrainingCompositionItem {
  programId?: string
  packageId?: string
  name: string
  listenersCount: number
  personIds: string[]
  locked: boolean
}

/**
 * Запись обучения у человека (§3e). Два источника: `order` (строка заказа Центрики) и `external`
 * (внешнее обучение, внесено вручную). Оба питают расчёт сроков и видны в блоке «Обучения».
 */
export interface TrainingRecord {
  id: string
  personId: string
  programId: string
  programName: string
  directionName: string
  directionSystemCode: string
  recurrenceMode: RecurrenceMode
  lastTrainedOn?: string
  dueOn?: string
  status: TrainingStatus
  otIndices: string[]
  source: 'order' | 'external'
  documentUrl?: string
  /**
   * Раздел дерева каталога и строка программы «как в каталоге» — блок «Обучения» карточки
   * сотрудника (ответ заказчицы 04.11 к п.5.2); в журнале сроков краткого названия, часов и
   * документа нет.
   */
  directionId?: string
  programShortName?: string
  programHours?: number
  programDocumentType?: string
}

/**
 * Клетка направления в журнале сроков (§3e): цвет статуса + короткий срок
 * («до 08.27» / «21 дн.» / «просроч.» / «—»). `otIndices` — только для direction='ot'.
 */
export interface JournalCell {
  directionKey: TrainingDirection
  directionLabel: string
  status: TrainingStatus
  term?: string
  otIndices?: string[]
}

/**
 * Строка журнала сроков = сотрудник (§3e). `rowStatus` — цвет строки (худший по ВСЕМ направлениям,
 * включая скрытые ролевые); `cells` — клетки по колонкам профиля; `other` — агрегат скрытых «+N»;
 * `records` — полный список для раскрытия строки.
 */
export interface JournalRow {
  personId: string
  fullName: string
  /** Должность сотрудника — под ФИО в строке журнала (centrika-docs, MR !17). */
  position: string
  departmentName: string
  rowStatus: 'red' | 'amber' | 'green' | 'gray'
  cells: JournalCell[]
  other: { count: number; status: 'red' | 'amber' | 'green' | 'gray' }
  records: TrainingRecord[]
}

/** Ответ журнала сроков (§3e). `columns` — колонки по профилю клиента (база + отраслевые). */
export interface JournalResponse {
  counters: { overdue: number; soon: number; ok: number; noData: number }
  threshold: number
  columns: { key: TrainingDirection; label: string }[]
  rows: JournalRow[]
}

// --- Блок 5: входные тела (формы — дословно по §3e) ---

/** Создание программы каталога (POST /api/admin/training/programs, только админ). */
export interface CreateProgramInput {
  name: string
  code?: string
  /** Необязательное примечание (centrika-docs!53). */
  note?: string
  /** Направление — id из справочника направлений (centrika-docs!55). */
  directionId: string
  /** Режим периодичности (centrika-docs!54); опущено трактуется как `months`. */
  recurrenceMode?: RecurrenceMode
  /** Периодичность повторного обучения в месяцах; требуется только при `months`. */
  recurrenceMonths?: number | null
  /** Атрибуты каталога (training-catalog-v2). */
  shortName?: string
  hours?: number | null
  documentType?: string
  audience?: string[]
  rank?: string
  profStandard?: string
  periodText?: string
  educationBase?: EducationBase
  priceKopecks?: number | null
  ucSection?: string
  otIndex?: OtIndex | ''
}

/** Правка программы каталога (PATCH /api/admin/training/programs/{id}). */
export type UpdateProgramInput = Partial<CreateProgramInput>

/** Внешняя запись обучения (POST /api/training/records, source='external'). */
export interface CreateExternalRecordInput {
  personId: string
  programId: string
  lastTrainedOn?: string
  otIndices?: string[]
}

/** Правка внешней записи (PATCH /api/training/records/{id}; только source='external'). */
export interface UpdateExternalRecordInput {
  /** Смена программы — сервер пересчитывает срок по её периодичности. */
  programId?: string
  lastTrainedOn?: string
  otIndices?: string[]
}

/** Ручной триггер напоминания (POST /api/team/training/remind → 202; доставка — Блок 7). */
export interface RemindInput {
  personId: string
  direction?: TrainingDirection
}

/** Фильтры командного журнала (§3e): `client` — обязателен. */
export interface TeamJournalQuery {
  client: string
  q?: string
  status?: string
  direction?: string
  department?: string
}

// --- Блок 6: профриски (service-prof-risks, api-contract §3f). Значения/имена — дословно по §3f. ---

/** Выезды на авто (profrisk_positions.vehicle_use, §3f). Три варианта характера выезда. */
export type VehicleUse = 'none' | 'with_driver' | 'self_drive'

/** Уровень риска результата (profrisk_results.risk_level, §3f). `not_assessed` — нет результата. */
export type RiskLevel = 'high' | 'medium' | 'acceptable' | 'not_assessed'

/** Ответ пункта чек-листа: «да»/«нет»/неотвечено (§3f). */
export type ChecklistValue = 'yes' | 'no' | ''

/**
 * Чек-лист «да/нет» по организации (profrisk_surveys.checklist, §3f): 5 пунктов.
 * Ключи дословно: аптечка/план эвакуации/обучение ОТ/инструкция первой помощи/пожарный инструктаж.
 */
export interface ProfriskChecklist {
  firstAid: ChecklistValue
  evacuationPlan: ChecklistValue
  laborSafetyTraining: ChecklistValue
  firstAidInstruction: ChecklistValue
  fireSafetyBriefing: ChecklistValue
}

/**
 * Строка ручного списка инструкций по ОТ (profrisk_surveys.instructions[], §3f).
 * centrika-docs!25: в строке осталось только «Наименование инструкции» — поле «№»
 * (`num`) из формы убрано, сервер его больше не возвращает и не пишет.
 */
export interface ProfriskInstruction {
  name: string
}

/**
 * Контактное лицо анкеты профрисков (profrisk_surveys.contact_*).
 * centrika-docs!25: ФИО — выбор СОТРУДНИКА организации (`personId`), телефон и e-mail
 * предзаполняются из его профиля и остаются редактируемыми; их правка сохраняется
 * в профиле выбранного сотрудника (сервер синхронизирует при PATCH анкеты).
 */
export interface ProfriskContact {
  /** id выбранного сотрудника (persons.id); пусто — контакт ещё не выбран. */
  personId?: string
  fullName: string
  phone: string
  email: string
}

/** Ответ блока «Разработанные инструкции по охране труда» (centrika-docs!25). '' — не выбран. */
export type ProfriskInstructionsDeveloped = '' | 'yes' | 'no'

/** Способ передачи списка инструкций (centrika-docs!25). '' — не выбран. */
export type ProfriskInstructionsMethod = '' | 'files' | 'manual'

/** Загруженный файл списка инструкций (способ «Загрузить файлы», centrika-docs!25). */
export interface ProfriskInstructionFile {
  id: string
  /** Имя файла как его назвал клиент. */
  name: string
  sizeBytes: number
  /** Дата загрузки, RFC3339. */
  uploadedAt: string
  /** Ссылка на скачивание (общий эндпоинт документов). */
  url: string
}

/**
 * Способ добавления должности на шаге «Должности» (centrika-docs!26): `structure` —
 * диалог «Из структуры компании» (метка «из структуры»), `manual` — кнопка «Добавить
 * вручную» (метка «вручную»), `import` — загрузка «Анкеты и формы (РИСКИ)» (метка «из файла»,
 * order-import-legacy-forms), `''` — источник не известен (должность создана до миграции 00026),
 * метка не показывается.
 */
export type ProfriskPositionSource = '' | 'structure' | 'manual' | 'import'

/** Источник, который клиент передаёт при добавлении должности (`import` ставит только сервер). */
export type ProfriskPositionClientSource = Extract<ProfriskPositionSource, 'structure' | 'manual'>

/**
 * Должность заказа профрисков (profrisk_positions) → DTO `ProfriskPosition` (§3f).
 * `cardsCount` — служебное (только команда видит/правит); клиенту в анкете — `null`.
 */
export interface ProfriskPosition {
  id: string
  departmentName: string
  position: string
  /** Ссылка на справочник должностей (p2-position-dictionary): строгий выбор из combobox. */
  positionId?: string
  headcount: number
  vehicleUse: VehicleUse
  equipment: string
  /**
   * Источник должности (centrika-docs!26) — метка в заголовке формы: «из структуры» /
   * «вручную». Пусто — должность создана до введения метки, источник не известен и
   * метка НЕ показывается (восстанавливать его сверкой со структурой нельзя).
   */
  source: ProfriskPositionSource
  /** «Количество карт по итогу работ» — заполняет только команда; клиенту null. */
  cardsCount: number | null
  valid: boolean
  /** Незаполненные обязательные поля (для красной рамки/счётчика). */
  missing: string[]
}

/** Анкета профрисков (GET /api/orders/{id}/profrisk, §3f). Организация — read-only из профиля. */
export interface ProfriskAnketa {
  organization: CompanyProfile
  contact: ProfriskContact
  checklist: ProfriskChecklist
  /** Блок «Разработанные инструкции по охране труда» (centrika-docs!25). */
  instructionsDeveloped: ProfriskInstructionsDeveloped
  instructionsMethod: ProfriskInstructionsMethod
  /** Ручной список (способ `manual`); всегда массив. */
  instructions: ProfriskInstruction[]
  /** Загруженные файлы (способ `files`); всегда массив (не null). */
  instructionFiles: ProfriskInstructionFile[]
  positions: ProfriskPosition[]
  canEdit: boolean
}

/** Результат профрисков по должности (profrisk_results) → DTO `ProfriskResult` (§3f). */
export interface ProfriskResult {
  id: string
  positionId?: string
  department: string
  position: string
  riskLevel: RiskLevel
  assessedOn?: string
  sourceOrderId: string
}

/** Строка дашборда «Профриски» (§3f): должность структуры LEFT JOIN последний результат. */
export interface ProfriskDashboardRow {
  department: string
  position: string
  /**
   * Ссылка на справочник должностей строки структуры (centrika-docs!26). Диалог
   * «Должности из структуры компании» шага 3 собирается из этого же дашборда и шлёт
   * в массовое добавление строгий id, а не свободный текст.
   */
  positionId: string
  headcount: number
  riskLevel: RiskLevel
  assessedOn?: string
  sourceOrderId?: string
  /** Id рабочего места строки (p2-manual-historical) — точка входа исторического ввода. */
  workplaceId: string
  /**
   * Сотрудники, связанные с рабочим местом строки (centrika-docs,
   * page.occupational-safety.profrisks, MR !20) — ВСЕ persons места, включая сменщиков
   * (делят один workplaceId), порядок стабильный (по fullName, сортировка на сервере).
   * Пустой массив — рабочее место вакантно.
   */
  employees: EmployeeSummary[]
  /**
   * Сменное рабочее место (centrika-docs!32): среди активных сотрудников места есть хотя бы
   * один с признаком сменности. Только по этому признаку список ФИО подписывается
   * «Сменщики:» — несколько сотрудников на одном месте сменностью НЕ являются (обычный поток
   * создания сотрудника переиспользует место по паре подразделение+должность).
   */
  shift: boolean
  /**
   * Должность строки включена хотя бы в один НЕЗАВЕРШЁННЫЙ заказ профрисков
   * (centrika-docs!34). По этому признаку строка получает статус оценки «В работе»,
   * приоритетный над ранее выполненной оценкой: `riskLevel`/`assessedOn` продолжают нести
   * последний видимый клиенту результат, но статусом уже не управляют.
   */
  inProgress: boolean
}

/**
 * Дашборд «Профриски»: счётчики + строки должностей (§3f). Счётчики считаются по СТАТУСУ
 * оценки (centrika-docs!34): строки «В работе» не входят ни в `high/medium/acceptable`
 * (окно «Оценено» и гистограмма «Уровни рисков»), ни в `notAssessed`.
 */
export interface ProfriskDashboard {
  counters: { high: number; medium: number; acceptable: number; notAssessed: number }
  rows: ProfriskDashboardRow[]
}

/**
 * Историческая (вне заказа) запись профрисков по рабочему месту (p2-manual-historical,
 * §11.3, Б7). Роль-агностично: вносят и правят и клиент, и команда. `riskLevel` — только
 * три значения (без `not_assessed`, в отличие от результата заказа).
 */
export type HistoricalRiskLevel = Exclude<RiskLevel, 'not_assessed'>

export interface ProfriskHistoricalResult {
  id: string
  workplaceId?: string
  department: string
  position: string
  riskLevel: HistoricalRiskLevel
  assessedOn?: string
  comment?: string
  documentFileId?: string
  documentUrl?: string
  updatedAt?: string
}

/** Создание исторической записи профрисков (POST /api/profrisk/historical-results). */
export interface CreateProfriskHistoricalResultInput {
  workplaceId: string
  riskLevel: HistoricalRiskLevel
  assessedOn: string
  comment?: string
  documentFileId?: string
}

/** Правка исторической записи профрисков (PATCH .../historical-results/{id}). */
export type UpdateProfriskHistoricalResultInput = Partial<CreateProfriskHistoricalResultInput>

// --- Блок 6: входные тела (формы — дословно по §3f) ---

/** Автосохранение анкеты-части-1 (PATCH /api/orders/{id}/profrisk). */
export interface ProfriskAnketaPatch {
  contact?: ProfriskContact
  checklist?: ProfriskChecklist
  /** Блок «Разработанные инструкции по охране труда» (centrika-docs!25). */
  instructionsDeveloped?: ProfriskInstructionsDeveloped
  instructionsMethod?: ProfriskInstructionsMethod
  /** Ручной список; файлы правятся отдельными эндпоинтами (upload/delete). */
  instructions?: ProfriskInstruction[]
}

/**
 * Добавление должности (POST /api/orders/{id}/profrisk/positions).
 * `copyFromId` — дублирует должность целиком (без обнуления, в отличие от СОУТ).
 */
export interface AddProfriskPositionInput {
  departmentName?: string
  position?: string
  /** Строгий выбор должности из справочника (p2-position-dictionary, Волна 3). */
  positionId?: string
  headcount?: number
  vehicleUse?: VehicleUse
  equipment?: string
  /**
   * Источник должности (centrika-docs!26). «Добавить вручную» шлёт `{source:'manual'}`
   * без прочих полей — сервер создаёт пустую форму. В PATCH игнорируется.
   */
  source?: ProfriskPositionClientSource
  /**
   * @deprecated Действие «Копировать» убрано из требований шага «Должности»
   * (centrika-docs!26). Серверная ветка сохранена, но клиент её больше не вызывает.
   */
  copyFromId?: string
}

/**
 * Элемент массового добавления должностей (centrika-docs!26, диалог «Из структуры
 * компании» → «Добавить выбранные»). Подставляются ТОЛЬКО подразделение и должность:
 * количество штатных единиц клиент всегда вводит вручную.
 */
export interface AddProfriskPositionsBatchItem {
  departmentName: string
  position: string
  /** Строгий id справочника из строки дашборда (`ProfriskDashboardRow.positionId`). */
  positionId?: string
  source?: ProfriskPositionClientSource
}

/** Тело POST /api/orders/{id}/profrisk/positions/batch — минимум одна должность. */
export interface AddProfriskPositionsBatchInput {
  positions: AddProfriskPositionsBatchItem[]
}

/** Ответ массового добавления: СОЗДАННЫЕ должности в порядке входного массива. */
export interface AddProfriskPositionsBatchResult {
  positions: ProfriskPosition[]
}

/**
 * Правка должности (PATCH …/positions/{posId}). Клиент правит данные должности;
 * команда правит служебное `cardsCount` (через `teamApi.profrisk.updatePosition`).
 */
export interface UpdateProfriskPositionInput {
  departmentName?: string
  position?: string
  /** Строгий выбор должности из справочника (p2-position-dictionary, Волна 3). */
  positionId?: string
  headcount?: number
  vehicleUse?: VehicleUse
  equipment?: string
  cardsCount?: number | null
}

/** Один результат для upsert (PUT /api/team/orders/{id}/profrisk/results, §3f). */
export interface ProfriskResultInput {
  positionId?: string
  department?: string
  position?: string
  riskLevel: RiskLevel
  assessedOn: string
}

/** Тело upsert результатов профрисков (§3f). */
export interface PutProfriskResultsInput {
  items: ProfriskResultInput[]
}

// --- Блок 8: админ-панель уведомлений (admin-notifications, api-contract §3h). Дословно по §3h. ---

/**
 * Встроенный вид письма-уведомления (`AdminTemplate`, §3h). `label`/`placeholders`/`critical`/
 * `editableOffsets`/`editableRecipients` приходят из доменного каталога бэкенда
 * (`editableOffsets` — у плановых напоминаний, `editableRecipients` — у письма команде о новом
 * заказе `order_submitted`). `offsets` — дни напоминаний (дефолт 60/30/7). `recipients` —
 * адреса получателей письма команде, заданные администратором; `teamInbox` — общий ящик команды
 * из настроек сервера (письмо уходит и на него; пусто — не задан). У остальных видов оба пусты.
 * Удаления вида нет — только включить/выключить; критичные виды защищают вход (см. `critical`).
 */
export interface AdminTemplate {
  kind: string
  label: string
  enabled: boolean
  subject: string
  bodyTemplate: string
  offsets: number[]
  placeholders: string[]
  builtin: boolean
  critical: boolean
  editableOffsets: boolean
  editableRecipients: boolean
  recipients: string[]
  teamInbox: string
}

/**
 * Тело PATCH /admin/notification-templates/{kind} (§3h). Любое подмножество полей.
 * `confirm:true` обязателен при выключении критичного вида (иначе 409); `offsets` принимается
 * только у плановых напоминаний, `recipients` — только у письма команде (адрес с опечаткой →
 * 422 с полем `recipients.N`); метки вне каталога вида → 422 со списком недопустимых.
 */
export interface PatchTemplateInput {
  subject?: string
  bodyTemplate?: string
  enabled?: boolean
  offsets?: number[]
  recipients?: string[]
  confirm?: boolean
}

/** Тело предпросмотра/тест-отправки (§3h): редактируемые тема и текст-шаблон. */
export interface TemplateDraftInput {
  subject: string
  bodyTemplate: string
}

/** Ответ предпросмотра (§3h): тема и текст с подставленными примерными значениями меток. */
export interface TemplatePreview {
  subject: string
  body: string
}

/**
 * Тело ручной разовой рассылки POST /admin/broadcasts (§3h). Адресаты — выбранные клиенты
 * (`clientIds` → резолв через clientRecipients) и/или конкретные контакты (`contactIds`).
 */
export interface BroadcastInput {
  subject: string
  body: string
  clientIds?: string[]
  contactIds?: string[]
}

// --- Ошибки ---

export type ApiErrorCode =
  | 'bad_request'
  | 'validation_error'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  // Организация клиента-компании (multi-organization-login), оба кода приходят с HTTP 409:
  // организация в сессии не выбрана (или её открепили) / вкладка открыта на другой организации.
  | 'organization_required'
  | 'organization_changed'
  | 'rate_limited'
  | 'internal'

export interface ApiFieldError {
  field: string
  message: string
}

/** Ошибка JSON-API: несёт код домена, HTTP-статус и детали полей. */
export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number
  readonly details?: ApiFieldError[]

  constructor(status: number, code: ApiErrorCode, message: string, details?: ApiFieldError[]) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

interface ErrorEnvelope {
  error?: { code?: ApiErrorCode; message?: string; details?: ApiFieldError[] }
}

const FALLBACK_CODE: Record<number, ApiErrorCode> = {
  400: 'bad_request',
  401: 'unauthenticated',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  422: 'validation_error',
  429: 'rate_limited',
}

function toApiError(status: number, payload: unknown): ApiError {
  const env = (payload ?? {}) as ErrorEnvelope
  const code = env.error?.code ?? FALLBACK_CODE[status] ?? 'internal'
  const message = env.error?.message ?? `Ошибка запроса (${status})`
  return new ApiError(status, code, message, env.error?.details)
}

// --- CSRF (double-submit) ---

const CSRF_COOKIE = 'csrf'
const CSRF_HEADER = 'X-CSRF-Token'

// --- Организация вкладки (multi-organization-login) ---

const ORG_HEADER = 'X-Org-Id'

/**
 * Организация, на которой открыта эта вкладка кабинета (задаёт провайдер авторизации по контексту
 * пользователя). Уходит в заголовке каждого запроса: если в другой вкладке переключили
 * организацию, сервер отвечает 409 `organization_changed`, а не выполняет действие в чужой
 * организации. У физлица и команды организации нет — заголовок не отправляется.
 */
let activeOrganizationId: string | null = null

export function setActiveOrganization(id: string | null): void {
  activeOrganizationId = id
}

function orgHeaders(): Record<string, string> {
  return activeOrganizationId ? { [ORG_HEADER]: activeOrganizationId } : {}
}

/** Событие сессии по организации: фронт перечитывает контекст или перезагружает вкладку. */
export type OrganizationSessionEvent = 'organization_required' | 'organization_changed'

let organizationEventHandler: ((event: OrganizationSessionEvent) => void) | null = null

/** Подписка провайдера авторизации на 409 по организации; возвращает отписку. */
export function onOrganizationEvent(handler: (event: OrganizationSessionEvent) => void): () => void {
  organizationEventHandler = handler
  return () => {
    if (organizationEventHandler === handler) organizationEventHandler = null
  }
}

/** Ошибка ответа сервера; 409 по организации дополнительно сообщается подписчику. */
function responseError(status: number, payload: unknown): ApiError {
  const err = toApiError(status, payload)
  if (err.code === 'organization_required' || err.code === 'organization_changed') {
    organizationEventHandler?.(err.code)
  }
  return err
}

/** Кэш токена — резерв для окружений, где cookie недоступна (тесты с моком fetch). */
let cachedCsrfToken: string | null = null

/**
 * In-flight промис `GET /auth/csrf`: дедуплицирует параллельные запросы токена. Иначе две
 * одновременные мутации до установки cookie запустили бы два GET (сервер выдаёт разные токены)
 * → cookie и заголовок рассинхронизировались бы, и запрос словил бы 403. Все ждут один промис.
 */
let pendingCsrfFetch: Promise<string> | null = null

/** Сбросить кэш CSRF (например, после выхода). */
export function resetCsrfCache(): void {
  cachedCsrfToken = null
  pendingCsrfFetch = null
}

function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${CSRF_COOKIE}=`))
  return match ? decodeURIComponent(match.slice(CSRF_COOKIE.length + 1)) : null
}

/**
 * Текущий CSRF-токен: приоритетно из cookie (гарантирует совпадение header===cookie),
 * иначе из кэша, иначе запрашивает `GET /api/auth/csrf` (он же ставит cookie).
 */
async function ensureCsrfToken(): Promise<string> {
  const fromCookie = readCsrfCookie()
  if (fromCookie) {
    cachedCsrfToken = fromCookie
    return fromCookie
  }
  if (cachedCsrfToken) return cachedCsrfToken
  // Запрос токена уже идёт — переиспользуем его, не плодя параллельные GET /auth/csrf.
  if (pendingCsrfFetch) return pendingCsrfFetch
  pendingCsrfFetch = (async () => {
    const res = await fetch(apiUrl('/auth/csrf'), { credentials: 'include' })
    if (!res.ok) {
      throw toApiError(res.status, await safeJson(res))
    }
    const data = (await res.json()) as { csrfToken: string }
    cachedCsrfToken = data.csrfToken
    return data.csrfToken
  })()
  try {
    return await pendingCsrfFetch
  } finally {
    pendingCsrfFetch = null
  }
}

// --- Низкоуровневый запрос ---

type QueryValue = string | number | boolean | undefined | null
export type QueryParams = Record<string, QueryValue>

function buildQuery(params?: QueryParams): string {
  if (!params) return ''
  const usp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      usp.append(key, String(value))
    }
  }
  const qs = usp.toString()
  return qs ? `?${qs}` : ''
}

async function safeJson(res: Response): Promise<unknown> {
  const text = await res.text()
  if (!text) return undefined
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  query?: QueryParams
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase()
  const mutating = method !== 'GET' && method !== 'HEAD'

  const headers: Record<string, string> = { ...orgHeaders() }
  let body: string | undefined
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.body)
  }
  if (mutating) {
    headers[CSRF_HEADER] = await ensureCsrfToken()
  }

  const res = await fetch(apiUrl(path) + buildQuery(options.query), {
    method,
    credentials: 'include',
    headers,
    body,
    signal: options.signal,
  })

  if (res.status === 204) {
    return undefined as T
  }
  const payload = await safeJson(res)
  if (!res.ok) {
    throw responseError(res.status, payload)
  }
  return payload as T
}

/** Типобезопасные хелперы по HTTP-методам. */
export const api = {
  get: <T>(path: string, query?: QueryParams, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
}

/**
 * GET файла (Blob) с owner-scoped эндпоинта — для выгрузок, которые формирует сервер
 * (Excel-список слушателей, centrika-docs!42). Обычная ссылка тут не годится: интерфейс
 * показывает состояние «Формирование…» и сообщение об ошибке, а значит нужен ответ
 * запроса, а не переход браузера. Ошибки — тот же конверт `{error:{...}}`, что у JSON.
 */
async function getBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const res = await fetch(apiUrl(path), {
    method: 'GET',
    credentials: 'include',
    headers: orgHeaders(),
    signal,
  })
  if (!res.ok) {
    throw responseError(res.status, await safeJson(res))
  }
  return res.blob()
}

/**
 * POST multipart/form-data (загрузка файлов). Content-Type не задаём — браузер сам выставит
 * boundary для FormData. CSRF-заголовок и cookie-сессия — как у обычных мутаций.
 */
async function postMultipart<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { ...orgHeaders(), [CSRF_HEADER]: await ensureCsrfToken() }
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: form,
  })
  if (res.status === 204) return undefined as T
  const payload = await safeJson(res)
  if (!res.ok) {
    throw responseError(res.status, payload)
  }
  return payload as T
}

// --- Доменные эндпоинты Блока 1 ---

export interface LoginInput {
  email: string
  password: string
}

export interface SetupAdminInput {
  email: string
  password: string
  fullName: string
}

export interface RegisterIndividualInput {
  email: string
  password: string
  fullName: string
  gender?: 'male' | 'female'
  birthDate?: string
  snils?: string
  education?: string
}

export interface ChangePasswordInput {
  currentPassword: string
  newPassword: string
}

/**
 * Через какой адрес открыт кабинет (team-zone-login-page; 152-ФЗ, разделение входов): `team` —
 * внутренний адрес кабинета сотрудников (только из сети офиса), `client` — внешний.
 * `clientUrl` — адрес клиентского кабинета, только на внутреннем адресе.
 */
export interface EntryZone {
  zone: 'team' | 'client'
  clientUrl?: string
}

export const authApi = {
  /** Через какой адрес открыт кабинет — подписи страниц входа (без сессии). */
  entryZone: () => api.get<EntryZone>('/auth/zone'),
  /** Гарантирует наличие CSRF-токена/cookie (можно вызвать до первой мутации). */
  primeCsrf: () => ensureCsrfToken(),
  setupStatus: () => api.get<{ needsSetup: boolean }>('/setup/status'),
  setupAdmin: (input: SetupAdminInput) => api.post<{ user: User }>('/setup/admin', input),
  login: (input: LoginInput) => api.post<{ user: User }>('/auth/login', input),
  logout: () => api.post<void>('/auth/logout'),
  /** @internal — ручная проба сессии (контракт `GET /auth/me`); в Блоке 1 не используется. */
  me: () => api.get<{ user: User }>('/auth/me'),
  context: () => api.get<MeContext>('/me/context'),
  register: (input: RegisterIndividualInput) => api.post<{ user: User }>('/auth/register', input),
  /** Организации входа клиента-компании (multi-organization-login): окно выбора и переключатель. */
  organizations: (signal?: AbortSignal) =>
    api.get<{ items: Organization[] }>('/auth/organizations', undefined, signal),
  /** Выбрать активную организацию: сервер выдаёт новую cookie сессии. */
  selectOrganization: (clientId: string) =>
    api.post<{ user: User }>('/auth/organizations/select', { clientId }),
  changePassword: (input: ChangePasswordInput) => api.post<void>('/auth/password/change', input),
  requestPasswordReset: (email: string) =>
    api.post<void>('/auth/password-reset/request', { email }),
  confirmPasswordReset: (token: string, newPassword: string) =>
    api.post<void>('/auth/password-reset/confirm', { token, newPassword }),
}

/** Вкладка ленты «колокольчика» (кроме «Все»). */
export type NotificationCategory = 'deadlines' | 'orders' | 'other'

export interface MarkReadInput {
  ids?: string[]
  all?: boolean
  /** С `all`: «прочитать все» только во вкладке ленты. */
  category?: NotificationCategory
}

// --- «Мои вопросы» (client-questions; ТЗ п.9.7, В29) ---

/** Статус вопроса: ждёт ответа Центрики, Центрика ответила, клиент закрыл. */
export type QuestionStatus = 'open' | 'answered' | 'closed'

export interface QuestionAttachment {
  id: string
  name: string
  size: number
}

export interface QuestionMessage {
  id: string
  fromClient: boolean
  /** Клиенту ответы Центрики подписаны «Центрика»; команде — имя сотрудника. */
  authorName: string
  body: string
  createdAt: string
  attachments: QuestionAttachment[]
}

export interface QuestionListItem {
  id: string
  number: number
  subject: string
  status: QuestionStatus
  orderId?: string
  orderNumber?: string
  createdAt: string
  lastMessageAt: string
  /** Только клиенту: непрочитанный ответ Центрики. */
  unread?: boolean
  /** Только команде: клиент вопроса. */
  clientId?: string
  clientName?: string
}

export interface QuestionDetail extends QuestionListItem {
  authorName: string
  messages: QuestionMessage[]
  canReply: boolean
  canClose: boolean
}

export interface QuestionListQuery {
  status?: string
  orderId?: string
  q?: string
  limit?: number
  offset?: number
}

export interface NewQuestionInput {
  subject: string
  body: string
  orderId?: string
  files: File[]
}

function questionForm(fields: Record<string, string | undefined>, files: File[]): FormData {
  const fd = new FormData()
  for (const [k, v] of Object.entries(fields)) if (v !== undefined) fd.append(k, v)
  for (const f of files) fd.append('files', f)
  return fd
}

/** Клиент: вопросы своей организации (активной в сессии). */
export const questionsApi = {
  list: (query?: QuestionListQuery, signal?: AbortSignal) =>
    api.get<ListResponse<QuestionListItem>>('/questions', { ...query }, signal),
  get: (id: string, signal?: AbortSignal) =>
    api.get<QuestionDetail>(`/questions/${id}`, undefined, signal),
  create: (input: NewQuestionInput) =>
    postMultipart<QuestionDetail>(
      '/questions',
      questionForm({ subject: input.subject, body: input.body, orderId: input.orderId }, input.files),
    ),
  reply: (id: string, body: string, files: File[]) =>
    postMultipart<QuestionDetail>(`/questions/${id}/messages`, questionForm({ body }, files)),
  close: (id: string) => api.post<QuestionDetail>(`/questions/${id}/close`),
}

export const notificationsApi = {
  /**
   * `category` — вкладка ленты (notification-categories; ТЗ п.9.8, В25): `deadlines` — сроки,
   * `orders` — заказы, `other` — всё, кроме заказов; без неё — все записи.
   */
  list: (
    query?: { unread?: boolean; category?: NotificationCategory; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) => api.get<ListResponse<Notification>>('/notifications', { ...query }, signal),
  read: (input: MarkReadInput) => api.post<void>('/notifications/read', input),
}

/**
 * Запись глобального справочника должностей (p2-position-dictionary, §D2). Строгий выбор
 * должности во всех формах scope идёт из этого справочника (combobox `PositionSelect`).
 * `GET /api/positions` отдаёт голый массив `{id,name,verified}[]` (НЕ ListResponse), сортированный
 * по имени; доступен любому вошедшему. Служебный сентинел «(Должность не указана)» приходит
 * в списке — фронт-combobox прячет записи с именем, начинающимся на «(».
 */
export interface Position {
  id: string
  name: string
  /**
   * Проверена администратором (positions-dictionary-review). В подсказках выбора должности —
   * только проверенные; новые наименования клиентов приходят непроверенными. Отсутствие поля
   * (старые моки) трактуется как «проверена» — см. `isVerifiedPosition`.
   */
  verified?: boolean
  /**
   * Скрыта администратором (positions-hide): не показывается в подсказках выбора должности и в
   * очереди «Не проверенные», но остаётся в справочнике со всеми ссылками. Отсутствие поля (старые
   * моки) трактуется как «не скрыта» — см. `isHiddenPosition`.
   */
  hidden?: boolean
}

/** Должность проверена (поле не пришло — считаем проверенной: сервер всегда его отдаёт). */
export function isVerifiedPosition(p: Position): boolean {
  return p.verified !== false
}

/** Должность скрыта администратором из подсказок (поле не пришло — не скрыта). */
export function isHiddenPosition(p: Position): boolean {
  return p.hidden === true
}

/** Должность предлагается в подсказках выбора: проверена и не скрыта. */
export function isOfferedPosition(p: Position): boolean {
  return isVerifiedPosition(p) && !isHiddenPosition(p)
}

/** Что изменит объединение должностей (GET /api/admin/positions/{id}/merge-preview). */
export interface PositionMergePreview {
  /** Рабочих мест исходной должности и у скольких клиентов. */
  workplaces: number
  clients: number
  /** Ссылок профрисков (должности анкет, результаты). */
  profriskPositions: number
  profriskResults: number
  /** Пар «клиент + подразделение», где после объединения окажутся два места одной должности. */
  duplicatePairs: number
  /** Результатов профрисков, из-за которых объединить нельзя (сервер ответит 409). */
  conflicts: number
}

/** Итог объединения (POST /api/admin/positions/{id}/merge): целевая запись + что изменилось. */
export interface PositionMergeResult extends PositionMergePreview {
  target: Position
}

// ---------- Экология: справочник услуг и документов (ecology-catalog, api-contract §3i) ----------

/** Вид позиции: проектный документ/норматив (срок действия) или отчётность (периодичность). */
export type EcoKind = 'document' | 'report'
/**
 * Подраздел «Экологии», где показываются строки позиции (ecology-lab-control): документы — всегда
 * «Проектные работы», отчётность — «Отчётность» или «Лабораторный контроль».
 */
export type EcoSection = 'projects' | 'reports' | 'lab'
/**
 * Периодичность отчётности; `''` — не задана (у позиций, которые не ведутся в пульте);
 * `continuous` — ведётся постоянно, без сроков («ведётся / не ведётся»).
 */
export type EcoPeriod = '' | 'yearly' | 'quarterly' | 'monthly' | 'by_pek' | 'continuous'
/** Категория объекта НВОС; `none` — предприятие без категории (не стоит на учёте как объект НВОС). */
export type EcoCategory = 'I' | 'II' | 'III' | 'IV' | 'none'
/** Вид изменений на предприятии (ежегодный вопрос «что менялось»). */
export type EcoUpdateTrigger = 'address' | 'legal' | 'equipment' | 'waste' | 'head'

/**
 * Позиция справочника услуг и документов по экологии. `code` — системный ключ встроенной позиции
 * (пуст у добавленных администратором); `builtin` — встроенная (не удаляется). Срок действия
 * документа — `validityYears` (null = бессрочно, пояснение в `validityNote`); у отчётности —
 * `period` и, у ежегодной, дата сдачи `deadlineDay`/`deadlineMonth`. `tracked` — позиция ведётся
 * строкой в пульте площадки. `dependsOn` — id документов, после нового документа по которым позиция
 * требует актуализации (ПЭК, НДВ, НМУ — после инвентаризации). `trainingLinked` — строка пульта
 * берётся из журнала обучения, заказ — через «Обучение» (в заказе экологии позиции нет).
 */
export interface EcoCatalogItem {
  id: string
  code: string
  builtin: boolean
  name: string
  kind: EcoKind
  section: EcoSection
  tracked: boolean
  validityYears: number | null
  validityNote: string
  period: EcoPeriod
  deadlineDay: number | null
  deadlineMonth: number | null
  developmentTerm: string
  approval: string
  clientDescription: string
  categories: EcoCategory[]
  conditionNote: string
  updateTriggers: EcoUpdateTrigger[]
  dependsOn: string[]
  /** «Вопросы при заказе» (ecology-order): вопросы опросника, которые мастер задаёт по этой услуге. */
  orderQuestionIds: string[]
  showInOrder: boolean
  trainingLinked: boolean
  /**
   * Документ, разработанный раньше этой даты (`YYYY-MM-DD`), требует актуализации: сменились бланк
   * или методика (паспорта отходов до 2014 года, планы НМУ до 03.2026). Пусто — правила нет.
   */
  outdatedBefore: string
  /** В заказе услуги заранее отмечены все площадки организации — клиент снимает лишние. */
  orgWide: boolean
  /**
   * Поля карточки строки (ecology-card-fields): встроенные поля, которых у позиции нет, подписи
   * встроенных полей вместо общих и дополнительные поля в порядке показа.
   */
  hiddenFields: EcoHideableField[]
  fieldLabels: Partial<Record<EcoBuiltinField, string>>
  fields: EcoCardField[]
  /** Поля заявки — реквизиты площадки, которые мастер заказа спрашивает по услуге (ecology-order-fields). */
  orderFields: EcoOrderField[]
}

/**
 * Поле заявки позиции справочника (ecology-order-fields, файл отдела экологии 28.09.2026):
 * `number` — целое число не меньше нуля, `choice_multi` — один или несколько вариантов `options`,
 * `fkko_list` — отходы «код ФККО + наименование», код необязателен, `file_multi` — файлы
 * (прикрепляются отдельными запросами; ecology-order-fields-files-fkko).
 */
export interface EcoOrderField {
  id: string
  key: string
  label: string
  hint: string
  type: 'number' | 'choice_multi' | 'fkko_list' | 'file_multi'
  options: string[]
  required: boolean
}

/**
 * Поля заявки позиции из панели администратора (ecology-order-fields-admin): сохраняются целиком;
 * поле без `id` — новое (ключ назначает сервер), поле, которого нет в списке, удаляется вместе со
 * значениями неподанных заявок. Тип сохранённого поля не меняется.
 */
export interface EcoOrderFieldsInput {
  fields: {
    id?: string
    label: string
    hint: string
    type: EcoOrderField['type']
    options: string[]
    required: boolean
  }[]
}

/** Значение поля заявки: число или выбранные варианты. */
export type EcoOrderFieldValue = number | string[]

/** Реквизит площадки в «фотографии» поданного заказа. */
export interface EcoOrderSnapshotValue {
  label: string
  value: string
  /** Файлы файлового поля — для ссылок на скачивание. */
  files?: EcoFieldFile[]
}

/** Встроенное поле карточки строки, которое позиция может переподписать. */
export type EcoBuiltinField =
  'developed_on' | 'valid_until' | 'doc_number' | 'last_submitted_on' | 'note'
/** Встроенное поле, которое позиция может скрыть. */
export type EcoHideableField = 'doc_number' | 'valid_until'

/**
 * Настройка карточки позиции из панели администратора (ecology-card-fields-admin): сохраняется
 * целиком; поле без `id` — новое (ключ назначает сервер), поле, которого нет в списке, удаляется
 * вместе со значениями клиентов. Тип существующего поля не меняется.
 */
export interface EcoCardConfigInput {
  hiddenFields: EcoHideableField[]
  fieldLabels: Partial<Record<EcoBuiltinField, string>>
  fields: {
    id?: string
    label: string
    hint: string
    type: EcoCardField['type']
    required: boolean
    group: string
  }[]
}

/**
 * Дополнительное поле карточки: `number` — целое число не меньше нуля; `site_nvos` — код объекта
 * НВОС и категория площадки (хранятся в площадке, правка в карточке меняет площадку); `file` — один
 * файл (новая загрузка заменяет прежний); `file_multi` — несколько файлов (ecology-card-file-fields);
 * `fkko_list` — список отходов «код ФККО + наименование» (ecology-fkko).
 */
export interface EcoCardField {
  id: string
  key: string
  label: string
  hint: string
  type: 'number' | 'site_nvos' | 'file' | 'file_multi' | 'fkko_list'
  required: boolean
  /** Подзаголовок группы в карточке (ecology-card-corrections); пусто — без группы. */
  group: string
}

/** Файл файлового поля строки: `id` — для удаления, `url` — скачивание. */
export interface EcoFieldFile {
  id: string
  name: string
  url: string
}

/**
 * Тело создания/правки позиции справочника (правка — полная). Поля карточки в этой форме не
 * правятся — их ведёт отдельная настройка (ecology-card-fields).
 */
export type EcoCatalogItemInput = Omit<
  EcoCatalogItem,
  | 'id'
  | 'code'
  | 'builtin'
  | 'trainingLinked'
  | 'hiddenFields'
  | 'fieldLabels'
  | 'fields'
  | 'orderFields'
>

/** Строка матрицы «Кому что нужно». */
export interface EcoMatrixRowInput {
  id: string
  categories: EcoCategory[]
  conditionNote: string
}

/** Дата сдачи ежегодной отчётности в календаре. */
export interface EcoCalendarRowInput {
  id: string
  deadlineDay: number
  deadlineMonth: number
}

/**
 * Настройки раздела: пороги «истекает скоро» (дней) — для документов и ежегодной отчётности,
 * ежеквартальной и ежемесячной отчётности — и период ежегодного вопроса «что менялось» (месяцев).
 */
export interface EcoSettings {
  expiringDays: number
  expiringDaysQuarterly: number
  expiringDaysMonthly: number
  changeCheckMonths: number
}

export const ecologyApi = {
  /** Справочник услуг и документов (любая сессия; архивные позиции не отдаются). */
  listCatalog: (signal?: AbortSignal) =>
    api.get<EcoCatalogItem[]>('/ecology/catalog', undefined, signal),
  /** Настройки раздела (любая сессия). */
  getSettings: (signal?: AbortSignal) =>
    api.get<EcoSettings>('/ecology/settings', undefined, signal),
  /** Включённые вопросы опросника в порядке опроса (любая сессия). */
  listQuestions: (signal?: AbortSignal) =>
    api.get<EcoQuestion[]>('/ecology/questions', undefined, signal),
  /**
   * Подсказки справочника ФККО (ecology-fkko, любая сессия): цифры — по началу кода, иначе — по
   * словам наименования; до 20 записей.
   */
  searchFkko: (q: string, signal?: AbortSignal) =>
    api.get<{ items: EcoFkkoEntry[] }>('/ecology/fkko', { q }, signal),
}

/** Отход по ФККО: код цифрами (11 знаков, без пробелов) и наименование (ecology-fkko). */
export interface EcoFkkoEntry {
  code: string
  name: string
}

/** Состояние справочника ФККО: записей и когда загружен (`null` — не загружался). */
export interface EcoFkkoStats {
  total: number
  importedAt: string | null
}

/**
 * Итог загрузки справочника ФККО: + сколько строк групп каталога пропущено и сколько строк вида
 * отхода не вошло (повтор, нет наименования).
 */
export interface EcoFkkoImport extends EcoFkkoStats {
  /** Строк групп каталога (код оканчивается нулём) — в справочник идут только виды отходов. */
  groups: number
  skipped: number
}

// ---------- Экология: площадки и строки «Проектных работ» / «Отчётности» (ecology-sites, §3i) ----------

/** Категория площадки: `''` — не указана, I–IV, `none` — без категории. */
export type EcoSiteCategory = '' | EcoCategory
/** Кто указал категорию площадки. */
export type EcoCategorySource = '' | 'client' | 'centrika'

/** Площадка экологической структуры организации; основная (`isMain`) не удаляется. */
export interface EcoSite {
  id: string
  name: string
  address: string
  nvosCode: string
  category: EcoSiteCategory
  categorySource: EcoCategorySource
  isMain: boolean
  /** Статус опроса площадки (ecology-survey). */
  survey: EcoSiteSurvey
}

/**
 * Опрос площадки в обзоре: `none` — не начат, `draft` — есть черновик, `completed` — пройден
 * (`completedAt`); `changeCheckDue` — пора спросить «что менялось».
 */
export interface EcoSiteSurvey {
  status: 'none' | 'draft' | 'completed'
  completedAt: string
  changeCheckDue: boolean
}

export interface EcoSiteInput {
  name: string
  address: string
  nvosCode: string
  category: EcoSiteCategory
}

/** Цвет светофора строки. */
export type EcoStatusColor = 'red' | 'amber' | 'green' | 'gray'

/** Статус строки — считает сервер. */
export interface EcoItemStatus {
  key: string
  label: string
  color: EcoStatusColor
}

/** Наличие позиции у площадки: есть / нужен, но нет. */
export type EcoPresence = 'present' | 'needed'
/** Кто внёс строку: вручную, по опросу, Центрика по заказу (клиент такую строку не правит). */
export type EcoItemSource = 'client' | 'survey' | 'centrika'

/**
 * Строка площадки — документ (`kind: document`) или отчётность (`report`). Поля позиции справочника
 * (name/kind/сроки) приходят вместе со строкой; `period` — периодичность на площадке. Даты —
 * `YYYY-MM-DD` или пусто; `nextDueOn` считает сервер.
 */
export interface EcoSiteItem {
  id: string
  siteId: string
  catalogItemId: string
  code: string
  name: string
  kind: EcoKind
  /** Подраздел позиции справочника — в какой вкладке показывается строка. */
  section: EcoSection
  catalogPeriod: EcoPeriod
  period: EcoPeriod
  validityYears: number | null
  validityNote: string
  deadlineDay: number | null
  deadlineMonth: number | null
  presence: EcoPresence
  developedOn: string
  validUntil: string
  lastSubmittedOn: string
  nextDueOn: string
  periodOverride: EcoPeriod
  needsUpdateSince: string
  needsUpdateReason: string
  docNumber: string
  note: string
  source: EcoItemSource
  fileName: string
  fileUrl: string
  status: EcoItemStatus
  /** Заказ Центрики в работе по этой позиции и площадке (ecology-order): строка «На согласовании». */
  pendingOrderId: string
  pendingOrderNumber: string
  pendingStage: string
  /** Значения дополнительных полей карточки по ключу поля; поля без значения в карте нет. */
  values: Record<string, string>
  /** Файлы файловых полей по ключу поля, в порядке загрузки; поля без файлов в карте нет. */
  files: Record<string, EcoFieldFile[]>
  /** Отходы полей «Отходы по ФККО» по ключу поля, в порядке ввода; поля без отходов в карте нет. */
  fkko: Record<string, EcoFkkoEntry[]>
}

/** Число строк по цвету светофора. */
export interface EcoStatusCounts {
  red: number
  amber: number
  green: number
  gray: number
}

/** Обзор раздела: площадки, строки со статусами, счётчики документов и отчётности. */
export interface EcoOverview {
  sites: EcoSite[]
  items: EcoSiteItem[]
  documents: EcoStatusCounts
  reports: EcoStatusCounts
  /** Счётчики вкладки «Лабораторный контроль» (ecology-lab-control). */
  lab: EcoStatusCounts
  /** Блок «Сопровождение» (ecology-subscription); null — клиент не на сопровождении. */
  subscription: EcoSubscription | null
  /** Ответственный за экологию: ему тоже уходят письма о сроках (ecology-followup). */
  responsible: EcoResponsible
  /** Обучение по экологической безопасности по данным журнала обучения (ecology-followup). */
  training: EcoTraining
}

/** Строка обучения по экологической безопасности в разделе. */
export interface EcoTraining {
  status: EcoItemStatus
  /** Чего не хватает: обучить ответственного, продлить, обучить второго. Пусто — всё в порядке. */
  hint: string
  people: EcoTrainingPerson[]
}

/** Обученный человек: кто и до какой даты. */
export interface EcoTrainingPerson {
  name: string
  position: string
  dueOn: string
  valid: boolean
}

/** Контакт ответственного за экологию (пустые поля — не указан). */
export interface EcoResponsible {
  name: string
  email: string
  phone: string
}

// --- Абонентское обслуживание (ecology-subscription, §3i) ---

/** Тариф сопровождения: «Защита Эко» / «Прайм Эко». */
export type EcoTariff = 'protection' | 'prime'

/** Работа плана на год; `siteId`/`siteName` пусты — работа по всей организации. */
export interface EcoPlanItem {
  id: string
  /** Год плана, к которому относится работа (планы прошлых лет остаются историей). */
  year: number
  siteId: string
  siteName: string
  title: string
  dueText: string
  done: boolean
}

/** Сопровождение клиента: договор, инженер, ближайший выезд и план работ на год. */
export interface EcoSubscription {
  tariff: EcoTariff
  tariffLabel: string
  contractUntil: string
  engineerName: string
  engineerPhone: string
  engineerEmail: string
  visitsPerYear: number
  nextVisitOn: string
  nextVisitNote: string
  planYear: number
  planDone: number
  planTotal: number
  /** Работы всех годов; клиент видит план `planYear`, инженер листает прошлые. */
  plan: EcoPlanItem[]
  /** Годы, по которым есть работы, от свежего к старому. */
  planYears: number[]
  /** Что входит в сопровождение сверх пакета тарифа (2-ТП (воздух) — всем, вода — по необходимости). */
  includesAirReport: boolean
  includesWaterReport: boolean
  /** Сопровождение завершено (ISO-время); пусто — действует. */
  endedAt: string
}

/** Строка вкладки «Абоненты» панели команды. */
export interface EcoSubscriber {
  clientId: string
  /** Сопровождение завершено (ISO-время); пусто — действует. История остаётся в списке. */
  endedAt: string
  clientName: string
  tariff: EcoTariff
  tariffLabel: string
  contractUntil: string
  engineerName: string
  nextVisitOn: string
  planDone: number
  planTotal: number
}

/** Карточка сопровождения из формы команды (даты — `YYYY-MM-DD` или пусто). */
export interface EcoSubscriptionInput {
  tariff: EcoTariff
  contractUntil: string
  engineerName: string
  engineerPhone: string
  engineerEmail: string
  visitsPerYear: number
  nextVisitOn: string
  nextVisitNote: string
  planYear: number
  includesAirReport: boolean
  includesWaterReport: boolean
}

/** Работа плана из формы «План работ» (порядок в списке — порядок показа у клиента). */
export interface EcoPlanItemInput {
  siteId: string
  title: string
  dueText: string
  done: boolean
}

/** Данные строки из формы (даты `YYYY-MM-DD` или пусто; `validUntil` — срок действия вручную). */
export interface EcoItemDataInput {
  presence: EcoPresence
  developedOn: string
  validUntil: string
  lastSubmittedOn: string
  periodOverride: EcoPeriod
  docNumber: string
  note: string
  /**
   * Значения дополнительных полей по ключу (ecology-card-fields): не передано — не меняются,
   * пустая строка — очистить.
   */
  values?: Record<string, string>
  /**
   * Отходы полей «Отходы по ФККО» по ключу (ecology-fkko): переданный список заменяет прежний
   * целиком (пустой — очистить), не переданный ключ не меняется.
   */
  fkko?: Record<string, EcoFkkoEntry[]>
}

/** «Внести имеющийся документ» / «Отметить сданный отчёт» на выбранных площадках. */
export interface EcoItemsCreateInput extends EcoItemDataInput {
  catalogItemId: string
  siteIds: string[]
  /**
   * Значения дополнительных полей по площадке (ecology-sources-by-site): у площадки со своими
   * значениями они заменяют общие `values`; площадка — из `siteIds`.
   */
  siteValues?: Record<string, Record<string, string>>
}

/**
 * Раздел «Экология» в скоупе организации: клиент — `ecologyClientApi` (скоуп из сессии), команда —
 * `teamEcologyApi(cid)`. Изменяющие запросы возвращают свежий обзор.
 */
export interface EcoScopeApi {
  overview: (signal?: AbortSignal) => Promise<EcoOverview>
  createSite: (input: EcoSiteInput) => Promise<EcoOverview>
  updateSite: (id: string, input: EcoSiteInput) => Promise<EcoOverview>
  deleteSite: (id: string) => Promise<EcoOverview>
  createItems: (input: EcoItemsCreateInput) => Promise<EcoOverview>
  updateItem: (id: string, input: EcoItemDataInput) => Promise<EcoOverview>
  dismissItem: (id: string) => Promise<EcoOverview>
  uploadItemFile: (id: string, file: File) => Promise<EcoOverview>
  deleteItemFile: (id: string) => Promise<EcoOverview>
  /** Файлы файловых полей карточки строки (ecology-card-file-fields). */
  uploadFieldFile: (id: string, key: string, file: File) => Promise<EcoOverview>
  deleteFieldFile: (id: string, key: string, fileId: string) => Promise<EcoOverview>
  getSurvey: (siteId: string, signal?: AbortSignal) => Promise<EcoSurvey>
  saveSurvey: (siteId: string, answers: EcoAnswers) => Promise<EcoSurvey>
  completeSurvey: (siteId: string, answers: EcoAnswers) => Promise<EcoSurveyComplete>
  listChangeChecks: (signal?: AbortSignal) => Promise<EcoChangeCheck[]>
  createChangeCheck: (input: EcoChangeCheckInput) => Promise<EcoChangeCheckResult>
  /** Ответственный за экологию — кому ещё писать о сроках (ecology-followup). */
  saveResponsible: (input: EcoResponsible) => Promise<EcoOverview>
}

// --- Опрос по площадке (ecology-survey, §3i) ---

/** Тип ответа на вопрос опросника. */
export type EcoAnswerType = 'yes_no' | 'single' | 'multi' | 'text' | 'number'
/** Тип уточнения при ответе «Да». */
export type EcoClarificationType = 'date' | 'number' | 'text' | 'single'

/** Уточнение при ответе «Да»; `key` пустой у нового уточнения (ключ выдаёт сервер). */
export interface EcoClarification {
  key: string
  type: EcoClarificationType
  label: string
  options: string[]
}

/**
 * Вопрос опросника. `section` — 1 «Учёт и изменения», 2 «Деятельность», 3 «Документы», 4 «Отходы
 * и отчётность»; `builtin` — вопрос анкеты: не удаляется, тип, варианты и уточнения (кроме подписей)
 * не меняются.
 */
export interface EcoQuestion {
  id: string
  code: string
  builtin: boolean
  section: number
  text: string
  hint: string
  answerType: EcoAnswerType
  options: string[]
  clarifications: EcoClarification[]
  showIfQuestionId: string | null
  showIfAnswer: string
  enabled: boolean
}

export type EcoQuestionInput = Omit<EcoQuestion, 'id' | 'code' | 'builtin'>

/** Ответ: `value` (Да/Нет, вариант, текст, число), `values` (несколько), уточнения при «Да». */
export interface EcoAnswer {
  value: string
  values: string[]
  clarifications: Record<string, string>
}

/** Ответы опроса по id вопроса. */
export type EcoAnswers = Record<string, EcoAnswer>

export type EcoRuleAction = 'need' | 'have' | 'update' | 'note'
export type EcoRuleCondition = '' | 'emissions' | 'categories' | 'answer'

/** Правило «ответ → документ». */
export interface EcoRule {
  id: string
  questionId: string
  answer: string
  catalogItemId: string | null
  action: EcoRuleAction
  explanation: string
  condition: EcoRuleCondition
  conditionCategories: EcoCategory[]
  conditionQuestionId: string | null
  conditionKey: string
  conditionNegate: boolean
  conditionValue: string
}

export type EcoRuleInput = Omit<EcoRule, 'id'>

/** Позиция в результате опроса (снимок названия). */
export interface EcoSurveyResultItem {
  catalogItemId: string
  name: string
  kind: EcoKind
  explanations: string[]
  date: string
  /** Строке нужна дата (документ со сроком действия, отчётность со сроком сдачи). */
  needsDate: boolean
}

/** Результат последнего завершения опроса; `category` — категория, по которой считались условия. */
export interface EcoSurveyResult {
  need: EcoSurveyResultItem[]
  have: EcoSurveyResultItem[]
  update: EcoSurveyResultItem[]
  notes: string[]
  category: EcoSiteCategory
  categoryFromSurvey: boolean
}

export interface EcoSurvey {
  siteId: string
  answers: EcoAnswers
  result: EcoSurveyResult | null
  completedAt: string
  updatedAt: string
  changeCheckedAt: string
}

export interface EcoSurveyComplete {
  survey: EcoSurvey
  overview: EcoOverview
}

/** «Что менялось»: пустой `changes` — «ничего не менялось». */
export interface EcoChangeCheckInput {
  siteIds: string[]
  changes: EcoUpdateTrigger[]
  note: string
}

export interface EcoChangeCheckResult {
  markedCount: number
  overview: EcoOverview
}

// --- Заказ услуги по экологии (ecology-order, §3i) ---

/** Площадка заказа и результат по ней; `id` — строка заказа, `siteId` пуст, если площадку удалили. */
export interface EcoOrderSite {
  id: string
  siteId: string
  name: string
  address: string
  nvosCode: string
  category: EcoSiteCategory
  resultDate: string
  resultValidUntil: string
  resultFileName: string
  resultFileUrl: string
  /** Поля заявки черновика по ключу (ecology-order-fields): числа и выбор. */
  values: Record<string, EcoOrderFieldValue>
  /** Отходы полей «список ФККО» и файлы файловых полей черновика по ключу. */
  fkko: Record<string, EcoFkkoEntry[]>
  files: Record<string, EcoFieldFile[]>
  /** «Фотография» заполненных полей заявки поданного заказа. */
  snapshotValues: EcoOrderSnapshotValue[]
}

/** Вопрос и ответ из «фотографии» поданного заказа. */
export interface EcoOrderAnswer {
  question: string
  answer: string
}

/**
 * Заказ услуги по экологии: черновик мастера (услуга, площадки, ответы, комментарий) и «фотография»
 * поданного заказа. `item` — позиция справочника заказа (в т.ч. архивная).
 */
export interface EcoOrder {
  orderId: string
  canEdit: boolean
  catalogItemId: string
  item: EcoCatalogItem | null
  sites: EcoOrderSite[]
  answers: EcoAnswers
  comment: string
  snapshotName: string
  snapshotNote: string
  snapshotAnswers: EcoOrderAnswer[]
  /** У всех площадок заказа внесена дата результата (кнопка «Завершить» у команды). */
  resultsReady: boolean
}

/** Создание черновика заказа из раздела (услуга и площадки необязательны). */
export interface EcoOrderCreateInput {
  catalogItemId?: string
  siteIds?: string[]
}

/** Правка черновика мастера; не переданное поле не меняется. */
export interface EcoOrderPatchInput {
  catalogItemId?: string
  siteIds?: string[]
  answers?: EcoAnswers
  comment?: string
  /**
   * Поля заявки по площадкам: id площадки организации → ключ поля → значение (число — строкой,
   * пустая строка очищает; выбор — массив вариантов). Площадка должна быть в заказе.
   */
  siteValues?: Record<string, Record<string, string | string[] | EcoFkkoEntry[]>>
}

/** Результат команды по площадке заказа. */
export interface EcoOrderResultInput {
  id: string
  resultDate: string
  validUntil: string
}

/** Запись истории «что менялось». */
export interface EcoChangeCheck {
  id: string
  siteNames: string[]
  changes: EcoUpdateTrigger[]
  note: string
  markedCount: number
  accountName: string
  createdAt: string
}

function makeEcoScope(base: string): EcoScopeApi {
  return {
    overview: (signal) => api.get<EcoOverview>(`${base}/overview`, undefined, signal),
    createSite: (input) => api.post<EcoOverview>(`${base}/sites`, input),
    updateSite: (id, input) => api.patch<EcoOverview>(`${base}/sites/${id}`, input),
    deleteSite: (id) => api.del<EcoOverview>(`${base}/sites/${id}`),
    createItems: (input) => api.post<EcoOverview>(`${base}/items`, input),
    updateItem: (id, input) => api.patch<EcoOverview>(`${base}/items/${id}`, input),
    dismissItem: (id) => api.post<EcoOverview>(`${base}/items/${id}/dismiss`, {}),
    uploadItemFile: (id, file) => {
      const fd = new FormData()
      fd.append('file', file)
      return postMultipart<EcoOverview>(`${base}/items/${id}/file`, fd)
    },
    deleteItemFile: (id) => api.del<EcoOverview>(`${base}/items/${id}/file`),
    uploadFieldFile: (id, key, file) => {
      const fd = new FormData()
      fd.append('file', file)
      return postMultipart<EcoOverview>(`${base}/items/${id}/fields/${key}/files`, fd)
    },
    deleteFieldFile: (id, key, fileId) =>
      api.del<EcoOverview>(`${base}/items/${id}/fields/${key}/files/${fileId}`),
    getSurvey: (siteId, signal) =>
      api.get<EcoSurvey>(`${base}/sites/${siteId}/survey`, undefined, signal),
    saveSurvey: (siteId, answers) =>
      api.put<EcoSurvey>(`${base}/sites/${siteId}/survey`, { answers }),
    completeSurvey: (siteId, answers) =>
      api.post<EcoSurveyComplete>(`${base}/sites/${siteId}/survey/complete`, { answers }),
    listChangeChecks: (signal) =>
      api.get<EcoChangeCheck[]>(`${base}/change-checks`, undefined, signal),
    createChangeCheck: (input) => api.post<EcoChangeCheckResult>(`${base}/change-checks`, input),
    saveResponsible: (input) => api.put<EcoOverview>(`${base}/responsible`, input),
  }
}

/** Раздел «Экология» клиента-компании (скоуп из сессии). */
export const ecologyClientApi: EcoScopeApi = makeEcoScope('/ecology')

/**
 * Заказ услуги по экологии у клиента (ecology-order): черновик создаётся из раздела, мастер живёт на
 * общем пути заказа `/orders/{id}/ecology`.
 */
export const ecologyOrdersApi = {
  create: (input: EcoOrderCreateInput) => api.post<OrderDetail>('/ecology/orders', input),
  get: (orderId: string, signal?: AbortSignal) =>
    api.get<EcoOrder>(`/orders/${orderId}/ecology`, undefined, signal),
  patch: (orderId: string, input: EcoOrderPatchInput) =>
    api.patch<EcoOrder>(`/orders/${orderId}/ecology`, input),
  /** Файл файлового поля заявки площадки черновика (ecology-order-fields-files-fkko). */
  uploadFieldFile: (orderId: string, siteId: string, key: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return postMultipart<EcoOrder>(
      `/orders/${orderId}/ecology/sites/${siteId}/fields/${key}/files`,
      fd,
    )
  },
  deleteFieldFile: (orderId: string, siteId: string, key: string, fileId: string) =>
    api.del<EcoOrder>(`/orders/${orderId}/ecology/sites/${siteId}/fields/${key}/files/${fileId}`),
}

/** Заказ по экологии у команды: карточка, результаты по площадкам и файлы результата. */
export const teamEcologyOrdersApi = {
  get: (orderId: string, signal?: AbortSignal) =>
    api.get<EcoOrder>(`/team/orders/${orderId}/ecology`, undefined, signal),
  putResults: (orderId: string, items: EcoOrderResultInput[]) =>
    api.put<EcoOrder>(`/team/orders/${orderId}/ecology/results`, { items }),
  uploadFile: (orderId: string, siteRowId: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return postMultipart<EcoOrder>(`/team/orders/${orderId}/ecology/sites/${siteRowId}/file`, fd)
  },
  deleteFile: (orderId: string, siteRowId: string) =>
    api.del<EcoOrder>(`/team/orders/${orderId}/ecology/sites/${siteRowId}/file`),
}

/** Раздел «Экология» клиента для команды Центрики (по `cid`). */
export function teamEcologyApi(cid: string): EcoScopeApi {
  return makeEcoScope(`/team/clients/${cid}/ecology`)
}

/**
 * Абонентское обслуживание у команды (ecology-subscription): список абонентов и карточка
 * сопровождения клиента с планом работ. Ведёт любой сотрудник Центрики.
 */
export const teamEcologySubscriptionsApi = {
  list: (signal?: AbortSignal) =>
    api.get<EcoSubscriber[]>('/team/ecology/subscriptions', undefined, signal),
  get: (cid: string, signal?: AbortSignal) =>
    api
      .get<{ subscription: EcoSubscription | null }>(
        `/team/clients/${cid}/ecology/subscription`,
        undefined,
        signal,
      )
      .then((r) => r.subscription),
  save: (cid: string, input: EcoSubscriptionInput) =>
    api
      .put<{ subscription: EcoSubscription | null }>(
        `/team/clients/${cid}/ecology/subscription`,
        input,
      )
      .then((r) => r.subscription),
  savePlan: (cid: string, year: number, items: EcoPlanItemInput[]) =>
    api
      .put<{ subscription: EcoSubscription | null }>(
        `/team/clients/${cid}/ecology/subscription/plan`,
        { year, items },
      )
      .then((r) => r.subscription),
  /** Перенести невыполненные работы года на следующий и сделать его текущим. */
  carryOverPlan: (cid: string, year: number) =>
    api
      .post<{ subscription: EcoSubscription | null }>(
        `/team/clients/${cid}/ecology/subscription/plan/carry-over`,
        { year },
      )
      .then((r) => r.subscription),
  remove: (cid: string) =>
    api.del<{ subscription: EcoSubscription | null }>(`/team/clients/${cid}/ecology/subscription`),
}

export const positionsApi = {
  /** Список справочника должностей для combobox (опц. серверный поиск `q` по name_norm). */
  list: (q?: string, signal?: AbortSignal) =>
    api.get<Position[]>('/positions', q ? { q } : undefined, signal),
}

/** Создание должности справочника (POST /api/admin/positions, только админ). */
export interface CreatePositionInput {
  name: string
}

/** Переименование должности справочника (PATCH /api/admin/positions/{id}). */
export type UpdatePositionInput = CreatePositionInput

/**
 * Итог пакетного добавления должностей (POST /api/admin/positions/batch, только админ;
 * import-missing-positions-to-dictionary): `created` — добавленные (наименования очищены),
 * `existing` — уже бывшие в справочнике, в том числе при другом написании.
 */
export interface CreatePositionsBatchResult {
  created: Position[]
  existing: Position[]
}

export interface CreateClientInput {
  name: string
  inn?: string
  kpp?: string
  ogrn?: string
  okpo?: string
  oktmo?: string
  okved?: string
  legalAddress?: string
  email?: string
  phone?: string
  directorName?: string
  directorPosition?: string
  industryProfile?: string
  hasOpo?: boolean
  /**
   * Вход в кабинет — один из двух вариантов (create-client-with-existing-login):
   * новый общий вход организации (`loginEmail` + `password`) либо существующий вход
   * клиента-компании (`existingLoginEmail`) — организация создаётся без своего входа и сразу
   * закрепляется за ним.
   */
  loginEmail?: string
  password?: string
  existingLoginEmail?: string
}

/** Ответ POST /team/clients: созданный клиент и вход, через который в него заходят. */
export interface CreatedClient extends ClientSummary {
  loginEmail: string
  /** Организация создана с закреплённым существующим входом (своего входа у неё нет). */
  existingLogin?: boolean
}

export interface CreateEmployeeInput {
  email: string
  fullName: string
  password: string
  role: TeamRole
  phone?: string
}

/** Правка учётной записи сотрудника Центрики суперадминистратором (PATCH /team/accounts/{id}). */
export interface UpdateAccountInput {
  fullName?: string
  phone?: string
  role?: TeamRole
}

/** Фильтры очереди команды (§3c): `client` — uuid, `q` — поиск по имени клиента. */
export interface OrdersQuery {
  status?: string
  service?: string
  /** Текущий этап «Ход работ» — только вместе с `service` (orders-list-filters). */
  stage?: string
  client?: string
  q?: string
  /** Ответственный от Центрики (team-access): uuid сотрудника или `none` — без ответственного. */
  responsible?: string
  /**
   * Поле серверной сортировки очереди (centrika-docs!77): `created` — дата создания,
   * `submitted` — дата отправки на проверку. Сортировка+пагинация выполняются на сервере
   * по всему отфильтрованному набору. Дефолт (не передан) — `submitted`.
   */
  sort?: 'created' | 'submitted'
  /** Направление сортировки: `asc`|`desc`. Дефолт (не передан) — `desc`. */
  dir?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

/** Внутренний комментарий команды к заказу (order-internal-notes). */
export interface OrderInternalNote {
  id: string
  /** Пусто — учётная запись автора удалена. */
  authorName: string
  body: string
  createdAt: string
}

/** Фильтры клиентского списка «Мои заказы» (§3c). */
export interface OrdersListQuery {
  /** Статус или несколько через запятую (у клиента «Выполняется» — `review,in_progress`). */
  status?: string
  service?: string
  /** Текущий этап «Ход работ» — только вместе с `service` (orders-list-filters). */
  stage?: string
  limit?: number
  offset?: number
}

/**
 * Строка списка аккаунтов команды (GET /api/team/accounts): User с обязательным `disabled` и
 * закреплениями (team-visibility) — `managedClients`: клиенты, у которых сотрудник менеджер;
 * `openOrders`: незавершённые заказы, где он ответственный; `cancelledOrders`: отменённые заказы, где
 * он ответственный (их можно восстановить, при передаче дел они переходят преемнику). У только что
 * созданной записи закреплений нет, поэтому поля необязательны.
 */
export type TeamAccount = User & {
  disabled: boolean
  managedClients?: number
  /** Клиенты, у которых сотрудник — менеджер по сопровождению (client-support-manager). */
  supportedClients?: number
  openOrders?: number
  cancelledOrders?: number
}

// --- Блок 3: входные тела (формы — дословно по §3c) ---

/** Создание заказа клиентом: `{service, volumeSummary?}` → OrderDetail (status `draft`). */
export interface CreateOrderInput {
  service: Service
  volumeSummary?: string
}

/** Правка заказа командой (PATCH /api/team/orders/{id}). */
export interface TeamUpdateOrderInput {
  volumeSummary?: string
  snapshotCommon?: Record<string, unknown>
  responsibleAccountId?: string
}

/** Команда: смена статуса данных (`in_progress`|`done`). */
export type TeamOrderStatus = 'in_progress' | 'done'

/** Команда: сделать этап текущим + ориент.дата + (опц.) привязать файл к этапу. */
export interface AdvanceStageInput {
  stageKey: string
  targetDate?: string
  fileId?: string
}

/**
 * Загрузка файла командой. ВНИМАНИЕ: имена multipart-полей берутся из УЖЕ существующего
 * хендлера `POST /api/team/files` (`file`, `order_id`, `stage_id`, `doc_type` — snake_case),
 * а не из camelCase-прозы §3c — иначе бэкенд молча проигнорировал бы привязки (FormValue → "").
 */
export interface UploadFileInput {
  file: File
  /**
   * Без привязки к заказу — только для исторического (вне заказа) ввода
   * (p2-manual-historical, design D6/D7): файл получит `owner_client_id = NULL`, тот же
   * класс поведения, что уже фактически есть у `training_records.documentFileId`.
   */
  orderId?: string
  stageId?: string
  documentTypeCode: string
  /** Привязка файла к строке ведомости обучения (§3e: `training_line_id`). */
  trainingLineId?: string
  /** Привязка документа/отчёта к строке рабочего места СОУТ (§7.5: `sout_line_id`). */
  soutLineId?: string
}

function buildFileForm(input: UploadFileInput): FormData {
  const fd = new FormData()
  fd.append('file', input.file)
  if (input.orderId) fd.append('order_id', input.orderId)
  if (input.stageId) fd.append('stage_id', input.stageId)
  if (input.trainingLineId) fd.append('training_line_id', input.trainingLineId)
  if (input.soutLineId) fd.append('sout_line_id', input.soutLineId)
  fd.append('doc_type', input.documentTypeCode)
  return fd
}

/** Фильтры реестра документов клиента (§3c): `type` — код типа. */
export interface DocumentsQuery {
  type?: string
  limit?: number
  offset?: number
}

/** Фильтры реестра документов команды (§3c): `client` — uuid, `type` — код типа. */
export interface TeamDocumentsQuery {
  client?: string
  type?: string
  limit?: number
  offset?: number
}

/** Фильтры журнала действий (§3c): `order`/`client` — uuid. */
export interface AuditQuery {
  order?: string
  client?: string
  limit?: number
  offset?: number
}

/**
 * Клиентские заказы (свой скоуп, без client_id; §3c). Пути `/api/orders...`.
 * Список — карточки; create/get/update/submit возвращают полную OrderDetail.
 */
export const ordersApi = {
  list: (query?: OrdersListQuery, signal?: AbortSignal) =>
    api.get<ListResponse<OrderCard>>('/orders', { ...query }, signal),
  create: (input: CreateOrderInput) => api.post<OrderDetail>('/orders', input),
  get: (id: string, signal?: AbortSignal) =>
    api.get<OrderDetail>(`/orders/${id}`, undefined, signal),
  submit: (id: string) => api.post<OrderDetail>(`/orders/${id}/submit`),
  /**
   * POST /api/orders/{id}/request — «Отправить заявку» (обучение, training-order-filling):
   * черновик → «На заполнении», менеджеру — договор и счёт. По абонентскому обслуживанию — 409.
   */
  request: (id: string) => api.post<OrderDetail>(`/orders/${id}/request`),
  /**
   * PATCH /api/orders/{id}/requisites — правка реквизитов на шаге 1 заказа (design D1/D3,
   * §3.4, §8.2): применяется сразу и к профилю компании, и к снимку ТЕКУЩЕГО заказа, без
   * модерации; доступно только пока заказ редактируем (draft|changes_requested — иначе 409).
   */
  patchRequisites: (id: string, input: UpdateCompanyProfileInput) =>
    api.patch<OrderDetail>(`/orders/${id}/requisites`, input),
  remove: (id: string) => api.del<void>(`/orders/${id}`),
  /**
   * Отмена и восстановление заказа клиентом (order-cancel-restore): отменить — поданный и не
   * завершённый (причина необязательна, её видит Центрика); восстановить — только заказ, отменённый
   * самим клиентом. Иначе 409.
   */
  cancel: (id: string, reason: string) => api.post<OrderDetail>(`/orders/${id}/cancel`, { reason }),
  restore: (id: string) => api.post<OrderDetail>(`/orders/${id}/restore`),
  documents: (id: string, signal?: AbortSignal) =>
    api.get<DocumentDTO[]>(`/orders/${id}/documents`, undefined, signal),
}

/** Глобальный реестр документов клиента (GET /api/documents, owner-scoped бэкендом). */
export const documentsApi = {
  list: (query?: DocumentsQuery, signal?: AbortSignal) =>
    api.get<ListResponse<DocumentDTO>>('/documents', { ...query }, signal),
}

/**
 * Клиентская сторона СОУТ (§3d). Анкета/строки правятся только при `draft|changes_requested`
 * (иначе 409). Подача — общий `ordersApi.submit` (СОУТ-валидация+снимок на сервере).
 * Скоуп — из сессии (client_id не передаётся); чужой заказ → 404.
 */
/**
 * Загрузка анкеты заказа старого образца из Excel (order-import-legacy-forms): «Анкета и форма
 * СОУТ», «Анкета и форма (РИСКИ)», «Список обучающихся». Предпросмотр и загрузка принимают ОДИН И
 * ТОТ ЖЕ файл — сервер строит по нему один и тот же план, что показан, то и записывается.
 */
export type OrderImportRowStatus = 'ok' | 'warning' | 'error'

/** Строка файла в предпросмотре: попадёт в заказ (`ok`), попадёт с замечаниями (`warning`), пропущена (`error`). */
export interface OrderImportRow {
  rowNumber: number
  status: OrderImportRowStatus
  /** Чем строка станет в заказе: «Склад · Кладовщик», ФИО слушателя. */
  title: string
  /** Ключевые значения строки одной фразой. */
  details: string
  /** Почему строка пропущена или что перенесено не так, как в файле. */
  messages: string[]
}

export interface OrderImportPreview {
  fileName: string
  rows: OrderImportRow[]
  /** Что будет сделано помимо строк (анкета заказа) и замечания к файлу. */
  notes: string[]
  /** Важное, что стоит проверить до загрузки (ИНН другой организации, повторная загрузка). */
  warnings: string[]
  toAdd: number
  skipped: number
  /** Обучение: сколько сотрудников будет заведено в структуре. */
  newEmployees: number
  /**
   * Обучение: из них без подразделения в файле — для них подразделение выбирается при загрузке
   * (training-import-template).
   */
  newEmployeesWithoutDepartment: number
  /** Обучение: строки, где сотрудник загрузится, а программу нужно выбрать в кабинете. */
  withoutProgram: number
  /** Загрузка что-то изменит в заказе (строки или анкета). */
  hasChanges: boolean
}

export interface OrderImportResult {
  added: number
  skipped: number
  /** Обучение: заведено сотрудников в структуре. */
  newEmployees: number
  /** Обучение: строк, где программу нужно выбрать в кабинете. */
  withoutProgram: number
}

/** Тело загрузки анкеты: файл и (для обучения) подразделение новых сотрудников. */
function orderImportForm(file: File, departmentName?: string): FormData {
  const fd = new FormData()
  fd.append('file', file)
  if (departmentName !== undefined) fd.append('departmentName', departmentName)
  return fd
}

export const soutApi = {
  getAnketa: (orderId: string, signal?: AbortSignal) =>
    api.get<SoutAnketa>(`/orders/${orderId}/sout`, undefined, signal),
  patchAnketa: (orderId: string, input: SoutAnketaPatch) =>
    api.patch<SoutAnketa>(`/orders/${orderId}/sout`, input),
  addLine: (orderId: string, input: AddSoutLineInput) =>
    api.post<SoutLine>(`/orders/${orderId}/sout/lines`, input),
  /**
   * «Добавить вручную» (§14, D7, Волна 7) — атомарное создание места (+сотрудника, если введён)
   * и строки заказа. См. `AddManualSoutLineInput`.
   */
  addManualLine: (orderId: string, input: AddManualSoutLineInput) =>
    api.post<SoutLine>(`/orders/${orderId}/sout/lines/manual`, input),
  /**
   * Рабочие места структуры с теми же подразделением и должностью (centrika-docs!101) — данные
   * для диалога подтверждения перед созданием ещё одного такого же места. Пустой список —
   * сохранение идёт без подтверждения. Совпадение авторитетно перепроверяется при создании.
   */
  workplaceMatches: (
    orderId: string,
    query: { departmentName: string; position?: string; positionId?: string },
    signal?: AbortSignal,
  ) =>
    api.get<{ items: SoutWorkplaceMatch[] }>(
      `/orders/${orderId}/sout/workplace-matches`,
      { ...query },
      signal,
    ),
  /**
   * Массовое добавление из модала «Из структуры компании» (§2, D2; Волна 5 D2.1 —
   * вакансии). Частичный неуспех — НЕ HTTP-ошибка (200 с непустым `rejected`), диалог
   * остаётся открытым и показывает, кого/что не добавили (см. StructureModal).
   */
  bulkAddFromStructure: (orderId: string, input: SoutBulkAddFromStructureInput) =>
    api.post<SoutBulkAddResult>(`/orders/${orderId}/sout/lines/from-persons`, input),
  updateLine: (orderId: string, lineId: string, input: UpdateSoutLineInput) =>
    api.patch<SoutLine>(`/orders/${orderId}/sout/lines/${lineId}`, input),
  deleteLine: (orderId: string, lineId: string) =>
    api.del<void>(`/orders/${orderId}/sout/lines/${lineId}`),
  carryover: (orderId: string, workplaceId: string, signal?: AbortSignal) =>
    api.get<SoutCarryoverPreview>(`/orders/${orderId}/sout/carryover`, { workplaceId }, signal),
  /** Предпросмотр загрузки «Анкеты и формы СОУТ» (order-import-legacy-forms). */
  importPreview: (orderId: string, file: File) =>
    postMultipart<OrderImportPreview>(
      `/orders/${orderId}/sout/import/preview`,
      orderImportForm(file),
    ),
  /** Загрузка «Анкеты и формы СОУТ» в заказ — тот же файл, что в предпросмотре. */
  importFile: (orderId: string, file: File) =>
    postMultipart<OrderImportResult>(`/orders/${orderId}/sout/import`, orderImportForm(file)),
  dashboard: (signal?: AbortSignal) => api.get<SoutDashboard>('/sout/dashboard', undefined, signal),
  /**
   * Результат СОУТ своего заказа (F2-15, замечание менеджера): done-gated (409 до статуса
   * «Готово», см. `dataStatus`) — вызывать только когда `order.dataStatus === 'done'`.
   */
  results: (orderId: string, signal?: AbortSignal) =>
    api.get<SoutResult[]>(`/orders/${orderId}/sout/results`, undefined, signal),
  /**
   * Историческая (вне заказа) запись СОУТ по рабочему месту (p2-manual-historical, §11.2).
   * Роль-агностично: доступно и клиенту (свой скоуп), и команде (владелец резолвится по
   * `workplaceId`). Доступно «в любой момент», независимо от заказов/статусов.
   */
  historical: {
    list: (workplaceId: string, signal?: AbortSignal) =>
      api.get<SoutHistoricalResult[]>('/sout/historical-results', { workplaceId }, signal),
    create: (input: CreateSoutHistoricalResultInput) =>
      api.post<SoutHistoricalResult>('/sout/historical-results', input),
    update: (id: string, input: UpdateSoutHistoricalResultInput) =>
      api.patch<SoutHistoricalResult>(`/sout/historical-results/${id}`, input),
    remove: (id: string) => api.del<void>(`/sout/historical-results/${id}`),
  },
}

/**
 * Клиентская сторона профрисков (§3f). Анкета/должности правятся только при `draft|changes_requested`
 * (иначе 409). Подача — общий `ordersApi.submit` (профриск-валидация+снимок на сервере).
 * Скоуп — из сессии (client_id не передаётся); чужой заказ → 404. `cardsCount` клиенту не отдаётся.
 */
export const profriskApi = {
  getAnketa: (orderId: string, signal?: AbortSignal) =>
    api.get<ProfriskAnketa>(`/orders/${orderId}/profrisk`, undefined, signal),
  patchAnketa: (orderId: string, input: ProfriskAnketaPatch) =>
    api.patch<ProfriskAnketa>(`/orders/${orderId}/profrisk`, input),
  addPosition: (orderId: string, input: AddProfriskPositionInput) =>
    api.post<ProfriskPosition>(`/orders/${orderId}/profrisk/positions`, input),
  /**
   * Массовое добавление должностей из структуры компании (centrika-docs!26). Запрос
   * АТОМАРЕН: в заказ попадают либо все отмеченные должности, либо ни одной. Пустой
   * массив → 422; заказ не в «Черновик»/«Требуются правки» → 409. Штатные единицы не
   * подставляются — созданные должности приходят с `headcount: 0` и `valid: false`.
   */
  addPositionsBatch: (orderId: string, input: AddProfriskPositionsBatchInput) =>
    api.post<AddProfriskPositionsBatchResult>(`/orders/${orderId}/profrisk/positions/batch`, input),
  updatePosition: (orderId: string, posId: string, input: UpdateProfriskPositionInput) =>
    api.patch<ProfriskPosition>(`/orders/${orderId}/profrisk/positions/${posId}`, input),
  deletePosition: (orderId: string, posId: string) =>
    api.del<void>(`/orders/${orderId}/profrisk/positions/${posId}`),
  /**
   * Загрузить ОДИН файл списка инструкций по ОТ (centrika-docs!25, способ «Загрузить
   * файлы»). По одному файлу за запрос — множественный выбор раскладывается в
   * независимые запросы, чтобы ошибка по одному файлу не рушила остальные и не теряла
   * введённые данные шага. Замена файла = загрузка нового + удаление старого.
   * Форматы: pdf/doc/docx/xls/xlsx/jpg/jpeg/png, до 15 МБ; иначе 422 по полю `file`.
   */
  uploadInstructionFile: (orderId: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return postMultipart<ProfriskInstructionFile>(
      `/orders/${orderId}/profrisk/instruction-files`,
      fd,
    )
  },
  /** Удалить загруженный файл списка инструкций (204). */
  deleteInstructionFile: (orderId: string, fileId: string) =>
    api.del<void>(`/orders/${orderId}/profrisk/instruction-files/${fileId}`),
  /** Предпросмотр загрузки «Анкеты и формы (РИСКИ)» (order-import-legacy-forms). */
  importPreview: (orderId: string, file: File) =>
    postMultipart<OrderImportPreview>(
      `/orders/${orderId}/profrisk/import/preview`,
      orderImportForm(file),
    ),
  /** Загрузка «Анкеты и формы (РИСКИ)» в заказ — тот же файл, что в предпросмотре. */
  importFile: (orderId: string, file: File) =>
    postMultipart<OrderImportResult>(`/orders/${orderId}/profrisk/import`, orderImportForm(file)),
  dashboard: (signal?: AbortSignal) =>
    api.get<ProfriskDashboard>('/profrisk/dashboard', undefined, signal),
  /**
   * Результат профрисков своего заказа (F2-15, замечание менеджера): done-gated (409 до
   * статуса «Готово») — вызывать только когда `order.dataStatus === 'done'`.
   */
  results: (orderId: string, signal?: AbortSignal) =>
    api.get<ProfriskResult[]>(`/orders/${orderId}/profrisk/results`, undefined, signal),
  /**
   * Историческая (вне заказа) запись профрисков по рабочему месту (p2-manual-historical,
   * §11.3, Б7). Роль-агностично; доступно «в любой момент», независимо от заказов.
   */
  historical: {
    list: (workplaceId: string, signal?: AbortSignal) =>
      api.get<ProfriskHistoricalResult[]>('/profrisk/historical-results', { workplaceId }, signal),
    create: (input: CreateProfriskHistoricalResultInput) =>
      api.post<ProfriskHistoricalResult>('/profrisk/historical-results', input),
    update: (id: string, input: UpdateProfriskHistoricalResultInput) =>
      api.patch<ProfriskHistoricalResult>(`/profrisk/historical-results/${id}`, input),
    remove: (id: string) => api.del<void>(`/profrisk/historical-results/${id}`),
  },
}

/**
 * Клиентская/общая сторона обучения (§3e, переработана p2-service-training). Каталог
 * программ — read-only для всех (правит админ); 3-шаговый мастер правится в своём скоупе
 * (client_id из сессии не передаётся). PDF-согласия живут в карточке сотрудника —
 * `CompanyScopeApi.uploadConsent/deleteConsent`.
 */
export const trainingApi = {
  /** Каталог программ для селектов (доступен всем по сессии). */
  listPrograms: (signal?: AbortSignal) =>
    api.get<Program[]>('/training/programs', undefined, signal),
  /** Справочник направлений для селектов/фильтров (доступен всем по сессии, centrika-docs!55). */
  listDirections: (signal?: AbortSignal) =>
    api.get<TrainingDirectionItem[]>('/training/directions', undefined, signal),
  /** Пакеты программ: клиенту — доступные для выбора, без цены; команде — все. */
  listPackages: (signal?: AbortSignal) =>
    api.get<ProgramPackage[]>('/training/packages', undefined, signal),
  /** 3-шаговый мастер: организация (шаг 1), состав (шаг 2), выбранные+согласия (шаг 3). */
  getWizard: (orderId: string, signal?: AbortSignal) =>
    api.get<TrainingWizard>(`/orders/${orderId}/training`, undefined, signal),
  /** Автосейв состава ОДНОЙ программы (шаг 2, D1 — клиент формирует состав). */
  patchComposition: (orderId: string, input: TrainingCompositionPatch) =>
    api.patch<TrainingWizard>(`/orders/${orderId}/training`, input),
  /** Сотрудники для модалки выбора под КОНКРЕТНУЮ программу (статус относительно неё, D7). */
  listProgramEmployees: (orderId: string, programId: string, signal?: AbortSignal) =>
    api.get<TrainingProgramEmployee[]>(
      `/orders/${orderId}/training/employees`,
      { programId },
      signal,
    ),
  /** Сотрудники для модалки выбора под ПАКЕТ (статус — самый срочный по его программам). */
  listPackageEmployees: (orderId: string, packageId: string, signal?: AbortSignal) =>
    api.get<TrainingProgramEmployee[]>(
      `/orders/${orderId}/training/employees`,
      { packageId },
      signal,
    ),
  /** Абсолютный URL статичного DOCX-шаблона согласия (кнопка «Скачать шаблон», Б8). */
  consentTemplateUrl: () => apiUrl('/training/consent-template'),
  /**
   * Абсолютный URL шаблона «Списка обучающихся» с выпадающим списком программ каталога
   * (training-import-template; ТЗ п.3, В23).
   */
  importTemplateUrl: () => apiUrl('/training/import-template'),
  /** Предпросмотр загрузки «Списка обучающихся» (order-import-legacy-forms). */
  importPreview: (orderId: string, file: File) =>
    postMultipart<OrderImportPreview>(
      `/orders/${orderId}/training/import/preview`,
      orderImportForm(file),
    ),
  /**
   * Загрузка «Списка обучающихся» в заказ — тот же файл, что в предпросмотре. `departmentName` —
   * подразделение сотрудников, которых нет в структуре (обязательно, если такие есть).
   */
  importFile: (orderId: string, file: File, departmentName: string) =>
    postMultipart<OrderImportResult>(
      `/orders/${orderId}/training/import`,
      orderImportForm(file, departmentName),
    ),
  /**
   * Состав своего заказа обучения группами по программам (centrika-docs!41) — карточка
   * «Состав заказа обучения» на странице заказа. Реквизиты групп клиенту только на чтение.
   */
  getComposition: (orderId: string, signal?: AbortSignal) =>
    api.get<TrainingComposition>(`/orders/${orderId}/training/composition`, undefined, signal),
  /**
   * «Заполнить по сотрудникам с истекающим и просроченным обучением» (training-order-entry-points;
   * ТЗ п.2.2, п.5.1, В22): сводка — что подставится; заполнение черновика. `personId` — только
   * этот сотрудник (карточка сотрудника).
   */
  prefillSummary: (personId?: string, signal?: AbortSignal) =>
    api.get<TrainingPrefill>('/training/prefill', personId ? { personId } : undefined, signal),
  prefill: (orderId: string, personId?: string) =>
    api.post<TrainingPrefill>(`/orders/${orderId}/training/prefill`, personId ? { personId } : {}),
  /**
   * Изменения состава заявки на согласовании (training-change-approval): предложить (желаемый
   * состав целиком), согласовать или отклонить изменения Центрики, отозвать свои.
   */
  proposeChange: (orderId: string, items: TrainingChangeItemInput[]) =>
    api.post<OrderDetail>(`/orders/${orderId}/training/change`, { items }),
  approveChange: (orderId: string) =>
    api.post<OrderDetail>(`/orders/${orderId}/training/change/approve`),
  /** «Отклонить» с необязательной причиной (ответ заказчицы 04.11 к п.1.4). */
  rejectChange: (orderId: string, reason = '') =>
    api.post<OrderDetail>(`/orders/${orderId}/training/change/reject`, { reason }),
  withdrawChange: (orderId: string) =>
    api.post<OrderDetail>(`/orders/${orderId}/training/change/withdraw`),
  /** Записи обучения по сотруднику (свой скоуп). */
  listRecords: (personId: string, signal?: AbortSignal) =>
    api.get<TrainingRecord[]>('/training/records', { personId }, signal),
  createRecord: (input: CreateExternalRecordInput) =>
    api.post<TrainingRecord>('/training/records', input),
  updateRecord: (id: string, input: UpdateExternalRecordInput) =>
    api.patch<TrainingRecord>(`/training/records/${id}`, input),
  removeRecord: (id: string) => api.del<void>(`/training/records/${id}`),
  /** Клиентский журнал сроков (только свои сотрудники). */
  journal: (signal?: AbortSignal) =>
    api.get<JournalResponse>('/training/journal', undefined, signal),
  /** Абонентское обслуживание по обучению своей организации (у физлица — пустой срок). */
  subscription: (signal?: AbortSignal) =>
    api.get<{ trainingSubscription: TrainingSubscription }>(
      '/training/subscription',
      undefined,
      signal,
    ),
  /**
   * Результат обучения своего заказа (F2-15, замечание менеджера): строки состава с датой
   * фактического обучения/сроком/статусом — done-gated (409 до статуса «Готово»), вызывать
   * только когда `order.dataStatus === 'done'`.
   */
  results: (orderId: string, signal?: AbortSignal) =>
    api.get<TrainingLine[]>(`/orders/${orderId}/training/results`, undefined, signal),
}

/** Админские операции: правка справочника программ (§3e) и админ-панель уведомлений (§3h) — роль admin. */
/** Итоги отправки писем за период (email-daily-limit, §3h). */
export interface EmailCounts {
  /** Принято почтовым сервером. */
  sent: number
  /** Отправка не удалась. */
  error: number
  /** Не отправлено: исчерпан дневной лимит. */
  limited: number
  /** Почта не подключена: письмо только записано в журнал. */
  logged: number
}

/**
 * Счётчик писем для администратора (`GET /admin/email-stats`, §3h). Сутки и месяцы считает
 * сервер по своим часам; `date` — его «сегодня» (ISO). `dailyLimit` 0 — лимит не действует.
 * `limitReached` — сегодняшний лимит выбран: уходят только письма входа. `monthlyQuota` —
 * месячный объём по тарифу почтового сервиса (0 — не задан): отправку он не ограничивает,
 * `monthlyQuotaExceeded` — за месяц отправлено больше объёма.
 */
export interface EmailStats {
  delivery: 'smtp' | 'log'
  dailyLimit: number
  limitReached: boolean
  monthlyQuota: number
  monthlyQuotaExceeded: boolean
  date: string
  today: EmailCounts
  month: EmailCounts
  prevMonth: EmailCounts
}

export const adminApi = {
  training: {
    updateProgram: (id: string, input: UpdateProgramInput) =>
      api.patch<Program>(`/admin/training/programs/${id}`, input),
    /** Подтвердить или отклонить программу «на подтверждении» (ТЗ 26.10.2026, п.4.4.3). */
    reviewProgram: (id: string, input: CatalogReviewInput) =>
      api.post<Program>(`/admin/training/programs/${id}/review`, input),
    /** Пакеты программ (training-packages-approval): правка, архив, порядок, подтверждение. */
    updatePackage: (id: string, input: ProgramPackageInput) =>
      api.patch<ProgramPackage>(`/admin/training/packages/${id}`, input),
    archivePackage: (id: string, archived: boolean) =>
      api.post<ProgramPackage>(`/admin/training/packages/${id}/archive`, { archived }),
    movePackage: (id: string, up: boolean) =>
      api.post<ProgramPackage[]>(`/admin/training/packages/${id}/move`, { up }),
    reviewPackage: (id: string, input: CatalogReviewInput) =>
      api.post<ProgramPackage>(`/admin/training/packages/${id}/review`, input),
    /** Архив программы и возврат из архива (training-catalog-v2). */
    archiveProgram: (id: string, archived: boolean) =>
      api.post<Program>(`/admin/training/programs/${id}/archive`, { archived }),
    /** Сопоставление прежней программы с новой: записи и черновики переходят на замену. */
    replaceProgram: (id: string, replacementId: string) =>
      api.post<{ recordsMoved: number; linesMoved: number; linesMerged: number; packagesMoved: number }>(
        `/admin/training/programs/${id}/replace`,
        { replacementId },
      ),
    /**
     * Разложение прежней программы-пакета на программы каталога (training-program-split): записи
     * обучения становятся записями программ состава с той же датой обучения.
     */
    splitProgram: (id: string, componentIds: string[]) =>
      api.post<ProgramSplitResult>(`/admin/training/programs/${id}/split`, { componentIds }),
    listProgramReplacements: (signal?: AbortSignal) =>
      api.get<ProgramReplacementCandidate[]>('/admin/training/program-replacements', undefined, signal),
    /** Файл каталога: предпросмотр (apply=false) или загрузка. */
    importCatalog: (
      file: File,
      opts: { apply: boolean; archiveMissing: boolean; skipErrors?: boolean },
    ) => {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('apply', String(opts.apply))
      fd.append('archiveMissing', String(opts.archiveMissing))
      fd.append('skipErrors', String(Boolean(opts.skipErrors)))
      return postMultipart<CatalogImportResult>('/admin/training/catalog/import', fd)
    },
    exportCatalog: (signal?: AbortSignal) => getBlob('/admin/training/catalog/export', signal),
    /** Дерево разделов (centrika-docs!55, training-catalog-v2), только админ. */
    createDirection: (name: string, parentId?: string) =>
      api.post<TrainingDirectionItem>('/admin/training/directions', { name, parentId: parentId ?? '' }),
    moveDirection: (id: string, up: boolean) =>
      api.post<TrainingDirectionItem[]>(`/admin/training/directions/${id}/move`, { up }),
    setDirectionJournalCode: (id: string, code: TrainingDirection | '') =>
      api.put<TrainingDirectionItem[]>(`/admin/training/directions/${id}/journal-code`, { code }),
    updateDirection: (id: string, name: string) =>
      api.patch<TrainingDirectionItem>(`/admin/training/directions/${id}`, { name }),
    deleteDirection: (id: string) => api.del<void>(`/admin/training/directions/${id}`),
  },
  /**
   * Раздел «Экология» (ecology-catalog), только админ: справочник услуг и документов (удаление
   * встроенной позиции → 409), матрица «Кому что нужно» и календарь отчётности (сохраняются целиком,
   * ответ — справочник), настройки раздела.
   */
  ecology: {
    createItem: (input: EcoCatalogItemInput) =>
      api.post<EcoCatalogItem>('/admin/ecology/catalog', input),
    updateItem: (id: string, input: EcoCatalogItemInput) =>
      api.patch<EcoCatalogItem>(`/admin/ecology/catalog/${id}`, input),
    deleteItem: (id: string) => api.del<void>(`/admin/ecology/catalog/${id}`),
    /** Настройка полей карточки строки позиции целиком (ecology-card-fields-admin). */
    saveCard: (id: string, input: EcoCardConfigInput) =>
      api.put<EcoCatalogItem>(`/admin/ecology/catalog/${id}/card`, input),
    /** Поля заявки позиции целиком (ecology-order-fields-admin). */
    saveOrderFields: (id: string, input: EcoOrderFieldsInput) =>
      api.put<EcoCatalogItem>(`/admin/ecology/catalog/${id}/order-fields`, input),
    saveMatrix: (rows: EcoMatrixRowInput[]) =>
      api.put<EcoCatalogItem[]>('/admin/ecology/matrix', { rows }),
    saveCalendar: (rows: EcoCalendarRowInput[]) =>
      api.put<EcoCatalogItem[]>('/admin/ecology/calendar', { rows }),
    updateSettings: (input: EcoSettings) =>
      api.patch<EcoSettings>('/admin/ecology/settings', input),
    /** Все вопросы опросника, в т.ч. выключенные. */
    listQuestions: (signal?: AbortSignal) =>
      api.get<EcoQuestion[]>('/admin/ecology/questions', undefined, signal),
    createQuestion: (input: EcoQuestionInput) =>
      api.post<EcoQuestion>('/admin/ecology/questions', input),
    updateQuestion: (id: string, input: EcoQuestionInput) =>
      api.patch<EcoQuestion>(`/admin/ecology/questions/${id}`, input),
    deleteQuestion: (id: string) => api.del<void>(`/admin/ecology/questions/${id}`),
    listRules: (signal?: AbortSignal) =>
      api.get<EcoRule[]>('/admin/ecology/rules', undefined, signal),
    createRule: (input: EcoRuleInput) => api.post<EcoRule>('/admin/ecology/rules', input),
    updateRule: (id: string, input: EcoRuleInput) =>
      api.patch<EcoRule>(`/admin/ecology/rules/${id}`, input),
    deleteRule: (id: string) => api.del<void>(`/admin/ecology/rules/${id}`),
    /** Состояние справочника ФККО (ecology-fkko). */
    fkkoStats: (signal?: AbortSignal) =>
      api.get<EcoFkkoStats>('/admin/ecology/fkko', undefined, signal),
    /** Загрузка справочника ФККО из файла (.xlsx, .xls, .csv, .docx) — заменяет его целиком. */
    importFkko: (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return postMultipart<EcoFkkoImport>('/admin/ecology/fkko/import', fd)
    },
  },
  /** Правка глобального справочника должностей (p2-position-dictionary §D2), только админ. */
  positions: {
    create: (input: CreatePositionInput) => api.post<Position>('/admin/positions', input),
    /** Добавить должности, не найденные при импорте структуры, одним запросом. */
    createBatch: (names: string[]) =>
      api.post<CreatePositionsBatchResult>('/admin/positions/batch', { names }),
    update: (id: string, input: UpdatePositionInput) =>
      api.patch<Position>(`/admin/positions/${id}`, input),
    delete: (id: string) => api.del<void>(`/admin/positions/${id}`),
    /** Отметить должность проверенной или снять отметку. */
    setVerified: (id: string, verified: boolean) =>
      api.patch<Position>(`/admin/positions/${id}/verified`, { verified }),
    /** Скрыть должность из подсказок или показать обратно (запись и ссылки не меняются). */
    setHidden: (id: string, hidden: boolean) =>
      api.patch<Position>(`/admin/positions/${id}/hidden`, { hidden }),
    /** Что изменит объединение должности `id` с `targetId` (только чтение). */
    mergePreview: (id: string, targetId: string, signal?: AbortSignal) =>
      api.get<PositionMergePreview>(`/admin/positions/${id}/merge-preview`, { targetId }, signal),
    /** Объединить должность `id` с `targetId`: ссылки переходят на целевую, `id` удаляется. */
    merge: (id: string, targetId: string) =>
      api.post<PositionMergeResult>(`/admin/positions/${id}/merge`, { targetId }),
  },
  /**
   * Админ-панель уведомлений (§3h): встроенные виды писем (вкл/выкл, тема/текст+метки, офсеты),
   * предпросмотр и тест-отправка себе (фронт-гейт сохранения), ручная разовая рассылка.
   */
  notifications: {
    listTemplates: (signal?: AbortSignal) =>
      api.get<AdminTemplate[]>('/admin/notification-templates', undefined, signal),
    patchTemplate: (kind: string, input: PatchTemplateInput) =>
      api.patch<AdminTemplate>(`/admin/notification-templates/${kind}`, input),
    previewTemplate: (kind: string, input: TemplateDraftInput) =>
      api.post<TemplatePreview>(`/admin/notification-templates/${kind}/preview`, input),
    /** Тест-письмо на email самого админа (себе); бэкенд отвечает 202. */
    testTemplate: (kind: string, input: TemplateDraftInput) =>
      api.post<void>(`/admin/notification-templates/${kind}/test`, input),
    broadcast: (input: BroadcastInput) => api.post<{ sent: number }>('/admin/broadcasts', input),
    /** Счётчик писем и состояние дневного лимита (email-daily-limit). */
    emailStats: (signal?: AbortSignal) =>
      api.get<EmailStats>('/admin/email-stats', undefined, signal),
  },
}

export const teamApi = {
  /** Список клиентов (Блок 2: реальный GET /api/team/clients). */
  /** `manager` — фильтр по менеджеру (team-access): uuid сотрудника или `none` — без менеджера. */
  listClients: (
    query?: {
      q?: string
      manager?: string
      /** Менеджер по сопровождению: uuid или `none` — без него (client-support-manager). */
      supportManager?: string
      limit?: number
      offset?: number
    },
    signal?: AbortSignal,
  ) => api.get<ListResponse<ClientSummary>>('/team/clients', { ...query }, signal),
  createClient: (input: CreateClientInput) =>
    api.post<{ client: CreatedClient }>('/team/clients', input),
  /**
   * Автозаполнение по ИНН через DaData (p2-dadata, design D11) для формы создания
   * клиента — клиента ещё не существует, поэтому БЕЗ `cid` (флат GET /api/team/dadata/party).
   */
  dadataLookup: (inn: string) => fetchDaDataMatches('/team/dadata/party', inn),
  /** Карточка клиента (GET /api/team/clients/{cid}) — ClientCard с name и profile?. */
  getClient: (cid: string, signal?: AbortSignal) =>
    api.get<ClientCard>(`/team/clients/${cid}`, undefined, signal),
  /**
   * Задать/сменить общий вход компании (PATCH /api/team/clients/{cid}/credentials).
   * Передаётся email и/или пароль; смена бампит версию токена (компания войдёт заново).
   */
  setClientCredentials: (cid: string, input: { loginEmail?: string; password?: string }) =>
    api.patch<{ loginEmail: string }>(`/team/clients/${cid}/credentials`, input),
  /** Входы с доступом к организации (multi-organization-login). */
  clientLogins: (cid: string, signal?: AbortSignal) =>
    api.get<{ items: ClientLogin[] }>(`/team/clients/${cid}/logins`, undefined, signal),
  /** Закрепить существующий вход клиента-компании за организацией по его почте. */
  linkClientLogin: (cid: string, email: string) =>
    api.post<{ items: ClientLogin[] }>(`/team/clients/${cid}/logins`, { email }),
  /** Открепить вход от организации (последнюю организацию входа открепить нельзя — 409). */
  unlinkClientLogin: (cid: string, accountId: string) =>
    api.del<{ items: ClientLogin[] }>(`/team/clients/${cid}/logins/${accountId}`),
  /**
   * Представитель организации у входа (login-representative; ТЗ п.9.4, В28): контактное лицо этой
   * организации; пустая строка — снять.
   */
  setLoginRepresentative: (cid: string, accountId: string, contactId: string) =>
    api.put<{ items: ClientLogin[] }>(`/team/clients/${cid}/logins/${accountId}/representative`, {
      contactId,
    }),
  /**
   * Срок договора абонентского обслуживания по обучению (администратор и руководитель —
   * training-subscription-head). Обе даты пустые — обслуживание снимается.
   */
  setTrainingSubscription: (cid: string, input: { from: string; to: string }) =>
    api.put<{ trainingSubscription: TrainingSubscription }>(
      `/team/clients/${cid}/training-subscription`,
      input,
    ),
  listAccounts: (query?: { limit?: number; offset?: number }, signal?: AbortSignal) =>
    api.get<ListResponse<TeamAccount>>('/team/accounts', { ...query }, signal),
  createEmployee: (input: CreateEmployeeInput) =>
    api.post<{ user: User }>('/team/accounts/employees', input),
  setAccountDisabled: (id: string, disabled: boolean) =>
    api.patch<void>(`/team/accounts/${id}`, { disabled }),
  deleteAccount: (id: string) => api.del<void>(`/team/accounts/${id}`),
  /**
   * Передать дела сотрудника (team-visibility): его клиентов и незавершённые заказы — другому
   * действующему сотруднику; пустой `toAccountId` снимает закрепления. Только администратор.
   */
  transferAssignments: (id: string, toAccountId: string) =>
    api.post<{ clients: number; supportedClients: number; orders: number }>(
      `/team/accounts/${id}/transfer`,
      { toAccountId },
    ),
  /** ФИО, телефон и роль сотрудника Центрики (team-access) — только суперадминистратор. */
  updateAccount: (id: string, input: UpdateAccountInput) =>
    api.patch<void>(`/team/accounts/${id}`, input),
  /** Ответственный по абонентскому обслуживанию по обучению — только администратор. */
  getTrainingSubscriptionResponsible: (signal?: AbortSignal) =>
    api.get<TrainingSubscriptionResponsible>(
      '/admin/training/subscription-responsible',
      undefined,
      signal,
    ),
  /** Задать ответственного по абонентскому обслуживанию (пустая строка — снять). */
  setTrainingSubscriptionResponsible: (accountId: string) =>
    api.put<TrainingSubscriptionResponsible>('/admin/training/subscription-responsible', {
      accountId,
    }),
  /** Справочник сотрудников Центрики: выбор менеджера клиента и ответственного в заказе. */
  listStaff: (signal?: AbortSignal) =>
    api.get<ListResponse<StaffMember>>('/team/staff', undefined, signal),
  /**
   * Назначить менеджера клиента (пустая строка — снять) — руководитель и суперадминистратор.
   * Заказы клиента не меняются: ответственного в заказе назначают отдельно.
   */
  setClientManager: (cid: string, managerAccountId: string) =>
    api.put<{ manager: ClientManager | null }>(`/team/clients/${cid}/manager`, {
      managerAccountId,
    }),
  /**
   * Назначить менеджера по сопровождению (пустая строка — снять) — руководитель и администратор
   * (client-support-manager; ТЗ п.9.1, В27).
   */
  setClientSupportManager: (cid: string, supportManagerAccountId: string) =>
    api.put<{ supportManager: ClientManager | null }>(`/team/clients/${cid}/support-manager`, {
      supportManagerAccountId,
    }),
  /** Сколько клиентов без менеджера по сопровождению — администратор и руководитель. */
  countClientsWithoutSupportManager: (signal?: AbortSignal) =>
    api.get<{ count: number }>('/team/clients/without-support-manager', undefined, signal),
  // --- Блок 3: очередь и карточка заказа команды ---
  listOrders: (query?: OrdersQuery, signal?: AbortSignal) =>
    api.get<ListResponse<TeamOrderRow>>('/team/orders', { ...query }, signal),
  /** Командные операции по конкретному заказу (`/api/team/orders/{id}/...`). */
  /** Вопросы клиентов (client-questions) — по видимости вопроса. */
  questions: {
    list: (query?: QuestionListQuery & { client?: string }, signal?: AbortSignal) =>
      api.get<ListResponse<QuestionListItem>>('/team/questions', { ...query }, signal),
    get: (id: string, signal?: AbortSignal) =>
      api.get<QuestionDetail>(`/team/questions/${id}`, undefined, signal),
    reply: (id: string, body: string, files: File[]) =>
      postMultipart<QuestionDetail>(`/team/questions/${id}/messages`, questionForm({ body }, files)),
  },
  orders: {
    /**
     * Внутренние комментарии команды к заказу (order-internal-notes; ТЗ п.9.5) — клиенту не видны.
     * Лента от старых к новым; добавление отвечает всей лентой.
     */
    listNotes: (id: string, signal?: AbortSignal) =>
      api.get<{ items: OrderInternalNote[] }>(`/team/orders/${id}/notes`, undefined, signal),
    addNote: (id: string, body: string) =>
      api.post<{ items: OrderInternalNote[] }>(`/team/orders/${id}/notes`, { body }),
    get: (id: string, signal?: AbortSignal) =>
      api.get<TeamOrderDetail>(`/team/orders/${id}`, undefined, signal),
    /**
     * Смена статуса заказа. Даты в теле нет: с centrika-docs!41 дата обучения принадлежит
     * ПРОГРАММЕ заказа, а завершение гейтится наличием даты и итогового документа у каждой
     * программы со слушателями (сервер отдаёт 422, если реквизиты не заполнены).
     */
    setStatus: (id: string, status: TeamOrderStatus) =>
      api.post<TeamOrderDetail>(`/team/orders/${id}/status`, { status }),
    return: (id: string, comment: string) =>
      api.post<TeamOrderDetail>(`/team/orders/${id}/return`, { comment }),
    /**
     * Отмена заказа командой (order-cancel-restore): причина необязательна, её видит клиент. Только
     * поданный и не завершённый заказ, иначе 409. «Восстановить» — любой отменённый.
     */
    cancel: (id: string, reason: string) =>
      api.post<TeamOrderDetail>(`/team/orders/${id}/cancel`, { reason }),
    restore: (id: string) => api.post<TeamOrderDetail>(`/team/orders/${id}/restore`),
    update: (id: string, input: TeamUpdateOrderInput) =>
      api.patch<TeamOrderDetail>(`/team/orders/${id}`, input),
    advanceStage: (id: string, input: AdvanceStageInput) =>
      api.post<TeamOrderDetail>(`/team/orders/${id}/stage`, input),
    /**
     * Сохранить регистрационный номер заказа СОУТ (centrika-docs!73) или профрисков
     * (profrisk-order-registration-number). Пустая строка сохраняется как отсутствие рег.номера.
     * Доступно только команде и только для этих услуг (у обучения — 409).
     */
    saveRegistrationNumber: (id: string, registrationNumber: string) =>
      api.put<TeamOrderDetail>(`/team/orders/${id}/registration-number`, { registrationNumber }),
  },
  /** Загрузка файла к заказу (multipart; уже существующий хендлер) → FileDTO. */
  uploadFile: (input: UploadFileInput) =>
    postMultipart<FileDTO>('/team/files', buildFileForm(input)),
  deleteFile: (id: string) => api.del<void>(`/team/files/${id}`),
  /** Реестр документов всех клиентов (с ownerName/ownerKind). */
  listDocuments: (query?: TeamDocumentsQuery, signal?: AbortSignal) =>
    api.get<ListResponse<DocumentDTO>>('/team/documents', { ...query }, signal),
  /** История заказов клиента (закрывает остаток team-portal 5.3). */
  clientOrders: (cid: string, query?: OrdersListQuery, signal?: AbortSignal) =>
    api.get<ListResponse<OrderCard>>(`/team/clients/${cid}/orders`, { ...query }, signal),
  /** Журнал действий (лента; фильтры order/client — uuid). */
  listAudit: (query?: AuditQuery, signal?: AbortSignal) =>
    api.get<ListResponse<AuditDTO>>('/team/audit', { ...query }, signal),
  /**
   * СОУТ командной стороны (§3d): просмотр анкеты клиента (read-only) и ввод результата по местам.
   * Срок/«бессрочно» считает сервер; `originalClass` — исходный класс из анкеты клиента.
   */
  sout: {
    getAnketa: (id: string, signal?: AbortSignal) =>
      api.get<SoutAnketa>(`/team/orders/${id}/sout`, undefined, signal),
    getResults: (id: string, signal?: AbortSignal) =>
      api.get<SoutResult[]>(`/team/orders/${id}/sout/results`, undefined, signal),
    putResults: (id: string, input: PutSoutResultsInput) =>
      api.put<SoutResult[]>(`/team/orders/${id}/sout/results`, input),
    /**
     * Excel-выгрузка рабочих мест заказа (centrika-docs!89): все места текущего заказа в
     * порядке анкеты, по строке на место, все поля полной информации — отдельными колонками.
     * Формирует сервер по СНИМКУ заказа; клиенту недоступна.
     */
    downloadWorkplaces: (id: string, signal?: AbortSignal) =>
      getBlob(`/team/orders/${id}/sout/workplaces/export`, signal),
    /**
     * Разбор сводной ведомости .docx (sout-results-statement-import): строки ведомости с классом и
     * номером места, сопоставленные с местами заказа. Ничего не сохраняет. Доступ — как у
     * сохранения результатов («В работе»; в «Готово» — только администратор).
     */
    parseStatement: (id: string, file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return postMultipart<SoutStatement>(`/team/orders/${id}/sout/results/statement`, fd)
    },
    /**
     * Разбор декларации .docx (sout-results-declaration-and-statement-date): рабочие места и
     * реквизиты декларации. Ничего не сохраняет. Доступ — как у сохранения результатов.
     */
    parseDeclaration: (id: string, file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return postMultipart<SoutDeclaration>(`/team/orders/${id}/sout/results/declaration`, fd)
    },
  },
  /**
   * Профриски командной стороны (§3f): просмотр анкеты клиента (+ видимый `cardsCount`), правка
   * служебной колонки «Количество карт по итогу работ» и ввод результата (уровень риска + дата)
   * по должностям. Результат вносится/правится в любой момент; клиент — read-only.
   */
  profrisk: {
    getAnketa: (id: string, signal?: AbortSignal) =>
      api.get<ProfriskAnketa>(`/team/orders/${id}/profrisk`, undefined, signal),
    updatePosition: (id: string, posId: string, input: UpdateProfriskPositionInput) =>
      api.patch<ProfriskPosition>(`/team/orders/${id}/profrisk/positions/${posId}`, input),
    getResults: (id: string, signal?: AbortSignal) =>
      api.get<ProfriskResult[]>(`/team/orders/${id}/profrisk/results`, undefined, signal),
    putResults: (id: string, input: PutProfriskResultsInput) =>
      api.put<ProfriskResult[]>(`/team/orders/${id}/profrisk/results`, input),
    /**
     * Excel-выгрузка анкеты профрисков по шаблону заказчика (profrisk-anketa-export-template):
     * лист «Анкета» (реквизиты, контактное лицо, чек-лист, список инструкций) и лист «Формы»
     * (должности). Формирует сервер по данным анкеты заказа; клиенту недоступна.
     */
    downloadAnketa: (id: string, signal?: AbortSignal) =>
      getBlob(`/team/orders/${id}/profrisk/export`, signal),
  },
  /**
   * Обучение командной стороны (§3e, переработана p2-service-training + centrika-docs!41):
   * состав заказа группами по программам (сам состав формирует клиент), реквизиты каждой
   * программы — дата окончания обучения и итоговый документ (правятся только у заказа
   * «В работе»), журнал сроков по всем клиентам и ручной триггер «Напомнить».
   */
  training: {
    /**
     * Добавить программу или пакет в каталог — любой сотрудник команды (ТЗ 26.10.2026,
     * п.4.4.3): у администратора действует сразу, у остальных ждёт подтверждения.
     * Удалить — своё неподтверждённое или отклонённое (администратор — любое без ссылок).
     */
    createProgram: (input: CreateProgramInput) => api.post<Program>('/team/training/programs', input),
    deleteProgram: (id: string) => api.del<void>(`/team/training/programs/${id}`),
    createPackage: (input: ProgramPackageInput) =>
      api.post<ProgramPackage>('/team/training/packages', input),
    deletePackage: (id: string) => api.del<void>(`/team/training/packages/${id}`),
    getComposition: (id: string, signal?: AbortSignal) =>
      api.get<TrainingComposition>(`/team/orders/${id}/training`, undefined, signal),
    /**
     * Изменения состава заявки на согласовании (training-change-approval): исходное состояние
     * редактора (каталог с выбранным в заказе и слушатели — как мастер клиента), сотрудники клиента
     * для окна выбора, предложение изменений и решение по изменениям клиента.
     */
    getWizard: (id: string, signal?: AbortSignal) =>
      api.get<TrainingWizard>(`/team/orders/${id}/training/wizard`, undefined, signal),
    listProgramEmployees: (id: string, programId: string, signal?: AbortSignal) =>
      api.get<TrainingProgramEmployee[]>(`/team/orders/${id}/training/employees`, { programId }, signal),
    listPackageEmployees: (id: string, packageId: string, signal?: AbortSignal) =>
      api.get<TrainingProgramEmployee[]>(`/team/orders/${id}/training/employees`, { packageId }, signal),
    proposeChange: (id: string, items: TrainingChangeItemInput[]) =>
      api.post<TeamOrderDetail>(`/team/orders/${id}/training/change`, { items }),
    approveChange: (id: string) => api.post<TeamOrderDetail>(`/team/orders/${id}/training/change/approve`),
    rejectChange: (id: string, reason = '') =>
      api.post<TeamOrderDetail>(`/team/orders/${id}/training/change/reject`, { reason }),
    /**
     * «Предложить свой вариант» (ответ заказчицы 04.11 к п.1.4): отклонить предложение клиента с
     * причиной и внести своё — одной операцией.
     */
    counterChange: (id: string, items: TrainingChangeItemInput[], reason: string) =>
      api.post<TeamOrderDetail>(`/team/orders/${id}/training/change/counter`, { items, reason }),
    withdrawChange: (id: string) =>
      api.post<TeamOrderDetail>(`/team/orders/${id}/training/change/withdraw`),
    /** Дата окончания обучения группы программы (пустая строка — снять дату). */
    setProgramEndDate: (id: string, programId: string, endDate: string) =>
      api.patch<TrainingComposition>(`/team/orders/${id}/training/programs/${programId}`, {
        endDate,
      }),
    /**
     * Общая Excel-выгрузка слушателей заказа по шаблону заказчика (centrika-docs!42):
     * одна строка — один уникальный сотрудник, программы через запятую. Формирует сервер
     * (шаблон с оформлением, справочным листом и проверками данных), клиенту недоступна.
     */
    downloadListeners: (id: string, signal?: AbortSignal) =>
      getBlob(`/team/orders/${id}/training/listeners`, signal),
    /** Загрузить/заменить итоговый документ (протокол об обучении) группы программы. */
    uploadProgramDocument: (id: string, programId: string, file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return postMultipart<TrainingComposition>(
        `/team/orders/${id}/training/programs/${programId}/document`,
        fd,
      )
    },
    journal: (query: TeamJournalQuery, signal?: AbortSignal) =>
      api.get<JournalResponse>('/team/training/journal', { ...query }, signal),
    /** Ручной триггер напоминания (202, доставка — Блок 7). `client` обязателен (query):
     * бэкенд проверяет, что сотрудник принадлежит этому клиенту. */
    remind: (clientId: string, input: RemindInput) =>
      api.post<void>(`/team/training/remind?client=${encodeURIComponent(clientId)}`, input),
  },
}

// --- Блок 2: клиенты и структура компании (api-contract §3b) ---

/**
 * Реквизиты организации (CompanyProfile, §3b). Имена полей — точно по контракту.
 * Фаза 2 (design D1, _РЕШЕНИЯ_PM_фаза2.md A1): реквизиты правят и клиент-компания
 * (вкл. ИНН/ОГРН), и команда — PATCH под `/company/profile` (клиент) и
 * `/team/clients/{cid}/profile` (команда), без модерации. `industryProfile`/`hasOpo`
 * остаются менеджерскими (клиентский PATCH их не меняет, даже если передать).
 * `phone` — телефон организации (формат +7 при наличии). ОКОГУ — видимое необязательное
 * поле (§3.2). `profileComplete`/`onboardingSkippedAt` — только для чтения (D5, онбординг).
 */
export interface CompanyProfile {
  id: string
  nameFull: string
  nameShort: string
  inn: string
  kpp: string
  ogrn: string
  okpo: string
  oktmo: string
  okved: string
  okogu?: string
  legalAddress: string
  email: string
  phone?: string
  directorName: string
  directorPosition: string
  industryProfile?: string
  hasOpo: boolean
  profileComplete?: boolean
  onboardingSkippedAt?: string
}

/**
 * Тело правки реквизитов. Поля «профиль отрасли» (industryProfile) и «есть ОПО»
 * (hasOpo) — менеджерские: сервер их игнорирует при клиентской правке (D1), но тип
 * остаётся общим для клиента и команды — компонент формы сам решает, отправлять ли их
 * (см. CompanyProfileForm `hideManagerFields`). `profileComplete`/`onboardingSkippedAt`
 * — только для чтения, здесь не участвуют (Omit).
 */
export type UpdateCompanyProfileInput = Partial<
  Omit<CompanyProfile, 'id' | 'profileComplete' | 'onboardingSkippedAt'>
>

/**
 * Организация/ИП, найденные DaData по ИНН (p2-dadata, §3.3, design D5). Поля, кроме
 * kind/nameFull/nameShort/inn/legalAddress, опциональны — тариф DaData может их не
 * отдавать (ОКПО/ОКТМО/ОКОГУ/руководитель) либо их нет у ИП (КПП). Отсутствующее поле
 * НЕ должно затирать уже введённое пользователем значение черновика формы.
 */
export interface DaDataPartyMatch {
  kind: 'legal' | 'individual'
  nameFull: string
  nameShort?: string
  inn: string
  kpp?: string
  ogrn?: string
  okpo?: string
  oktmo?: string
  okved?: string
  okogu?: string
  legalAddress?: string
  directorName?: string
  directorPosition?: string
}

/** Контактное лицо (§3b). `maxContact` — контакт в мессенджере «Макс» (пока просто хранится). */
export interface Contact {
  id: string
  fullName: string
  position: string
  phone: string
  email: string
  receivesNotifications: boolean
  maxContact?: string
}

export type ContactInput = Omit<Contact, 'id'>

/** Подразделение (§3b). */
export interface Department {
  id: string
  name: string
}

/**
 * Лёгкое рабочее место — отдел + должность (§3b) + фаза 2 (§10.2/§8.3):
 * `hasNoEmployee` — режим «место без сотрудника»; `staffUnits` — вычисляемые
 * штатные единицы должности в подразделении (design D4, только для чтения).
 *
 * `actualAddress`/`shiftHours`/`weekHours`/`schedule` (p3-sout-workplaces Волна 1, D4,
 * миграция 00023) — режим работы/адрес места для двусторонней синхронизации с карточкой
 * РМ заказа СОУТ (§5/§7 ТЗ); сама синхронизация — бизнес-логика следующей волны, здесь
 * только контракт полей. `shiftHours`/`weekHours` — nullable (места без заполненного
 * режима, в т.ч. заведённые до этой миграции, читаются как `null` — §15); целое
 * положительное число при заполнении (решение оркестратора, валидация на бэке).
 */
export interface Workplace {
  id: string
  departmentId: string
  departmentName: string
  position: string
  hasNoEmployee: boolean
  staffUnits: number
  actualAddress: string
  shiftHours: number | null
  weekHours: number | null
  schedule: string
  /**
   * Сотрудник/сменщик, связанные ЭТИМ созданием (§7, p3-structure-sout-training волна 7,
   * D2) — заполняются ТОЛЬКО в ответе композитного создания места (`createWorkplace` с
   * телом `employee`/`shiftPartner`); у остальных ответов (list/get/update) — `undefined`
   * (бэкенд отдаёт `omitempty`, обратная совместимость контракта полная).
   */
  employee?: Employee
  shiftPartner?: Employee
}

/**
 * Сотрудник/сменщик композитной формы «Добавление рабочего места» (§7.1, backend
 * `workplacePersonReq`): РОВНО один способ — существующий `personId` (переносится на
 * создаваемое место, «переезд» осознанная семантика) ИЛИ поля нового сотрудника (те же
 * личные/кадровые поля, что у `CreateEmployeeFormInput`, без селекторов места — место уже
 * задаётся верхним уровнем `CreateWorkplaceInput`).
 */
export interface WorkplacePersonInput {
  personId?: string
  fullName?: string
  gender?: 'm' | 'f'
  birthDate?: string
  snils?: string
  email?: string
  phone?: string
  education?: string
  hasDisability?: boolean
  hiredOn?: string
  rank?: string
}

/** `hasNoEmployee` — обязательный явный выбор режима при создании места (§10.2). */
export interface CreateWorkplaceInput {
  departmentId?: string
  departmentName?: string
  /**
   * Должность-текст (миррор лейбла). Переходный контракт: сервер резолвит по name_norm.
   * Строгий путь (p2-position-dictionary, Волна 3) — `positionId` из справочника: если задан,
   * он авторитетен, текст сервер берёт каноническим из справочника.
   */
  position: string
  positionId?: string
  hasNoEmployee: boolean
  /**
   * Режим работы/адрес места (p3-sout-workplaces Волна 1, D4) — необязательные: не
   * переданы → при создании остаются пустыми/`null`, при PATCH (см. `updateWorkplace`,
   * `Partial<CreateWorkplaceInput>`) СОХРАНЯЮТ текущее значение (частичный патч только
   * для этих полей). `shiftHours`/`weekHours` — число больше 0, не более двух знаков после
   * запятой при заполнении (fractional-work-hours, `lib/work-hours`).
   */
  actualAddress?: string
  shiftHours?: number
  weekHours?: number
  schedule?: string
  /**
   * Единая форма «Добавление рабочего места» (§7, p3-structure-sout-training волна 7, D2):
   * опциональный сотрудник/сменщик — тогда бэкенд создаёт место+сотрудника(-ов) ОДНОЙ
   * транзакцией (`CreateWorkplaceWithComposition`). `shiftPartner` допустим ТОЛЬКО вместе с
   * `employee` (проверяется на бэкенде, 422 у поля `employee`, если нарушено). Без обоих полей —
   * старый путь без изменений (обратная совместимость: `StructureImport`, прочие вызовы).
   */
  employee?: WorkplacePersonInput
  shiftPartner?: WorkplacePersonInput
}

/** Сменщик (§10.1) — только для отображения в карточке (роster их не показывает). */
export interface ShiftPartner {
  id: string
  fullName: string
}

/**
 * Сотрудник (§3b) + фаза 2 (§10.1). `isMinor` — ХРАНИМЫЙ признак профиля «Сотрудник младше
 * 18 лет» (centrika-docs!93): его ставит клиент вручную в форме рабочего места заказа СОУТ,
 * из `birthDate` он не вычисляется.
 */
export interface Employee {
  id: string
  fullName: string
  gender: 'm' | 'f'
  birthDate: string
  snils: string
  email?: string
  /** Телефон сотрудника — необязательное поле профиля (p3-sout-commission D1). */
  phone?: string
  education?: string
  hasDisability: boolean
  workplaceId: string
  departmentName: string
  position: string
  isMinor: boolean
  /** Дата приёма на работу — необязательная (§10.1). */
  hiredOn?: string
  /** Разряд — необязательный, без гейта по типу профессии (design D5). */
  rank?: string
  /** Признак сменного рабочего места. */
  isShift: boolean
  /** Сменщики — заполнено только в карточке (GET по id), в роster-списке пусто. */
  shiftPartners: ShiftPartner[]
  /** Статус активности — мягкая деактивация вместо удаления (design D1). */
  isActive: boolean
  deactivatedAt?: string
}

/**
 * Сводка последнего ВИДИМОГО результата СОУТ (p2-ot-section §9.4/§9.5, design D6/D7):
 * `null` у EmployeeCard.workConditions/WorkplaceCard.soutSummary — «нет данных», не ошибка.
 */
export interface WorkConditionsSummary {
  hazardClass: string
  conductedOn?: string
  validUntil?: string
  perpetual: boolean
  /**
   * «N раб.места» (centrika-docs!90) — номер рабочего места из ЭТОГО отображаемого
   * результата СОУТ. Пустая строка — номер не сохранён (карточка места рисует «—»).
   * Необязательно в типе: ответы/фикстуры без поля читаются как «номера нет».
   */
  rmNumber?: string
}

/**
 * Сводка последнего done-результата профрисков должности (p2-ot-section §9.4/§9.5):
 * `null` — «нет данных».
 */
export interface ProfriskInfoSummary {
  riskLevel: RiskLevel
  assessedOn?: string
}

/**
 * Карточка сотрудника (GET by id): Employee + блок «Условия труда» (сводка последнего
 * результата СОУТ рабочего места) + блок «Информация по профрискам» (сводка последнего
 * результата должности) + блок «Обучения» (Блок 5). BREAKING (p2-ot-section, design D6/D7):
 * `workConditions` был буквальным `null`, теперь — реальная сводка или `null` при отсутствии.
 */
export interface EmployeeCard extends Employee {
  workConditions: WorkConditionsSummary | null
  profriskInfo: ProfriskInfoSummary | null
  /** Блок «Обучения» (§3e): все записи обучения, где человек — слушатель (заказы + внешние). */
  trainings: TrainingRecord[]
  /** Документ об образовании (centrika-docs!59) — один PDF на человека; правит клиент. */
  educationDocument?: EducationDocumentStatus
  /** Согласие на обработку ПД — один PDF на человека, во всех заказах обучения; правит клиент. */
  consent?: TrainingConsentStatus
}

/** Создание рабочего места на месте при добавлении сотрудника (атомарно). */
export interface NewWorkplaceInput {
  departmentId?: string
  departmentName?: string
  position: string
}

/** Создание сотрудника: выбор существующего места ИЛИ создание нового на месте (§3b). */
export interface CreateEmployeeFormInput {
  fullName: string
  gender: 'm' | 'f'
  birthDate: string
  snils: string
  email?: string
  /** Телефон — необязательное поле профиля (p3-sout-commission D1). */
  phone?: string
  education?: string
  hasDisability: boolean
  /** Дата приёма — необязательная (§10.1). */
  hiredOn?: string
  /** Разряд — необязательный (design D5). */
  rank?: string
  /** Сменное рабочее место — сменщики обязательны, только если true. */
  isShift?: boolean
  shiftPartnerIds?: string[]
  workplaceId?: string
  newWorkplace?: NewWorkplaceInput
}

/**
 * Правка сотрудника. Форма (UI) удобнее с вложенным `newWorkplace`; перед отправкой
 * тело разворачивается в плоское (см. toEmployeeRequestBody). Все поля опциональны (PATCH).
 */
export interface UpdateEmployeeInput {
  fullName?: string
  gender?: 'm' | 'f'
  birthDate?: string
  snils?: string
  email?: string
  phone?: string
  education?: string
  hasDisability?: boolean
  hiredOn?: string
  rank?: string
  isShift?: boolean
  shiftPartnerIds?: string[]
  workplaceId?: string
  newWorkplace?: NewWorkplaceInput
}

/**
 * ПЛОСКОЕ тело POST/PATCH /employees (api-contract §3b) — то, что реально уходит на сервер:
 * без вложенных объектов. Рабочее место задаётся РОВНО одним способом:
 * `workplaceId` | `departmentId`+`position` | `departmentName`+`position`.
 */
export interface EmployeeRequestBody {
  fullName?: string
  gender?: 'm' | 'f'
  birthDate?: string
  snils?: string
  email?: string
  phone?: string
  education?: string
  hasDisability?: boolean
  hiredOn?: string
  rank?: string
  isShift?: boolean
  shiftPartnerIds?: string[]
  workplaceId?: string
  departmentId?: string
  departmentName?: string
  position?: string
}

/**
 * Разворачивает форму сотрудника (с вложенным `newWorkplace`) в плоское тело §3b.
 * Личные поля переносятся как есть; выбор места — приоритет существующему `workplaceId`,
 * иначе новое место (`departmentId`+`position` или `departmentName`+`position`).
 */
export function toEmployeeRequestBody(
  input: CreateEmployeeFormInput | UpdateEmployeeInput,
): EmployeeRequestBody {
  const { workplaceId, newWorkplace, ...personal } = input
  const body: EmployeeRequestBody = { ...personal }
  if (workplaceId) {
    body.workplaceId = workplaceId
  } else if (newWorkplace) {
    if (newWorkplace.departmentId) {
      body.departmentId = newWorkplace.departmentId
    } else if (newWorkplace.departmentName) {
      body.departmentName = newWorkplace.departmentName
    }
    body.position = newWorkplace.position
  }
  return body
}

/** Вид подсказки против дублей. */
export type SuggestKind = 'department' | 'position'

/** Подсказка существующего отдела/должности (анти-дубли, §3b). */
export interface Suggestion {
  /** Текст (название отдела / должность). */
  value: string
  /** id существующей записи справочника (для отделов; у должностей отсутствует). */
  id?: string
}

/**
 * Ответ GET /suggest (§3b): только `{ items }` — без total/limit/offset (это НЕ обычный
 * список с пагинацией, бэкенд отдаёт усечённый набор подсказок).
 */
export interface SuggestResponse {
  items: Suggestion[]
}

/**
 * Запрос адресной книги (GET /api/employees, §3b). Поиск — `q` (по ФИО/СНИЛС, ILIKE).
 * Фильтра по отделу на сервере нет — он клиентский (фильтрация на фронте).
 * `activeOnly` (design D6): true — пикеры для НОВЫХ заказов (деактивированные не
 * предлагаются); по умолчанию (false/omitted) — роster показывает всех.
 */
export interface EmployeesQuery {
  q?: string
  /**
   * Фильтр по подразделению — uuid подразделения (issue #14). Серверный: ростер отдаётся
   * страницами, поэтому отбор по загруженной странице на фронте не находил бы сотрудников
   * за её пределами.
   */
  department?: string
  /**
   * Искать `q` ещё и по должности рабочего места (issue #15). Ставит диалог СОУТ «Из
   * структуры компании» — его поле подписано «Поиск по ФИО или должности», а список
   * выдаётся страницами, поэтому отбор по должности обязан идти на сервере. Ростер
   * сотрудников флаг не передаёт: его поиск остаётся по ФИО/СНИЛС.
   */
  searchPosition?: boolean
  limit?: number
  offset?: number
  activeOnly?: boolean
}

// --- Импорт структуры из Excel (p2-excel-import, §10.3/§12) ---

/** Классификация строки предпросмотра импорта (design D3). */
export type ImportRowAction = 'update' | 'create' | 'warning' | 'manual'

/** Строка предпросмотра импорта: разобранные поля + её классификация. */
export interface ImportPreviewRow {
  rowNumber: number
  action: ImportRowAction
  department: string
  position: string
  fullName: string
  birthDate: string
  snils: string
  gender: 'm' | 'f' | ''
  hiredOn: string
  education: string
  rank: string
  /** id совпавшего сотрудника — только для action=update. */
  matchedPersonId?: string
  /**
   * Строка без даты рождения (action=manual): единственный активный сотрудник с тем же ФИО, которого
   * можно обновить решением `update` (structure-import-custom-file, D2.5). Нет — решения только
   * «создать»/«пропустить».
   */
  suggestedPersonId?: string
  /** Сколько активных сотрудников с тем же ФИО (для строки без даты рождения). */
  nameMatches?: number
  /**
   * Пары штатных единиц строки (structure-import-staffing): где строка создаст сотрудника, текущая пара
   * сотрудника, которого она обновит, и его пара после обновления. По ним предпросмотр пересчитывает
   * вакантные места под решения по строкам.
   */
  createPairKey?: string
  personPairKey?: string
  updatePairKey?: string
}

/**
 * Штатные единицы пары «подразделение + должность» из файла (structure-import-staffing): в файле и в
 * структуре сейчас. `rounded` — дробные единицы округлены вверх.
 */
export interface ImportStaffingPair {
  key: string
  department: string
  position: string
  fileUnits: number
  currentUnits: number
  rows: number[]
  rounded: boolean
}

/** Ошибка по строке исходного файла (design D5). */
export interface ImportRowError {
  rowNumber: number
  message: string
}

/** Кандидат на мягкую деактивацию (активный сотрудник, отсутствующий в файле). */
export interface ImportDeactivateCandidate {
  personId: string
  fullName: string
}

/**
 * Новая должность импорта (position-free-text): наименование из файла, которого ещё нет в
 * справочнике должностей, + номера строк, где оно встретилось. Строку такая должность не
 * отклоняет — должность свободный текст, а запись справочника заводится при подтверждении.
 */
export interface ImportNewPosition {
  value: string
  rows: number[]
}

/** Сводка предпросмотра/применения импорта (design D8). */
export interface ImportSummary {
  total: number
  toCreate: number
  toUpdate: number
  warnings: number
  manual: number
  errorsCount: number
}

/** Ответ предпросмотра импорта: стейдж (importId) + секции для UI. */
export interface ImportPreview {
  importId: string
  rows: ImportPreviewRow[]
  errors: ImportRowError[]
  deactivateCandidates: ImportDeactivateCandidate[]
  /**
   * Должности из файла, которых ещё нет в справочнике (position-free-text) — отчёт для UI:
   * они будут добавлены в справочник при подтверждении.
   * Опционально в типе для обратной совместимости мок-фикстур; бэк всегда возвращает массив.
   */
  newPositions?: ImportNewPosition[]
  /** Штатные единицы пар файла (structure-import-staffing); нет — в файле их нет. */
  staffing?: ImportStaffingPair[]
  /** Текущая пара кандидата на деактивацию: personId → ключ пары. */
  personPairs?: Record<string, string>
  summary: ImportSummary
}

/**
 * Решение по строке, требующей выбора: создать нового, пропустить или (только строка без даты
 * рождения с `suggestedPersonId`) обновить найденного сотрудника.
 */
export type ImportRowDecision = 'create' | 'skip' | 'update'

/** Тело подтверждения импорта (решения по строкам + флаг деактивации). */
export interface ImportConfirmInput {
  importId: string
  /** Решения по manual/warning-строкам: ключ — номер строки. */
  rowDecisions?: Record<string, ImportRowDecision>
  /** Явное подтверждение мягкой деактивации отсутствующих (без него — никого). */
  deactivateConfirmed?: boolean
  /** Подмножество кандидатов; пусто при deactivateConfirmed=true = все. */
  deactivatePersonIds?: string[]
}

/** Сводка применённого импорта (ответ confirm). */
export interface ImportResult {
  created: number
  updated: number
  deactivated: number
  skipped: number
  /** Добавлено вакантных мест по штатным единицам (structure-import-staffing). */
  vacanciesCreated?: number
}

/**
 * Поле структуры для колонки своего файла (structure-import-custom-file) — зеркало `imports.Field`.
 */
export type ImportColumnField =
  | 'department'
  | 'position'
  | 'fullName'
  | 'lastName'
  | 'firstName'
  | 'middleName'
  | 'snils'
  | 'birthDate'
  | 'gender'
  | 'hiredOn'
  | 'rank'
  | 'education'
  | 'staffUnits'

/** Колонка своего файла: заголовок, примеры значений и распознанное поле (`''` — не загружается). */
export interface ImportAnalysisColumn {
  /** Номер колонки листа (с 0) — ключ разметки. */
  index: number
  header: string
  samples: string[]
  field: ImportColumnField | ''
}

/**
 * Анализ файла импорта (POST …/structure/import/analyze). `template` — шаблон Центрики, сразу
 * предпросмотр; `custom` — свой файл, пользователь подтверждает колонки.
 */
/** Что распознано в отчёте 1С «Штатная расстановка» (structure-import-staffing). */
export interface ImportArrangementSummary {
  departments: number
  positions: number
  persons: number
  vacancies: number
}

/**
 * Роль строк-заголовков файла (structure-import-formats): в выгрузках 1С подразделение или
 * должность стоит не колонкой, а строкой над своей группой сотрудников.
 */
export type ImportGroupRole = 'department' | 'position' | 'skip'

/** Строки-заголовки, найденные в файле: предложенная роль, сколько их и примеры значений. */
export interface ImportGroups {
  role: ImportGroupRole
  count: number
  samples: string[]
}

export interface ImportAnalysis {
  /**
   * `template` — шаблон Центрики и `zup_arrangement` — отчёт 1С «Штатная расстановка» (сразу
   * предпросмотр); `custom` — свой файл (шаг «Колонки файла»).
   */
  format: 'template' | 'custom' | 'zup_arrangement'
  sheets: { name: string; recognized: boolean }[]
  sheet: string
  /** Номер последней строки шапки (с 1): данные — со следующей строки. */
  headerRow: number
  columns: ImportAnalysisColumn[]
  /** Поля для выпадающего списка — в порядке показа, с подписями. */
  fields: { field: ImportColumnField; label: string }[]
  /** Сводка отчёта 1С (`zup_arrangement`). */
  arrangement?: ImportArrangementSummary
  /** Строки-заголовки файла; нет поля — заголовков в файле нет. */
  groups?: ImportGroups
}

/** Подтверждённая разметка своего файла для предпросмотра. */
export interface ImportPreviewOptions {
  sheet: string
  headerRow: number
  /** Номер колонки → поле; неразмеченные колонки не передаются. */
  mapping: Record<number, ImportColumnField>
  /** Подразделение для строк, у которых его нет в файле. */
  defaultDepartment?: string
  /** Чем считать строки-заголовки файла; не передано — подразделение. */
  groupRole?: ImportGroupRole
}

/** Одна колонка листа «Структура» в справке «?» к импорту (замечание F2-05). */
export interface ImportHelpColumn {
  header: string
  required: boolean
  hint: string
}

/**
 * Справка к импорту структуры (F2-05): все колонки шаблона по порядку +
 * примечание про признаки, которые заполняются в кабинете после импорта.
 * Приходит с бэка (единый источник — `imports.SheetColumns`/`ImportantNote`),
 * тот же, что генерирует шаблон и валидирует парсер — исключает дрейф.
 */
export interface ImportHelp {
  columns: ImportHelpColumn[]
  note: string
}

// --- Раздел «Охрана труда» (p2-ot-section, §9) ---

/** Сигнал/общий статус ОТ (design D2, Б6): приоритет худшего — overdue>expiring>no_data>ok. */
export type OtSignal = 'overdue' | 'expiring' | 'no_data' | 'ok'

/** Сотрудник в контексте карточки рабочего места (занимающий либо сменщик), §9.5. */
export interface EmployeeSummary {
  id: string
  fullName: string
}

/**
 * Одна запись содержимого колонки «Обучение» строки ОТ (F2-10): программа + пересчитанный
 * сервером статус/срок (см. `otTrainingItemDTO`/`domain.ComputeTrainingStatus` — тот же
 * авторитетный расчёт, что и журнал сроков, не отдельная копия правил).
 */
export interface OtTrainingItem {
  programName: string
  status: TrainingStatus
  dueLabel: string
}

/**
 * Строка главной таблицы «Охрана труда» (§9.2): сотрудник ИЛИ вакантное рабочее место.
 * `fullName`/`employeeId` — только у kind='employee'; `workplaceId` — у обоих (переход в
 * карточку сотрудника использует `employeeId` через маршрут `/staff/:id`, у вакансии переход —
 * по `workplaceId` на `/workplaces/:id`).
 *
 * F2-10 (реверс дизайн-решения D3 «одна колонка статус» → раздельные колонки «СОУТ»/«Профриски»/
 * «Обучение» с СОДЕРЖИМЫМ, не только цветом бейджа): `sout`/`profrisk` — `null` = «нет данных»
 * (те же типы, что и карточка места/сотрудника, §9.4/§9.5); `training` — список ПОСЛЕ
 * дедупликации по (сотрудник,программа) (F2-08), пустой массив у сотрудника без записей ИЛИ у
 * вакансии (там `trainingSignal` отсутствует вовсе — этим и различаем «нет записей» от
 * «не применимо» на экране). `shiftPartners`/`isShift` — вложенные сменщики (design «Сменщики»
 * §10.1), непусто ТОЛЬКО у employee-строк с `isShift=true`.
 */
export interface OtOverviewRow {
  kind: 'employee' | 'vacancy'
  fullName?: string
  isVacancy: boolean
  departmentName: string
  position: string
  overallStatus: OtSignal
  /**
   * Разбивка по подсистемам — сверх буквального минимального набора полей: нужна для 7
   * РАЗЛИЧИМЫХ фильтров §9.2 («просроч. обучение»/«просроч. СОУТ»/«высокий»/«средний»/
   * «допустимый риск» иначе неразличимы — все схлопываются в overallStatus='overdue'/'expiring'/
   * 'ok'). `trainingSignal` отсутствует у вакансии (сигнал обучения не применим, design D2/Б6).
   */
  trainingSignal?: OtSignal
  soutSignal: OtSignal
  profriskSignal: OtSignal
  employeeId?: string
  workplaceId?: string
  sout: WorkConditionsSummary | null
  profrisk: ProfriskInfoSummary | null
  training: OtTrainingItem[]
  isShift?: boolean
  shiftPartners?: EmployeeSummary[]
}

/** Связанный документ карточки рабочего места (пока только отчёты СОУТ, design D6). */
export interface DocumentSummary {
  id: string
  title: string
  date: string
  downloadUrl: string
}

/**
 * Источник одной записи хронологии карточки места (F2-13): `kind='order'` — результат
 * заказа (только done — snapshot-гейт применён на сервере), `orderDate` — дата заказа
 * (design-конвенция «Заказ от {дата}», см. order-detail.tsx; в схеме заказов нет
 * человекочитаемого номера, поэтому источник различают текстом, а не выдуманным «№…»).
 * `kind='historical'` — запись без заказа-источника (source_order_id IS NULL,
 * p2-manual-historical §11.2/§11.3), `orderId`/`orderDate` пусты.
 */
export interface WorkplaceHistorySource {
  kind: 'order' | 'historical'
  orderId?: string
  orderDate?: string
}

/** Одна запись хронологии СОУТ рабочего места (F2-13) — ВСЕ видимые результаты, не только
 * последний (см. `soutSummary` выше), свежие сверху (сортировка — на сервере). */
export interface WorkplaceSoutHistoryItem {
  id: string
  hazardClass: string
  conductedOn?: string
  validUntil?: string
  perpetual: boolean
  source: WorkplaceHistorySource
}

/** Одна запись хронологии профрисков должности (F2-13), симметрично WorkplaceSoutHistoryItem. */
export interface WorkplaceProfriskHistoryItem {
  id: string
  riskLevel: RiskLevel
  assessedOn?: string
  source: WorkplaceHistorySource
}

/**
 * Карточка рабочего места (§9.5, design D5) — read-only, без действий; доступна и для
 * вакантных, и для занятых мест (`employees` пуст у вакантного). `soutHistory`/
 * `profriskHistory` (F2-13) — полная хронология (не дублирует *Summary — та остаётся
 * «последним видимым результатом» для существующих блоков сводки).
 */
export interface WorkplaceCard {
  id: string
  // departmentId/positionId — для предзаполнения формы «Изменение» (centrika-docs!78): выбор
  // существующего подразделения/должности в DepartmentSelect/PositionSelect по id.
  departmentId: string
  departmentName: string
  position: string
  positionId: string
  hasNoEmployee: boolean
  // Режим работы места (centrika-docs!78, §основные сведения 5–8): показ на странице места и
  // предзаполнение формы «Изменение». shiftHours/weekHours — null у мест без заполненного режима.
  actualAddress: string
  shiftHours: number | null
  weekHours: number | null
  schedule: string
  employees: EmployeeSummary[]
  shiftPartners: EmployeeSummary[]
  soutSummary: WorkConditionsSummary | null
  profriskSummary: ProfriskInfoSummary | null
  documents: DocumentSummary[]
  overallStatus: OtSignal
  soutHistory: WorkplaceSoutHistoryItem[]
  profriskHistory: WorkplaceProfriskHistoryItem[]
}

/**
 * Поверхность одной «стороны» работы со структурой/контактами компании. Имена путей различаются
 * у клиента (свой скоуп) и команды (скоуп = {cid}), поэтому собираем по двум базам:
 *  - `base` — реквизиты/контакты/структура/подсказки (клиент: `/company`; команда: `/team/clients/{cid}`);
 *  - `employeesBase` — адресная книга (клиент: `/employees`; команда: `/team/clients/{cid}/employees`).
 * Фаза 2 (design D1): `updateProfile` теперь есть и у клиента, и у команды — оба пути
 * ведут на один и тот же PATCH `/profile` (клиентский больше не 403).
 */
export interface CompanyScopeApi {
  getProfile: (signal?: AbortSignal) => Promise<CompanyProfile>
  updateProfile: (input: UpdateCompanyProfileInput) => Promise<CompanyProfile>
  listContacts: (signal?: AbortSignal) => Promise<ListResponse<Contact>>
  createContact: (input: ContactInput) => Promise<Contact>
  updateContact: (id: string, input: Partial<ContactInput>) => Promise<Contact>
  deleteContact: (id: string) => Promise<void>
  listDepartments: (signal?: AbortSignal) => Promise<ListResponse<Department>>
  createDepartment: (name: string) => Promise<Department>
  updateDepartment: (id: string, name: string) => Promise<Department>
  deleteDepartment: (id: string) => Promise<void>
  listWorkplaces: (signal?: AbortSignal) => Promise<ListResponse<Workplace>>
  createWorkplace: (input: CreateWorkplaceInput) => Promise<Workplace>
  updateWorkplace: (id: string, input: Partial<CreateWorkplaceInput>) => Promise<Workplace>
  deleteWorkplace: (id: string) => Promise<void>
  suggest: (kind: SuggestKind, q: string, signal?: AbortSignal) => Promise<SuggestResponse>
  listEmployees: (query?: EmployeesQuery, signal?: AbortSignal) => Promise<ListResponse<Employee>>
  createEmployee: (input: CreateEmployeeFormInput) => Promise<Employee>
  getEmployee: (id: string, signal?: AbortSignal) => Promise<EmployeeCard>
  updateEmployee: (id: string, input: UpdateEmployeeInput) => Promise<Employee>
  // Документ об образовании (centrika-docs!59) и согласие на обработку ПД: загрузка/удаление
  // доступны ТОЛЬКО на клиентском скоупе (`companyApi`) — бэкенд регистрирует роуты лишь под
  // клиентской группой (`/employees/{id}/education-document`, `/employees/{id}/consent`), у
  // команды их нет (она документы только просматривает). На teamClientApi(cid) эти методы
  // существуют в типе, но вызывать их нельзя — вернут 404; управление в карточке включается лишь
  // для клиента (EmployeeCardLoader.manageDocuments).
  /** Загрузка/замена PDF документа об образовании сотрудника (centrika-docs!59, клиент). */
  uploadEducationDocument: (id: string, file: File) => Promise<EducationDocumentStatus>
  /** Удаление документа об образовании сотрудника (centrika-docs!59, клиент). */
  deleteEducationDocument: (id: string) => Promise<EducationDocumentStatus>
  /** Загрузка/замена PDF-согласия сотрудника на обработку ПД (клиент; из карточки и из заказа). */
  uploadConsent: (id: string, file: File) => Promise<TrainingConsentStatus>
  /** Удаление согласия сотрудника на обработку ПД (клиент). */
  deleteConsent: (id: string) => Promise<TrainingConsentStatus>
  /**
   * «Удаление» сотрудника — семантически мягкая деактивация (design D1, §10.3):
   * запись не удаляется, возвращается обновлённый сотрудник (isActive:false).
   */
  deleteEmployee: (id: string) => Promise<Employee>
  /** Повторная активация ранее деактивированного сотрудника. */
  activateEmployee: (id: string) => Promise<Employee>
  // --- Импорт структуры из Excel (p2-excel-import) ---
  /** Абсолютный URL скачивания шаблона (owner-scoped GET, cookie-сессия). */
  importTemplateUrl: () => string
  /** Справка «?» по колонкам шаблона (F2-05) — тот же источник, что шаблон/валидатор. */
  importHelp: (signal?: AbortSignal) => Promise<ImportHelp>
  /**
   * Анализ файла импорта: шаблон это или свой файл, листы и колонки (structure-import-custom-file).
   * `sheet` — распознать другой лист. Ничего не применяет.
   */
  analyzeImport: (file: File, sheet?: string) => Promise<ImportAnalysis>
  /**
   * Загрузить файл и получить предпросмотр синхронизации. Без `options` — шаблон Центрики (.xlsx);
   * с `options` — свой файл по подтверждённой разметке колонок.
   */
  uploadImportPreview: (file: File, options?: ImportPreviewOptions) => Promise<ImportPreview>
  /** Подтвердить импорт (применить строки + опц. деактивация). */
  confirmImport: (input: ImportConfirmInput) => Promise<ImportResult>
  // --- Охрана труда (p2-ot-section, §9.2/§9.5) ---
  /** Главная таблица «Охрана труда»: плоский список сотрудники+вакансии, без пагинации. */
  otOverview: (signal?: AbortSignal) => Promise<ListResponse<OtOverviewRow>>
  /** Карточка рабочего места по id (доступна и для вакантных, и для занятых мест). */
  getWorkplace: (id: string, signal?: AbortSignal) => Promise<WorkplaceCard>
}

/**
 * Автозаполнение реквизитов по ИНН через DaData (p2-dadata, §3.3, design D3/D11).
 * ВАЖНО: путь ВСЕГДА `/company/dadata/party` (клиент) или флат `/team/dadata/party`
 * (команда) — НЕ под `/team/clients/{cid}/...`, в отличие от остальных `CompanyScopeApi`
 * путей (design D3: стейтлес-прокси без owner-scoping, `cid` — необязательная query-метка
 * только для аудита). Поэтому это отдельная фабрика, не часть `makeCompanyScope`.
 */
function fetchDaDataMatches(path: string, inn: string, cid?: string): Promise<DaDataPartyMatch[]> {
  return api.get<{ matches: DaDataPartyMatch[] }>(path, { inn, cid }).then((res) => res.matches)
}

/** Фабрика поверхности структуры/контактов по двум базовым путям (клиент/команда). */
function makeCompanyScope(base: string, employeesBase: string): CompanyScopeApi {
  return {
    getProfile: (signal) => api.get<CompanyProfile>(`${base}/profile`, undefined, signal),
    updateProfile: (input) => api.patch<CompanyProfile>(`${base}/profile`, input),
    listContacts: (signal) => api.get<ListResponse<Contact>>(`${base}/contacts`, undefined, signal),
    createContact: (input) => api.post<Contact>(`${base}/contacts`, input),
    updateContact: (id, input) => api.patch<Contact>(`${base}/contacts/${id}`, input),
    deleteContact: (id) => api.del<void>(`${base}/contacts/${id}`),
    listDepartments: (signal) =>
      api.get<ListResponse<Department>>(`${base}/departments`, undefined, signal),
    createDepartment: (name) => api.post<Department>(`${base}/departments`, { name }),
    updateDepartment: (id, name) => api.patch<Department>(`${base}/departments/${id}`, { name }),
    deleteDepartment: (id) => api.del<void>(`${base}/departments/${id}`),
    listWorkplaces: (signal) =>
      api.get<ListResponse<Workplace>>(`${base}/workplaces`, undefined, signal),
    createWorkplace: (input) => api.post<Workplace>(`${base}/workplaces`, input),
    updateWorkplace: (id, input) => api.patch<Workplace>(`${base}/workplaces/${id}`, input),
    deleteWorkplace: (id) => api.del<void>(`${base}/workplaces/${id}`),
    suggest: (kind, q, signal) => api.get<SuggestResponse>(`${base}/suggest`, { kind, q }, signal),
    listEmployees: (query, signal) =>
      api.get<ListResponse<Employee>>(employeesBase, { ...query }, signal),
    // Тело — ПЛОСКОЕ по §3b: форму разворачиваем перед отправкой (см. toEmployeeRequestBody).
    createEmployee: (input) => api.post<Employee>(employeesBase, toEmployeeRequestBody(input)),
    getEmployee: (id, signal) => api.get<EmployeeCard>(`${employeesBase}/${id}`, undefined, signal),
    updateEmployee: (id, input) =>
      api.patch<Employee>(`${employeesBase}/${id}`, toEmployeeRequestBody(input)),
    uploadEducationDocument: (id, file) => {
      const fd = new FormData()
      fd.append('file', file)
      return postMultipart<EducationDocumentStatus>(`${employeesBase}/${id}/education-document`, fd)
    },
    deleteEducationDocument: (id) =>
      api.del<EducationDocumentStatus>(`${employeesBase}/${id}/education-document`),
    uploadConsent: (id, file) => {
      const fd = new FormData()
      fd.append('file', file)
      return postMultipart<TrainingConsentStatus>(`${employeesBase}/${id}/consent`, fd)
    },
    deleteConsent: (id) => api.del<TrainingConsentStatus>(`${employeesBase}/${id}/consent`),
    deleteEmployee: (id) => api.del<Employee>(`${employeesBase}/${id}`),
    activateEmployee: (id) => api.post<Employee>(`${employeesBase}/${id}/activate`, {}),
    importTemplateUrl: () => apiUrl(`${base}/structure/import/template`),
    importHelp: (signal) => api.get<ImportHelp>(`${base}/structure/import/help`, undefined, signal),
    analyzeImport: (file, sheet) => {
      const fd = new FormData()
      fd.append('file', file)
      if (sheet) fd.append('sheet', sheet)
      return postMultipart<ImportAnalysis>(`${base}/structure/import/analyze`, fd)
    },
    uploadImportPreview: (file, options) => {
      const fd = new FormData()
      fd.append('file', file)
      if (options) {
        fd.append('sheet', options.sheet)
        fd.append('headerRow', String(options.headerRow))
        fd.append('mapping', JSON.stringify(options.mapping))
        if (options.defaultDepartment) fd.append('defaultDepartment', options.defaultDepartment)
        if (options.groupRole) fd.append('groupRole', options.groupRole)
      }
      return postMultipart<ImportPreview>(`${base}/structure/import/preview`, fd)
    },
    confirmImport: (input) => api.post<ImportResult>(`${base}/structure/import/confirm`, input),
    otOverview: (signal) =>
      api.get<ListResponse<OtOverviewRow>>(`${base}/ot/overview`, undefined, signal),
    getWorkplace: (id, signal) =>
      api.get<WorkplaceCard>(`${base}/workplaces/${id}`, undefined, signal),
  }
}

/**
 * Клиентская сторона (свой скоуп из сессии, без client_id). Фаза 2 (design D1, A1):
 * `updateProfile` теперь доступен и клиенту — правит реквизиты сам, без модерации;
 * `skipOnboarding` — «пропустить» заполнение профиля при первом входе (§3.1, D5).
 */
export const companyApi: CompanyScopeApi & {
  skipOnboarding: () => Promise<CompanyProfile>
  /** Автозаполнение по ИНН через DaData (design D11) — GET /api/company/dadata/party. */
  dadataLookup: (inn: string) => Promise<DaDataPartyMatch[]>
} = {
  ...makeCompanyScope('/company', '/employees'),
  skipOnboarding: () => api.post<CompanyProfile>('/company/onboarding/skip', {}),
  dadataLookup: (inn) => fetchDaDataMatches('/company/dadata/party', inn),
}

/**
 * Командная сторона: зеркало клиентских путей под `/api/team/clients/{cid}/...`
 * (включая `updateProfile` — менеджер тоже правит реквизиты, D1) плюс карточка
 * клиента (`getClient`). `dadataLookup` — ИСКЛЮЧЕНИЕ из этого зеркалирования (design
 * D3): бьёт во флат `/api/team/dadata/party?cid=` (не `/team/clients/{cid}/dadata/party`),
 * `cid` передаётся только как метка для аудита.
 */
export function teamClientApi(cid: string): CompanyScopeApi & {
  getClient: (signal?: AbortSignal) => Promise<ClientCard>
  dadataLookup: (inn: string) => Promise<DaDataPartyMatch[]>
} {
  const base = `/team/clients/${cid}`
  return {
    ...makeCompanyScope(base, `${base}/employees`),
    getClient: (signal) => api.get<ClientCard>(base, undefined, signal),
    dadataLookup: (inn) => fetchDaDataMatches('/team/dadata/party', inn, cid),
  }
}
